import { useState, useEffect, useRef, useMemo, useDeferredValue } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useSession } from "@/lib/SessionContext";
import { getPageAccess } from "@/lib/AuthSession";
import { useNavigate } from "react-router-dom";
import { useAsaPersonality } from "@/hooks/useAsaPersonality";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { deductStockForTask, previewStockDeduction } from "@/lib/stockDeduction";
import AsaStockDeductionModal from "@/Components/shared/AsaStockDeductionModal";
import { autoAdvanceNextTask, checkIfPOReadyForSiapKirim } from "@/lib/productionTaskPipeline";
import { formatDualVolumeWeight, formatQty, calculateRacikanDual } from "@/lib/unitConverter";
import MaserasiTimer from "@/Components/production/MaserasiTimer";
import {
  ClipboardList, PlayCircle, CheckCircle2, Clock, AlertCircle,
  LogOut, Package, Pause, Play, Timer, Sparkles, X, ShieldCheck, Trash2, UserCheck, Users, UserPlus, RefreshCw, Search, BarChart3, Eye,
  Target, User, Box, AlertTriangle, FlaskConical, ChevronDown, ChevronUp, CheckSquare, Square, Layers, ListChecks, Droplets, Hourglass, Loader2
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

const ACTIVITY_LABELS = {
  preparation: "Persiapan", mixing: "Mixing", filling: "Filling",
  sealing: "Sealing", labeling: "Pengemasan", qc: "QC",
  packaging: "Pengemasan", racik: "Racik / Mixing", maserasi: "Maserasi",
  packing: "Pengemasan", other: "Lainnya",
};

const STATUS_CONFIG = {
  assigned: { label: "Belum Dimulai", cls: "bg-blue-100 text-blue-700", icon: AlertCircle },
  in_progress: { label: "Sedang Berjalan", cls: "bg-emerald-100 text-emerald-700", icon: PlayCircle },
  on_hold: { label: "Dijeda", cls: "bg-amber-100 text-amber-700", icon: Pause },
  waiting_filling: { label: "Pengemasan Selesai (Menunggu Filling)", cls: "bg-amber-100 text-amber-900 border border-amber-300 font-semibold", icon: Clock },
  waiting_qc: { label: "Menunggu Validasi QC", cls: "bg-purple-100 text-purple-700", icon: Clock },
  completed: { label: "Selesai", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  cancelled: { label: "Dibatalkan", cls: "bg-red-100 text-red-600", icon: AlertCircle },
};

const ACTIVITY_COLORS = {
  preparation: "bg-purple-100 text-purple-700",
  mixing: "bg-blue-100 text-blue-700",
  filling: "bg-cyan-100 text-cyan-700",
  sealing: "bg-indigo-100 text-indigo-700",
  labeling: "bg-orange-100 text-orange-700",
  qc: "bg-green-100 text-green-700",
  packaging: "bg-orange-100 text-orange-700",
  racik: "bg-blue-100 text-blue-700",
  maserasi: "bg-violet-100 text-violet-700",
  packing: "bg-orange-100 text-orange-700",
  other: "bg-gray-100 text-gray-600",
};

const TASK_PO_STATUS_MAP = {
  racik: { onStart: "diracik", onSubmitQC: "menunggu_qc_mixing", onComplete: "maserasi" },
  mixing: { onStart: "diracik", onSubmitQC: "menunggu_qc_mixing", onComplete: "maserasi" },
  maserasi: { onStart: "maserasi", onComplete: "menunggu_filling" },
  filling: { onStart: "filling", onSubmitQC: "menunggu_qc_filling", onComplete: "menunggu_packing" },
  packing: { onStart: "packing", onSubmitQC: "menunggu_qc_packing", onComplete: "siap_kirim" },
  labeling: { onStart: "packing", onSubmitQC: "menunggu_qc_packing", onComplete: "siap_kirim" },
  pengemasan: { onStart: "packing", onSubmitQC: "menunggu_qc_packing", onComplete: "siap_kirim" },
  packaging: { onStart: "packing", onSubmitQC: "menunggu_qc_packing", onComplete: "siap_kirim" },
  sealing: { onStart: "packing", onSubmitQC: "menunggu_qc_packing", onComplete: "siap_kirim" },
};

const getTaskStatusConfig = (task) => {
  if (!task) return STATUS_CONFIG.assigned;
  const s = (task.status || '').toLowerCase();
  const act = (task.activity_type || '').toLowerCase();
  
  if (s === 'waiting_qc' || s === 'menunggu_qc') {
    if (act === 'racik' || act === 'mixing') {
      return { label: "Menunggu Validasi QC - Mixing", cls: "bg-purple-100 text-purple-800 border border-purple-200 font-semibold", icon: Clock };
    }
    if (act === 'filling') {
      return { label: "Menunggu Validasi QC - Filling", cls: "bg-purple-100 text-purple-800 border border-purple-200 font-semibold", icon: Clock };
    }
    if (act === 'packing' || act === 'pengemasan' || act === 'labeling' || act === 'sealing') {
      return { label: "Menunggu Validasi QC - Pengemasan", cls: "bg-purple-100 text-purple-800 border border-purple-200 font-semibold", icon: Clock };
    }
    return { label: "Menunggu Validasi QC", cls: "bg-purple-100 text-purple-700 font-semibold", icon: Clock };
  }

  if (s === 'assigned' || s === 'belum_dimulai') {
    if (act === 'racik' || act === 'mixing') {
      return { label: "Ready Produksi (Siap Diracik)", cls: "bg-blue-100 text-blue-700", icon: AlertCircle };
    }
    if (act === 'filling') {
      return { label: "Menunggu Filling", cls: "bg-cyan-100 text-cyan-800 border border-cyan-200", icon: Clock };
    }
    if (act === 'packing' || act === 'pengemasan') {
      return { label: "Menunggu Pengemasan", cls: "bg-orange-100 text-orange-800 border border-orange-200", icon: Clock };
    }
  }

  return STATUS_CONFIG[s] || STATUS_CONFIG.assigned;
};

const getShiftFromHour = (hour) => {
  if (hour >= 6 && hour < 14) return "Pagi";
  if (hour >= 14 && hour < 22) return "Siang";
  return "Malam";
};

// ASA pause messages based on personality style
function getAsaPauseMessages(sapaan, gaya) {
  const messages = {
    santai: [
      `Eh ${sapaan}, mau jeda dulu nih? Santai aja~ Tapi kasih tau dong alasannya biar aku catat ya 😄`,
      `Okeee ${sapaan} istirahat dulu yuk! Btw kenapa dijeda? Cerita dong~ 🫣`,
      `${sapaan} mau break? Boleh banget! Tulis alasannya ya biar rapih catatannya ✍️`,
    ],
    formal: [
      `${sapaan}, Anda akan menjeda tugas ini. Mohon isikan alasan jeda untuk keperluan dokumentasi.`,
      `Baik ${sapaan}, tugas akan dijeda sementara. Silakan tuliskan alasan jeda Anda.`,
      `${sapaan}, permintaan jeda diterima. Harap mencantumkan alasan penundaan untuk catatan produksi.`,
    ],
    default: [
      `Hai ${sapaan}! Mau jeda dulu ya? Boleh, tapi tulis alasannya dulu ya biar tercatat 📝`,
      `${sapaan}, sebelum dijeda, ceritain dong kenapa? Biar aku bantu catet ya~`,
      `Oke ${sapaan}! Jeda sebentar ya. Kasih tau alasannya biar nanti bisa ditindaklanjuti 👍`,
    ],
  };
  const pool = messages[gaya] || messages.default;
  return pool[Math.floor(Math.random() * pool.length)];
}

function getAsaPauseReaction(sapaan, gaya, reason) {
  const lower = (reason || '').toLowerCase();
  const isTired = lower.includes('capek') || lower.includes('cape') || lower.includes('lelah') || lower.includes('istirahat');
  const isBroken = lower.includes('rusak') || lower.includes('mesin') || lower.includes('alat') || lower.includes('error');
  const isMaterial = lower.includes('bahan') || lower.includes('habis') || lower.includes('stok') || lower.includes('kurang');
  const isEat = lower.includes('makan') || lower.includes('sholat') || lower.includes('solat') || lower.includes('ibadah');

  if (gaya === 'santai') {
    if (isTired) return `Istirahat dulu ya ${sapaan}, jangan lupa minum air putih! 💪 Nanti lanjut lagi~`;
    if (isBroken) return `Waduh ${sapaan}, mesin trouble ya? Semoga cepet beres! 🔧`;
    if (isMaterial) return `Bahan kurang ya ${sapaan}? Oke deh aku catat, nanti kabarin tim purchasing ya 📦`;
    if (isEat) return `Oke ${sapaan}, selamat istirahat & ibadah! Semangat lagi nanti ya ✨`;
    return `Oke noted ${sapaan}! Santai aja, nanti kalau udah siap lanjut lagi ya 😊`;
  }
  if (gaya === 'formal') {
    if (isTired) return `Baik ${sapaan}, istirahat sejenak untuk memulihkan tenaga. Silakan lanjutkan setelah siap.`;
    if (isBroken) return `Dipahami ${sapaan}. Kendala teknis akan dicatat untuk ditindaklanjuti tim maintenance.`;
    if (isMaterial) return `Terima kasih informasinya ${sapaan}. Kekurangan bahan akan dikoordinasikan dengan purchasing.`;
    if (isEat) return `Baik ${sapaan}, silakan beristirahat. Selamat menjalankan ibadah.`;
    return `Tercatat ${sapaan}. Silakan lanjutkan tugas saat sudah siap kembali.`;
  }
  // default
  if (isTired) return `Oke ${sapaan}, istirahat dulu ya! Jaga kesehatan, nanti lanjut lagi 💪`;
  if (isBroken) return `Noted ${sapaan}! Kendala alat ya, semoga cepat teratasi 🔧`;
  if (isMaterial) return `Oke ${sapaan}, bahan kurang ya? Sudah aku catat, nanti difollow up 📋`;
  if (isEat) return `Silakan ${sapaan}! Selamat istirahat, nanti lanjut ya 🙏`;
  return `Oke ${sapaan}, sudah dicatat! Lanjutkan kapanpun kamu siap ya 👍`;
}

// Timer component
function LiveTimer({ startTime, pauseMinutes = 0 }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startTime) return;
    const tick = () => {
      const now = Date.now();
      const start = new Date(startTime).getTime();
      const totalMins = Math.floor((now - start) / 60000) - pauseMinutes;
      setElapsed(Math.max(0, totalMins));
    };
    tick();
    const interval = setInterval(tick, 10000);
    return () => clearInterval(interval);
  }, [startTime, pauseMinutes]);

  const hrs = Math.floor(elapsed / 60);
  const mins = elapsed % 60;

  return (
    <span className="font-mono text-sm font-bold tabular-nums">
      {hrs > 0 ? `${hrs}j ${mins}m` : `${mins} menit`}
    </span>
  );
}

// Live Pause Timer component (updates in real-time when task is paused)
function LivePauseTimer({ lastPauseTime, storedPauseMins = 0 }) {
  const [totalPauseMins, setTotalPauseMins] = useState(storedPauseMins);

  useEffect(() => {
    const tick = () => {
      if (!lastPauseTime) {
        setTotalPauseMins(storedPauseMins);
        return;
      }
      const now = Date.now();
      const pauseStart = new Date(lastPauseTime).getTime();
      const currentPauseMins = Math.max(0, Math.floor((now - pauseStart) / 60000));
      setTotalPauseMins(storedPauseMins + currentPauseMins);
    };
    tick();
    const interval = setInterval(tick, 10000);
    return () => clearInterval(interval);
  }, [lastPauseTime, storedPauseMins]);

  const hrs = Math.floor(totalPauseMins / 60);
  const mins = totalPauseMins % 60;

  return (
    <span className="font-mono font-bold tabular-nums">
      {hrs > 0 ? `${hrs}j ${mins}m` : `${mins}m`}
    </span>
  );
}

// Helper to get exact Target PO in whole pcs for a task
function getTaskPoTargetPcs(task, poList = []) {
  if (!task) return 0;
  const matchedPO = poList.find(p => p.id === task.po_id || p.order_number === task.po_number);
  if (matchedPO) {
    if (Array.isArray(matchedPO.products) && matchedPO.products.length > 0) {
      const p = matchedPO.products.find(prod => 
        (prod.product_name && task.product_name && prod.product_name.toLowerCase() === task.product_name.toLowerCase()) || 
        (prod.name && task.product_name && prod.name.toLowerCase() === task.product_name.toLowerCase()) ||
        (task.task_name && prod.product_name && task.task_name.toLowerCase().includes(prod.product_name.toLowerCase())) ||
        (task.task_name && prod.name && task.task_name.toLowerCase().includes(prod.name.toLowerCase()))
      );
      if (p && Number(p.target_qty) > 0) {
        return Math.round(Number(p.target_qty));
      }
    }
    if (matchedPO.variant_details && task.product_name) {
      const vKey = Object.keys(matchedPO.variant_details).find(k => k.toLowerCase() === task.product_name.toLowerCase());
      if (vKey && Number(matchedPO.variant_details[vKey]?.qty) > 0) {
        return Math.round(Number(matchedPO.variant_details[vKey].qty));
      }
    }
  }
  const bSize = Number(task.botol_ukuran_aktual || task.ukuran_ml || 30);
  const qty = Number(task.target_quantity) || 0;
  if ((task.unit || '').toLowerCase().includes('l') || (task.unit || '').toLowerCase().includes('liter')) {
    return Math.round(qty * 1000 / bSize);
  }
  return Math.round(qty);
}

export default function TugasPage() {
  const { member, logout } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sapaan, gaya } = useAsaPersonality();

  const [activeTab, setActiveTab] = useState("active");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "assigned" | "in_progress" | "on_hold"
  const [loading, setLoading] = useState(null); // task id being processed

  // Mulai dialog state (ASA AI Analyst)
  const [mulaiTask, setMulaiTask] = useState(null);

  // Takeover & Manual Transfer PIC dialog state
  const [takeoverTask, setTakeoverTask] = useState(null);
  const [transferTask, setTransferTask] = useState(null);
  const [targetMemberId, setTargetMemberId] = useState("");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  // Jeda dialog state
  const [jedaTask, setJedaTask] = useState(null);
  const [jedaStep, setJedaStep] = useState("ask"); // "ask" | "reaction"
  const [jedaReason, setJedaReason] = useState("");
  const [asaMessage, setAsaMessage] = useState("");
  const [asaReaction, setAsaReaction] = useState("");
  const [detailTask, setDetailTask] = useState(null);

  // Selesai & Reject dialog state
  const [selesaiTask, setSelesaiTask] = useState(null);
  const [completedQty, setCompletedQty] = useState("");
  const [selesaiNotes, setSelesaiNotes] = useState("");
  const [maserasiHari, setMaserasiHari] = useState("");
  const [maserasiJam, setMaserasiJam] = useState("");
  const [rejectStep, setRejectStep] = useState("form"); // "ask_reject" | "form"
  const [rejects, setRejects] = useState([]); // { variantName, items: [{ name, qty, notes }] }
  const [discrepancyType, setDiscrepancyType] = useState(""); // "" | "reject" | "tidak_reject"
  const [discrepancyReason, setDiscrepancyReason] = useState("");
  const [discrepancyRejectQty, setDiscrepancyRejectQty] = useState("");
  const [qcChecklist, setQcChecklist] = useState({
    packaging: true,
    labeling: true,
    expDate: true,
    sealBox: true,
    qtyMatch: true,
  });

  const [expandedRacikanTaskId, setExpandedRacikanTaskId] = useState(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState(new Set());
  const [selectedSubTaskIds, setSelectedSubTaskIds] = useState(new Set());
  const [batchMulaiTasks, setBatchMulaiTasks] = useState(null);
  const [earlyStartModal, setEarlyStartModal] = useState(null);
  const [batchQcModal, setBatchQcModal] = useState(null);
  const [batchQcNotes, setBatchQcNotes] = useState("");
  const [batchQcChecklist, setBatchQcChecklist] = useState({
    aromaColor: true,
    cappingSeal: true,
    labelingExp: true,
    qtyMatch: true,
  });
  const [batchRejectMode, setBatchRejectMode] = useState("none"); // "none" | "custom"
  const [batchRejectQuantities, setBatchRejectQuantities] = useState({}); // { [taskId]: qty }
  const [batchPicModal, setBatchPicModal] = useState(null);
  const [batchTargetPicId, setBatchTargetPicId] = useState("");
  const [asaDeductionModal, setAsaDeductionModal] = useState(null);
  const [cicilQcModal, setCicilQcModal] = useState(null);
  const [cicilQcPassedQty, setCicilQcPassedQty] = useState("");
  const [cicilQcRejectQty, setCicilQcRejectQty] = useState("0");
  const [cicilQcNotes, setCicilQcNotes] = useState("");
  const [cicilQcChecklist, setCicilQcChecklist] = useState({
    volumeMl: true,
    cappingSeal: true,
    leakFree: true,
    cleanParticle: true,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [localSearch, setLocalSearch] = useState({});
  const [visibleCountMap, setVisibleCountMap] = useState({});
  const [activityFilter, setActivityFilter] = useState("all"); // 'all' | 'mixing' | 'filling' | 'waiting_qc' | 'packing'

  const canEdit = getPageAccess(member, "tugas_saya") !== "none";

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["production-tasks"],
    queryFn: () => base44.entities.ProductionTask.list("-created_date"),
  });

  const { data: productionOrders = [] } = useQuery({
    queryKey: ["production-orders-list"],
    queryFn: () => base44.entities.ProductionOrder.list("-created_date"),
  });

  // Auto-migrate legacy consolidated multi-variant tasks into real individual tasks seamlessly in the background
  const hasAutoMigratedRef = useRef(new Set());
  useEffect(() => {
    if (tasks.length > 0 && productionOrders.length > 0) {
      tasks.forEach(async (t) => {
        if (!t?.id || hasAutoMigratedRef.current.has(t.id)) return;
        const matchedPO = productionOrders.find(p => p.id === t.po_id || p.order_number === t.po_number || p.id === t.production_order_id);
        const vItems = getVariantItems(t, matchedPO);
        if (vItems && vItems.length > 1) {
          hasAutoMigratedRef.current.add(t.id);
          try {
            const brandPrefix = (t.po_brand || "PO").split('/')[0].trim();
            const activityLabel = ACTIVITY_LABELS[t.activity_type] || t.activity_type;

            const promises = vItems.map(v => {
              return base44.entities.ProductionTask.create({
                task_name: `${brandPrefix} - ${v.variant_name} - ${activityLabel}`,
                po_id: t.po_id || t.production_order_id,
                po_number: String(t.po_number || ""),
                po_brand: t.po_brand,
                activity_type: t.activity_type,
                product_name: v.variant_name,
                botol_ukuran_aktual: v.size_ml,
                target_quantity: Number(v.target_quantity) || 0,
                unit: v.unit || (t.activity_type === 'racik' ? 'liter' : 'pcs'),
                pic_id: t.pic_id || null,
                pic_name: t.pic_name || null,
                pic_role: t.pic_role || null,
                status: t.status || "assigned",
                start_time: t.start_time || null,
                duration_minutes: t.duration_minutes || null,
                notes: t.notes || null,
              });
            });
            await Promise.all(promises);
            await base44.entities.ProductionTask.delete(t.id);
            invalidate();
          } catch (e) {
            console.warn("Auto-migrate task failed:", e);
          }
        }
      });
    }
  }, [tasks, productionOrders]);

  // Fetch PO for Reject variants (hanya ketika modal Selesai terbuka untuk filling/packing)
  const { data: selesaiPO } = useQuery({
    queryKey: ['production-order-reject', selesaiTask?.po_id],
    queryFn: async () => {
      const orders = await base44.entities.ProductionOrder.filter({ id: selesaiTask.po_id });
      return orders[0];
    },
    enabled: !!selesaiTask?.po_id && ['filling', 'packing', 'packaging'].includes(selesaiTask?.activity_type),
  });

  // Queries for stock deduction
  const { data: rawInventory = [] } = useQuery({ queryKey: ['inventory'], queryFn: () => base44.entities.InventoryItem.list() });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: teamMembers = [] } = useQuery({ queryKey: ['team-members'], queryFn: () => base44.entities.TeamMember.list() });

  if (!member) {
    navigate("/login-tim");
    return null;
  }

  // Ambil data member ter-update secara real-time dari database
  const currentMember = teamMembers.find(m => 
    (m.id && member?.id && String(m.id) === String(member.id)) ||
    (m.email && member?.email && m.email.toLowerCase() === member.email.toLowerCase()) ||
    (m.full_name && member?.full_name && m.full_name.toLowerCase() === member.full_name.toLowerCase())
  ) || member;

  const mSubDivisi = (currentMember?.sub_divisi || member?.sub_divisi || '').toLowerCase();
  const mPosition = (currentMember?.position || member?.position || '').toLowerCase();
  const mRole = (currentMember?.role || member?.role || '').toLowerCase();
  const mDept = (currentMember?.department || member?.department || '').toLowerCase();
  const mPosRole = `${mPosition} ${mRole}`.toLowerCase();

  const isDevOrOwner = mRole === 'developer' || mRole === 'owner' || mRole === 'admin' || member?.role === 'developer' || member?.role === 'owner' || member?.role === 'admin';

  // 1. Tim QC (Quality Control)
  const isQCUser = mPosRole.includes('qc') || mPosRole.includes('quality') || mDept.includes('qc');

  // 2. Tim Produksi Sekunder (Pengemasan / Packing / Labelling / Sealing)
  const isSekunderTeam = !isQCUser && (
    mSubDivisi === 'sekunder' ||
    mPosition.includes('pengemasan') ||
    mPosition.includes('packing') ||
    mPosition.includes('labeling') ||
    mPosition.includes('sealing') ||
    mPosition.includes('sekunder') ||
    mPosition.includes('kemas') ||
    mRole.includes('pengemasan') ||
    mRole.includes('packing') ||
    mRole.includes('sekunder') ||
    mDept.includes('sekunder') ||
    mDept.includes('pengemasan')
  );

  // 3. Tim Produksi Primer (Mixing / Racik / Maserasi / Filling)
  const isPrimerTeam = !isQCUser && !isSekunderTeam && (
    mSubDivisi === 'primer' ||
    mPosition.includes('primer') ||
    mPosition.includes('mixing') ||
    mPosition.includes('racik') ||
    mPosition.includes('maserasi') ||
    mPosition.includes('filling') ||
    mRole.includes('primer') ||
    mDept.includes('primer') ||
    (!isDevOrOwner && !isSekunderTeam)
  );

  const isPengemasanTeam = isSekunderTeam;
  const isProduksiTeam = isPrimerTeam;

  const isKepala = isDevOrOwner || mRole === 'kepala' || mRole === 'admin' || mPosRole.includes('kepala') || mPosRole.includes('supervisor') || mPosRole.includes('lead');
  const isGeneralManager = isDevOrOwner || mPosRole.includes('kepala pabrik') || mPosRole.includes('plant manager') || mPosRole.includes('general manager');

  // Granular Action Permissions from Manajemen Tim (PageAccessDialog Set PIC toggle)
  const tPicMode = currentMember?.page_access?.tugas_saya_is_pic || member?.page_access?.tugas_saya_is_pic;
  const canStart = isDevOrOwner || (!isQCUser && (tPicMode === 'mulai' || tPicMode === 'all' || tPicMode === true || tPicMode === undefined));
  const canMonitor = isDevOrOwner || isGeneralManager || isQCUser || tPicMode === 'memantau' || tPicMode === 'all';
  const canValidateQC = isDevOrOwner || isQCUser || tPicMode === 'validasi' || tPicMode === 'all' || currentMember?.page_access?.validasi_qc === true || member?.page_access?.validasi_qc === true;

  const myTasks = tasks.filter((t) => {
    const act = (t.activity_type || '').toLowerCase();
    const stg = (t.stage || '').toLowerCase();
    const name = (t.nama || t.title || '').toLowerCase();
    const isPengemasanTask = act === 'packing' || act === 'pengemasan' || act === 'labeling' || act === 'sealing' || act === 'packaging' || stg.includes('kemas') || stg.includes('pack') || name.includes('pengemasan');
    const isProduksiTask = !isPengemasanTask && (act === 'racik' || act === 'mixing' || act === 'maserasi' || act === 'filling' || act === 'preparation' || stg.includes('mix') || stg.includes('fill') || stg.includes('maserasi') || name.includes('filling') || name.includes('mixing'));

    // 1. Explicitly assigned to this member or member participated/worked on it
    const isMyTask =
      (t.pic_id && currentMember?.id && String(t.pic_id) === String(currentMember.id)) ||
      (t.pic_id && member?.id && String(t.pic_id) === String(member.id)) ||
      t.pendukung?.some((p) => String(p.id) === String(currentMember?.id || member?.id)) ||
      !!t.member_progress?.[currentMember?.id] ||
      !!t.member_progress?.[member?.id];

    if (isMyTask) return true;

    // 2. Jika Tim Sekunder (seperti Kepala / Staff Produksi Sekunder):
    // Hanya tampilkan tugas pengemasan/sekunder di tab Tugas Aktif!
    // Tugas primer (mixing/maserasi/filling) akan masuk ke tab "Akan Datang".
    if (isSekunderTeam) {
      if (isPengemasanTask) {
        if (!t.pic_id) return true;
        if (t.status === "in_progress" || t.status === "on_hold" || t.status === "waiting_qc") return true;
        return t.status === "assigned";
      }
      return false;
    }

    // 3. Jika Tim Primer (seperti Kepala / Staff Produksi Primer):
    // Hanya tampilkan tugas primer (racik, mixing, maserasi, filling) di tab Tugas Aktif!
    if (isPrimerTeam) {
      if (isProduksiTask) {
        if (!t.pic_id) return true;
        if (t.status === "in_progress" || t.status === "on_hold" || t.status === "waiting_qc") return true;
        return t.status === "assigned";
      }
      return false;
    }

    // 4. Developer, Owner, QC Validator, atau General Manager:
    if (isGeneralManager || isDevOrOwner || isQCUser || canValidateQC) {
      return true;
    }

    return false;
  });

  const activeTasks = myTasks.filter((t) =>
    !["completed", "selesai", "cancelled", "dibatalkan"].includes((t.status || '').toLowerCase())
  );

  const activeTaskIds = new Set(activeTasks.map(t => t.id));

  // Antrean Masuk (Incoming Tasks): Tahap sebelumnya yang siap dipantau / dicuri start
  const incomingTasks = tasks.filter((t) => {
    const s = (t.status || '').toLowerCase();
    if (["completed", "selesai", "cancelled", "dibatalkan"].includes(s)) return false;

    // Jangan pernah menduplikasi tugas yang sudah aktif di Tugas Aktif pengguna
    if (activeTaskIds.has(t.id)) return false;

    const act = (t.activity_type || '').toLowerCase();
    const isProduksiTask = act === 'racik' || act === 'mixing' || act === 'maserasi' || act === 'filling' || act === 'preparation';
    const isPengemasanTask = act === 'packing' || act === 'pengemasan' || act === 'labeling' || act === 'sealing' || act === 'packaging';

    if (isSekunderTeam) {
      // Untuk Tim Sekunder: Antrean masuk adalah seluruh tugas primer yang sedang berjalan di hulu
      return isProduksiTask;
    }

    if (isPrimerTeam) {
      // Untuk Tim Primer: Antrean masuk adalah persiapan / tugas sekunder
      return isPengemasanTask || act === 'preparation';
    }

    return false;
  });

  // 1. Tasks filtered by Tahap (activityFilter) — used to compute dynamic Status counts
  const tasksForStage = useMemo(() => {
    if (activityFilter === "all") return activeTasks;
    return activeTasks.filter(t => {
      const act = (t.activity_type || '').toLowerCase();
      const s = (t.status || '').toLowerCase();
      if (activityFilter === "mixing") return act === "racik" || act === "mixing";
      if (activityFilter === "filling") return act === "filling";
      if (activityFilter === "waiting_qc") return s === "waiting_qc" || s === "menunggu_qc";
      if (activityFilter === "packing") return ['packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes(act);
      return true;
    });
  }, [activeTasks, activityFilter]);

  const assignedTasksCount = useMemo(() => tasksForStage.filter((t) => (t.status || '').toLowerCase() === "assigned" || (t.status || '').toLowerCase() === "belum_dimulai").length, [tasksForStage]);
  const inProgressTasksCount = useMemo(() => tasksForStage.filter((t) => (t.status || '').toLowerCase() === "in_progress" || (t.status || '').toLowerCase() === "berjalan").length, [tasksForStage]);
  const onHoldTasksCount = useMemo(() => tasksForStage.filter((t) => (t.status || '').toLowerCase() === "on_hold" || (t.status || '').toLowerCase() === "dijeda").length, [tasksForStage]);
  const waitingQcTasksCount = useMemo(() => tasksForStage.filter((t) => (t.status || '').toLowerCase() === "waiting_qc" || (t.status || '').toLowerCase() === "menunggu_qc").length, [tasksForStage]);

  // 2. Tasks filtered by Status (statusFilter) — used to compute dynamic Tahap counts
  const tasksForStatus = useMemo(() => {
    if (statusFilter === "all") return activeTasks;
    return activeTasks.filter(t => {
      const s = (t.status || '').toLowerCase();
      if (statusFilter === "assigned") return s === "assigned" || s === "belum_dimulai";
      if (statusFilter === "in_progress") return s === "in_progress" || s === "berjalan";
      if (statusFilter === "on_hold") return s === "on_hold" || s === "dijeda";
      if (statusFilter === "waiting_qc") return s === "waiting_qc" || s === "menunggu_qc";
      return true;
    });
  }, [activeTasks, statusFilter]);

  const mixingTasksCount = useMemo(() => tasksForStatus.filter(t => (t.activity_type === 'racik' || t.activity_type === 'mixing')).length, [tasksForStatus]);
  const fillingTasksCount = useMemo(() => tasksForStatus.filter(t => t.activity_type === 'filling').length, [tasksForStatus]);
  const stageQcTasksCount = useMemo(() => tasksForStatus.filter(t => (t.status || '').toLowerCase() === "waiting_qc" || (t.status || '').toLowerCase() === "menunggu_qc").length, [tasksForStatus]);
  const packingTasksCount = useMemo(() => tasksForStatus.filter(t => ['packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes((t.activity_type || '').toLowerCase())).length, [tasksForStatus]);

  // 3. Filtered active tasks
  const filteredActiveTasks = useMemo(() => {
    return activeTasks.filter((t) => {
      const s = (t.status || '').toLowerCase();
      const act = (t.activity_type || '').toLowerCase();

      if (statusFilter !== "all") {
        if (statusFilter === "assigned" && !(s === "assigned" || s === "belum_dimulai")) return false;
        if (statusFilter === "in_progress" && !(s === "in_progress" || s === "berjalan")) return false;
        if (statusFilter === "on_hold" && !(s === "on_hold" || s === "dijeda")) return false;
        if (statusFilter === "waiting_qc" && !(s === "waiting_qc" || s === "menunggu_qc")) return false;
      }

      if (activityFilter !== "all") {
        if (activityFilter === "mixing" && !(act === "racik" || act === "mixing")) return false;
        if (activityFilter === "filling" && act !== "filling") return false;
        if (activityFilter === "waiting_qc" && !(s === "waiting_qc" || s === "menunggu_qc")) return false;
        if (activityFilter === "packing" && !['packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes(act)) return false;
      }

      return true;
    });
  }, [activeTasks, statusFilter, activityFilter]);

  // ===== GET VARIANT ITEMS HELPER =====
  const getVariantItems = (task, po) => {
    if (!task) return [];
    if (task.variant_details && Object.keys(task.variant_details).length > 1) {
      return Object.entries(task.variant_details).map(([vName, vData]) => {
        const pMatch = po?.products?.find(p => p.product_name === vName || `${p.product_name} (${p.size_ml || p.ukuran_ml || p.ukuran_botol_ml || 30}ml)` === vName);
        const sizeMl = vData.botol_size_ml || pMatch?.size_ml || pMatch?.ukuran_ml || pMatch?.ukuran_botol_ml || po?.ukuran_botol_ml || 30;
        const targetQty = Number(vData.pcs_target ?? vData.qty ?? pMatch?.target_qty ?? pMatch?.quantity ?? 0);
        const totalVolMl = targetQty * sizeMl;
        const rac = vData.racikan_breakdown || pMatch?.racikan_digunakan || pMatch?.racikan || [];
        return {
          id: `${task.id}_${vName}`,
          task_id: task.id,
          variant_name: vName,
          target_quantity: targetQty,
          size_ml: sizeMl,
          unit: vData.unit || 'pcs',
          total_ml: totalVolMl,
          dual: calculateRacikanDual(totalVolMl, rac),
          racikan: rac,
        };
      });
    }

    if (Array.isArray(task.variants) && task.variants.length > 1) {
      return task.variants.map(vName => {
        const pMatch = po?.products?.find(p => p.product_name === vName || `${p.product_name} (${p.size_ml || p.ukuran_ml || p.ukuran_botol_ml || 30}ml)` === vName);
        const sizeMl = pMatch?.size_ml || pMatch?.ukuran_ml || pMatch?.ukuran_botol_ml || po?.ukuran_botol_ml || 30;
        const targetQty = Number(pMatch?.target_qty ?? pMatch?.quantity ?? pMatch?.qty ?? 0);
        const totalVolMl = targetQty * sizeMl;
        const rac = pMatch?.racikan_digunakan || pMatch?.racikan || [];
        return {
          id: `${task.id}_${vName}`,
          task_id: task.id,
          variant_name: vName,
          target_quantity: targetQty,
          size_ml: sizeMl,
          unit: task.unit || 'pcs',
          total_ml: totalVolMl,
          dual: calculateRacikanDual(totalVolMl, rac),
          racikan: rac,
        };
      });
    }

    // Jika PO memiliki banyak varian/produk dan tiket ini belum dipecah ke varian spesifik
    if (po?.products && po.products.length > 1) {
      const isSingleSpecificProduct = task.product_name && 
        !task.is_consolidated && 
        !task.product_name.toLowerCase().includes('semua varian') && 
        !task.task_name?.toLowerCase().includes('semua varian') &&
        task.product_name !== po?.brand_name;

      if (!isSingleSpecificProduct) {
        return po.products.map((p, pIdx) => {
          const sizeMl = p.ukuran_botol_ml || p.size_ml || p.ukuran_ml || p.botol_ukuran_aktual || po.ukuran_botol_ml || 30;
          const vName = p.ukuran_botol_ml ? `${p.product_name} (${sizeMl}ml)` : (p.product_name || `Varian ${pIdx + 1}`);
          const targetQty = Number(p.target_qty ?? p.quantity ?? p.qty ?? 0);
          const totalVolMl = targetQty * sizeMl;
          const rac = p.racikan_digunakan || p.racikan || [];
          return {
            id: `${task.id}_${vName}`,
            task_id: task.id,
            variant_name: vName,
            target_quantity: targetQty,
            size_ml: sizeMl,
            unit: task.unit || 'pcs',
            total_ml: totalVolMl,
            dual: calculateRacikanDual(totalVolMl, rac),
            racikan: rac,
          };
        });
      }
    }

    return [];
  };

  // ===== GROUP TASKS HELPER (Head Card per PO & Tahapan) =====
  const groupTasks = (taskList) => {
    const q = (searchQuery || '').toLowerCase().trim();
    const filteredList = q
      ? taskList.filter(t => {
          const taskName = (t.task_name || '').toLowerCase();
          const poNumber = (t.po_number || '').toLowerCase();
          const brand = (t.po_brand || '').toLowerCase();
          const prodName = (t.product_name || '').toLowerCase();
          const pic = (t.pic_name || '').toLowerCase();
          const activity = (t.activity_type || '').toLowerCase();
          const variants = (t.variants || []).map(v => String(v).toLowerCase()).join(' ');

          const matchedPO = productionOrders.find(p => p.id === t.po_id || p.order_number === t.po_number || p.id === t.production_order_id);
          const vItems = getVariantItems(t, matchedPO);
          const vNames = vItems.map(v => (v.variant_name || '').toLowerCase()).join(' ');

          return taskName.includes(q) || 
                 poNumber.includes(q) || 
                 brand.includes(q) || 
                 prodName.includes(q) || 
                 pic.includes(q) || 
                 activity.includes(q) || 
                 variants.includes(q) ||
                 vNames.includes(q);
        })
      : taskList;

    const groups = {};
    filteredList.forEach(t => {
      const key = `${t.po_id || t.po_number || t.production_order_id || 'standalone'}_${t.activity_type || 'other'}`;
      if (!groups[key]) {
        groups[key] = {
          id: key,
          po_id: t.po_id || t.production_order_id,
          po_number: t.po_number,
          po_brand: t.po_brand,
          activity_type: t.activity_type,
          tasks: [],
        };
      }

      const matchedPO = productionOrders.find(p => p.id === t.po_id || p.order_number === t.po_number || p.id === t.production_order_id);
      const vItems = getVariantItems(t, matchedPO);

      if (vItems && vItems.length > 1) {
        vItems.forEach((v, vIdx) => {
          const brandPrefix = (t.po_brand || "PO").split('/')[0].trim();
          const activityLabel = ACTIVITY_LABELS[t.activity_type] || t.activity_type;
          groups[key].tasks.push({
            ...t,
            id: `${t.id}__var__${v.variant_name || vIdx}`,
            _rawTaskId: t.id,
            is_virtual_subtask: true,
            task_name: `${brandPrefix} - ${v.variant_name} - ${activityLabel}`,
            product_name: v.variant_name,
            botol_ukuran_aktual: v.size_ml,
            target_quantity: v.target_quantity,
            unit: v.unit || (t.activity_type === 'racik' ? 'liter' : 'pcs'),
            status: t.status || "assigned",
            start_time: t.start_time || null,
            duration_minutes: t.duration_minutes || null,
            completed_quantity: t.status === 'completed' ? v.target_quantity : null,
          });
        });
      } else {
        groups[key].tasks.push(t);
      }
    });
    return Object.values(groups);
  };

  // Selected tasks list across all available groups/tasks for floating action bar
  const selectedTasksList = useMemo(() => {
    if (selectedSubTaskIds.size === 0) return [];
    const allAvailable = [];
    const groups = groupTasks(filteredActiveTasks);
    groups.forEach(g => {
      g.tasks.forEach(t => {
        if (selectedSubTaskIds.has(t.id)) allAvailable.push(t);
      });
    });
    return allAvailable;
  }, [selectedSubTaskIds, filteredActiveTasks, deferredSearchQuery, productionOrders, searchQuery]);

  const doneTasks = myTasks.filter((t) => {
    const s = (t.status || '').toLowerCase();
    const isCompleted = s === "completed" || s === "selesai";
    if (!isCompleted) return false;

    // Developer / Owner / Superadmin bisa melihat semua tugas selesai untuk pemantauan
    if (isDevOrOwner || isKepala) return true;

    // Untuk staf/PIC/Validator: tugas dihitung selesai jika orang tersebut terlibat/bertanggung jawab:
    // 1. Sebagai PIC utama
    if (t.pic_id && (String(t.pic_id) === String(currentMember?.id) || String(t.pic_id) === String(member?.id))) return true;
    // 2. Ada di daftar pendukung
    if (t.pendukung?.some((p) => String(p.id) === String(currentMember?.id || member?.id))) return true;
    // 3. Pernah mengerjakan progres (member_progress)
    if (t.member_progress?.[currentMember?.id] || t.member_progress?.[member?.id]) return true;
    // 4. Ada di riwayat kontributor PIC (history_pic)
    if (t.history_pic?.some(h => String(h.id) === String(currentMember?.id || member?.id) || h.pic_name === (currentMember?.full_name || member?.full_name))) return true;
    // 5. Sebagai Validator QC yang memvalidasi akhir tugas tersebut
    if (t.qc_validator_id && (String(t.qc_validator_id) === String(currentMember?.id) || String(t.qc_validator_id) === String(member?.id))) return true;
    if (t.qc_member_id && (String(t.qc_member_id) === String(currentMember?.id) || String(t.qc_member_id) === String(member?.id))) return true;
    if (t.validated_by && t.validated_by === (currentMember?.full_name || member?.full_name)) return true;

    return false;
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["production-tasks"] });
    queryClient.invalidateQueries({ queryKey: ["catatan-harian"] });
  };

  // ===== ACTION: PIC SUBMIT SELESAI (Diajukan ke QC / Menunggu Filling) =====
  const handlePicSubmitSelesai = async (task) => {
    setLoading(task.id);
    const now = new Date().toISOString();
    const mProg = task.member_progress?.[member.id] || {};
    const grossMinutes = mProg.start_time
      ? Math.round((new Date(now).getTime() - new Date(mProg.start_time).getTime()) / 60000)
      : 0;
    const netMinutes = grossMinutes - (mProg.total_pause_minutes || 0);

    const isPacking = task.activity_type === 'packing' || task.activity_type === 'pengemasan' || task.activity_type === 'labeling' || task.activity_type === 'sealing';
    const relatedFilling = tasks.find(t => 
      (t.po_id === task.po_id || t.po_number === task.po_number) &&
      (t.activity_type === 'filling') &&
      (!task.product_name || !t.product_name || t.product_name.toLowerCase().trim() === task.product_name.toLowerCase().trim()) &&
      !['completed', 'selesai'].includes((t.status || '').toLowerCase())
    );

    if (isPacking && relatedFilling) {
      toast.error(`Pengemasan ${task.product_name ? `(${task.product_name}) ` : ''}tidak bisa diselesaikan karena proses Filling masih berjalan! Tunggu tim Filling menyelesaikan pengisian seluruh botol.`);
      setLoading(null);
      return;
    }

    const targetStatus = "waiting_qc";

    let newMemberProgress = { ...(task.member_progress || {}) };
    newMemberProgress[member.id] = {
      ...mProg,
      status: targetStatus,
      end_time: now,
      gross_minutes: grossMinutes,
      duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
    };

    await base44.entities.ProductionTask.update(task.id, {
      member_progress: newMemberProgress,
      status: targetStatus,
      gross_minutes: grossMinutes,
      duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
      end_time: now,
      notes: task.notes ? task.notes.replace(/\[Selesai Dikerjakan\]\s*(Menunggu Validasi QC|Menunggu Filling Selesai)/gi, '').trim() : task.notes,
    });

    toast.success("Tugas diselesaikan & diajukan ke Quality Control untuk divalidasi! ⏳");
    invalidate();
    setLoading(null);
  };

  // Helper to build combined inventory
  const getCombinedInventoryList = () => [
    ...botolList.map(i => ({ id: `botol-${i.id}`, _stokId: i.id, _entity: 'Botol', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...tutupList.map(i => ({ id: `tutup-${i.id}`, _stokId: i.id, _entity: 'Tutup', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...sprayList.map(i => ({ id: `spray-${i.id}`, _stokId: i.id, _entity: 'Spray', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...bahanList.map(i => ({ id: `bahan-${i.id}`, _stokId: i.id, _entity: 'BahanCair', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...rawInventory,
  ];

  // ===== ACTION: BUKA POPUP CONFIRMATION MULAI (ASA AI) =====
  const handleOpenMulai = (task) => {
    const matchedPO = productionOrders.find(p => p.id === task.po_id || p.order_number === task.po_number || p.id === task.production_order_id);
    const combinedInventory = getCombinedInventoryList();
    const variantTarget = task.product_name || task.variant_name;

    if (matchedPO) {
      const preview = previewStockDeduction(matchedPO, task.activity_type, combinedInventory, bahanList, variantTarget);
      if (preview.items.length > 0) {
        setAsaDeductionModal({
          open: true,
          title: `Konfirmasi Pemotongan Bahan: ${task.task_name}`,
          taskName: task.task_name,
          poNumber: task.po_number,
          brandName: task.po_brand,
          activityType: task.activity_type,
          previewData: preview,
          confirmLabel: preview.hasShortage ? "Tetap Mulai Pengerjaan" : "Konfirmasi & Mulai Pengerjaan",
          onConfirm: async () => {
            await deductStockForTask(matchedPO, task.activity_type, combinedInventory, bahanList, variantTarget);
            await handleConfirmMulai(task);
            setAsaDeductionModal(null);
          }
        });
        return;
      }
    }

    // Jika sudah pernah dipotong / 0 items, langsung mulai
    handleConfirmMulai(task);
  };

  // ===== ACTION: CONFIRM MULAI =====
  const handleConfirmMulai = async (task) => {
    setLoading(task.id);
    const now = new Date().toISOString();
    const poStatusMap = TASK_PO_STATUS_MAP[task.activity_type];

    let newMemberProgress = { ...(task.member_progress || {}) };
    newMemberProgress[member.id] = {
      status: "in_progress",
      start_time: now,
    };

    const updateData = {
      member_progress: newMemberProgress,
      pic_id: member.id,
      pic_name: member.full_name,
    };

    if (task.status === "assigned" || !task.status) {
      updateData.status = "in_progress";
      updateData.start_time = now;

      if (task.po_id && poStatusMap?.onStart) {
        await base44.entities.ProductionOrder.update(task.po_id, {
          status: poStatusMap.onStart,
        }).catch(() => {});
      }
    }

    await base44.entities.ProductionTask.update(task.id, updateData);
    toast.success("Tugas dimulai! Anda resmi menjadi PIC tugas ini 🚀");
    invalidate();
    setLoading(null);
    setMulaiTask(null);
  };

  // ===== BATCH EXECUTION HELPERS (HEAD CARD & MULTI-VARIANT) =====
  const toggleGroupExpand = (groupId) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleSelectSubTask = (taskId) => {
    setSelectedSubTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const handleSelectAllInGroup = (group) => {
    const groupTaskIds = group.tasks.map(t => t.id);
    const allSelected = groupTaskIds.every(id => selectedSubTaskIds.has(id));
    setSelectedSubTaskIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        groupTaskIds.forEach(id => next.delete(id));
      } else {
        groupTaskIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const executeBatchStart = async (taskList) => {
    const now = new Date().toISOString();
    setLoading("batch");
    try {
      for (const t of taskList) {
        const poStatusMap = TASK_PO_STATUS_MAP[t.activity_type];
        let newMemberProgress = { ...(t.member_progress || {}) };
        newMemberProgress[member.id] = {
          status: "in_progress",
          start_time: now,
        };

        const updateData = {
          member_progress: newMemberProgress,
          pic_id: member.id,
          pic_name: member.full_name,
          status: "in_progress",
          start_time: now,
        };

        if (t.po_id && poStatusMap?.onStart) {
          await base44.entities.ProductionOrder.update(t.po_id, {
            status: poStatusMap.onStart,
          }).catch(() => {});
        }

        await base44.entities.ProductionTask.update(t.id, updateData);
      }
      toast.success(`🎉 Berhasil memulai ${taskList.length} varian/tugas sekaligus!`);
      setSelectedSubTaskIds(prev => {
        const next = new Set(prev);
        taskList.forEach(t => next.delete(t.id));
        return next;
      });
      invalidate();
    } catch (err) {
      console.error("Gagal memulai tugas batch:", err);
      toast.error("Terjadi kendala saat memulai tugas");
    } finally {
      setLoading(null);
      setBatchMulaiTasks(null);
    }
  };

  const handleBatchStartTasks = async (taskList) => {
    if (!taskList || taskList.length === 0) return;
    const firstTask = taskList[0];
    const matchedPO = productionOrders.find(p => p.id === firstTask.po_id || p.order_number === firstTask.po_number || p.id === firstTask.production_order_id);
    const combinedInventory = getCombinedInventoryList();

    if (matchedPO) {
      const preview = previewStockDeduction(matchedPO, firstTask.activity_type, combinedInventory, bahanList);
      if (preview.items.length > 0) {
        setAsaDeductionModal({
          open: true,
          title: `Konfirmasi Pemotongan Bahan: ${taskList.length} Varian (${firstTask.po_brand || ''})`,
          taskName: `${taskList.length} Varian`,
          poNumber: firstTask.po_number,
          brandName: firstTask.po_brand,
          activityType: firstTask.activity_type,
          previewData: preview,
          confirmLabel: `Konfirmasi & Mulai (${taskList.length} Varian)`,
          onConfirm: async () => {
            await deductStockForTask(matchedPO, firstTask.activity_type, combinedInventory, bahanList);
            await executeBatchStart(taskList);
            setAsaDeductionModal(null);
          }
        });
        return;
      }
    }

    await executeBatchStart(taskList);
  };

  const handleBatchResumeTasks = async (taskList) => {
    if (!taskList || taskList.length === 0) return;
    const now = new Date().toISOString();
    setLoading("batch");
    try {
      for (const t of taskList) {
        const mProg = t.member_progress?.[member.id];
        const pauseDurationMins = mProg?.last_pause_time
          ? Math.round((new Date(now).getTime() - new Date(mProg.last_pause_time).getTime()) / 60000)
          : 0;

        let newMemberProgress = { ...(t.member_progress || {}) };
        newMemberProgress[member.id] = {
          ...mProg,
          status: "in_progress",
          total_pause_minutes: (mProg?.total_pause_minutes || 0) + pauseDurationMins,
        };

        await base44.entities.ProductionTask.update(t.id, {
          member_progress: newMemberProgress,
          status: "in_progress",
        });
      }
      toast.success(`Berhasil melanjutkan ${taskList.length} tugas/varian!`);
      invalidate();
    } catch (err) {
      console.error("Gagal melanjutkan tugas batch:", err);
      toast.error("Terjadi kendala saat melanjutkan tugas");
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: BATCH SELESAI PENGERJAAN FISIK (in_progress -> waiting_qc) =====
  const handleBatchFinishToQC = async (taskList) => {
    if (!taskList || taskList.length === 0) return;

    // Cek apakah ada varian pengemasan yang proses filling-nya belum selesai
    const invalidPackingTasks = taskList.filter(t => {
      const isPacking = ['packing', 'pengemasan', 'labeling', 'sealing'].includes(t.activity_type);
      if (!isPacking) return false;
      return tasks.some(ex => 
        (ex.po_id === t.po_id || ex.po_number === t.po_number) &&
        (ex.activity_type === 'filling') &&
        (!t.product_name || !ex.product_name || ex.product_name.toLowerCase().trim() === t.product_name.toLowerCase().trim()) &&
        !['completed', 'selesai'].includes((ex.status || '').toLowerCase())
      );
    });

    if (invalidPackingTasks.length > 0) {
      toast.error(`Pengemasan tidak bisa diselesaikan! Ada ${invalidPackingTasks.length} varian yang proses Filling-nya masih belum selesai. Harap selesaikan proses pengisian botol (Filling) terlebih dahulu.`);
      return;
    }

    const now = new Date().toISOString();
    setLoading("batch");
    try {
      for (const t of taskList) {
        const mProg = t.member_progress?.[member.id] || {};
        const grossMinutes = t.start_time
          ? Math.round((new Date(now).getTime() - new Date(t.start_time).getTime()) / 60000)
          : (mProg?.gross_minutes || 0);
        const pauseMinutes = mProg?.total_pause_minutes || 0;
        const netMinutes = grossMinutes - pauseMinutes;

        const targetStatus = "waiting_qc";

        let newMemberProgress = { ...(t.member_progress || {}) };
        newMemberProgress[member.id] = {
          ...mProg,
          status: targetStatus,
          end_time: now,
          gross_minutes: grossMinutes,
          total_pause_minutes: pauseMinutes,
          duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
        };

        const isRacik = t.activity_type === "racik" || t.activity_type === "mixing";
        const cleanBaseNotes = (t.notes || '').replace(/\[Selesai Dikerjakan\]\s*(Menunggu Validasi QC|Menunggu Filling Selesai)/gi, '').trim();

        const updateData = {
          member_progress: newMemberProgress,
          status: targetStatus,
          end_time: now,
          gross_minutes: grossMinutes,
          total_pause_minutes: pauseMinutes,
          duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
          notes: cleanBaseNotes 
            ? `${cleanBaseNotes}\n[Selesai Dikerjakan] Menunggu Validasi QC` 
            : `[Selesai Dikerjakan] Menunggu Validasi QC`,
        };

        if (isRacik) {
          const finalHari = t.maserasi_hari !== undefined && t.maserasi_hari !== null ? Number(t.maserasi_hari) : 2;
          const finalJam = t.maserasi_jam !== undefined && t.maserasi_jam !== null ? Number(t.maserasi_jam) : 0;
          updateData.maserasi_jam = finalJam;
          updateData.maserasi_hari = finalHari;
          if (t.po_id) {
            await base44.entities.ProductionOrder.update(t.po_id, {
              status: "maserasi",
              maserasi_start: now,
              maserasi_durasi_jam: finalJam,
              maserasi_durasi_hari: finalHari,
            }).catch(() => {});
          }
        }

        await base44.entities.ProductionTask.update(t.id, updateData);
      }

      toast.success(`Pengerjaan ${taskList.length} varian selesai! Beralih ke Menunggu Validasi QC.`);
      setSelectedSubTaskIds(prev => {
        const next = new Set(prev);
        taskList.forEach(t => next.delete(t.id));
        return next;
      });
      invalidate();
    } catch (err) {
      console.error("Gagal menyelesaikan pengerjaan batch:", err);
      toast.error("Terjadi kendala saat menyelesaikan tugas");
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: BATCH ASSIGN PIC =====
  const handleBatchAssignPic = async () => {
    if (!batchPicModal || batchPicModal.length === 0 || !batchTargetPicId) {
      toast.error("Mohon pilih PIC pengganti!");
      return;
    }
    const targetMem = teamMembers.find(m => String(m.id) === String(batchTargetPicId));
    if (!targetMem) {
      toast.error("Data anggota tim tidak ditemukan");
      return;
    }

    setLoading("batch");
    try {
      for (const t of batchPicModal) {
        await base44.entities.ProductionTask.update(t.id, {
          pic_id: targetMem.id,
          pic_name: targetMem.full_name,
          pic_role: targetMem.role || targetMem.position || null,
        });
      }
      toast.success(`Berhasil menugaskan ${batchPicModal.length} tugas kepada ${targetMem.full_name}`);
      setSelectedSubTaskIds(prev => {
        const next = new Set(prev);
        batchPicModal.forEach(t => next.delete(t.id));
        return next;
      });
      setBatchPicModal(null);
      setBatchTargetPicId("");
      invalidate();
    } catch (err) {
      console.error("Gagal ganti PIC batch:", err);
      toast.error("Terjadi kendala saat mengubah PIC");
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: BATCH QC VALIDATION (waiting_qc -> completed) =====
  const handleBatchConfirmQC = async () => {
    if (!batchQcModal || batchQcModal.length === 0) return;
    const now = new Date().toISOString();
    const today = now.split("T")[0];
    setLoading("batch_qc");

    try {
      const processedPoIds = new Set();

      for (const task of batchQcModal) {
        const mProg = task.member_progress?.[task.pic_id || member.id] || {};
        const grossMinutes = task.start_time
          ? Math.round((new Date(now).getTime() - new Date(task.start_time).getTime()) / 60000)
          : (task.gross_minutes || 0);
        const pauseMinutes = task.total_pause_minutes || mProg?.total_pause_minutes || 0;
        const netMinutes = grossMinutes - pauseMinutes;
        const isRacik = task.activity_type === "racik" || task.activity_type === "mixing";
        const poStatusMap = TASK_PO_STATUS_MAP[task.activity_type];
        const rejectQty = batchRejectMode === 'custom' ? Number(batchRejectQuantities[task.id] || 0) : 0;

        let newMemberProgress = { ...(task.member_progress || {}) };
        if (task.pic_id && newMemberProgress[task.pic_id]) {
          newMemberProgress[task.pic_id] = {
            ...newMemberProgress[task.pic_id],
            status: "completed",
            end_time: now,
          };
        }

        const cleanBaseNotes = (task.notes || '').replace(/\[Selesai Dikerjakan\]\s*Menunggu Validasi QC/gi, '').trim();
        const baseOutput = task.completed_quantity !== undefined && task.completed_quantity !== null
          ? Number(task.completed_quantity)
          : (Number(task.target_quantity) || 0);
        const actualOutputQty = Math.max(0, baseOutput - rejectQty);

        const updateData = {
          member_progress: newMemberProgress,
          status: "completed",
          end_time: now,
          completed_quantity: actualOutputQty,
          gross_minutes: grossMinutes,
          total_pause_minutes: pauseMinutes,
          duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
          qc_validator_id: currentMember?.id || member?.id,
          qc_validator_name: currentMember?.full_name || member?.full_name,
          validated_by: currentMember?.full_name || member?.full_name,
          notes: batchQcNotes ? (cleanBaseNotes ? `${cleanBaseNotes}\n[QC VALIDASI] ${batchQcNotes}` : `[QC VALIDASI] ${batchQcNotes}`) : cleanBaseNotes,
        };

        if (rejectQty > 0) {
          updateData.rejected_items = [{
            variantName: task.product_name || task.task_name,
            items: [{ id: Date.now(), name: 'Reject Hasil Validasi QC', qty: rejectQty, notes: batchQcNotes || 'Reject QC' }]
          }];
        }

        if (isRacik) {
          const finalHari = task.maserasi_hari !== undefined && task.maserasi_hari !== null ? Number(task.maserasi_hari) : 2;
          const finalJam = task.maserasi_jam !== undefined && task.maserasi_jam !== null ? Number(task.maserasi_jam) : 0;
          updateData.maserasi_jam = finalJam;
          updateData.maserasi_hari = finalHari;
          if (task.po_id && !processedPoIds.has(task.po_id)) {
            processedPoIds.add(task.po_id);
            await base44.entities.ProductionOrder.update(task.po_id, {
              status: "maserasi",
              maserasi_start: now,
              maserasi_durasi_jam: finalJam,
              maserasi_durasi_hari: finalHari,
            }).catch(() => {});
          }
        } else if (task.po_id && poStatusMap?.onComplete && !processedPoIds.has(task.po_id)) {
          processedPoIds.add(task.po_id);
          if (poStatusMap.onComplete === "siap_kirim") {
            const isReady = await checkIfPOReadyForSiapKirim(task.po_id);
            await base44.entities.ProductionOrder.update(task.po_id, {
              status: isReady ? "siap_kirim" : "packing",
            }).catch(() => {});
          } else {
            await base44.entities.ProductionOrder.update(task.po_id, {
              status: poStatusMap.onComplete,
            }).catch(() => {});
          }
        }

        // Task-based stock deduction
        if (task.po_id) {
          try {
            const orders = await base44.entities.ProductionOrder.filter({ id: task.po_id });
            if (orders.length > 0) {
              const order = orders[0];
              const inventoryItems = [
                ...botolList.map(i => ({ id: `botol-${i.id}`, _stokId: i.id, _entity: 'Botol', total_stock: i.stok ?? 0 })),
                ...tutupList.map(i => ({ id: `tutup-${i.id}`, _stokId: i.id, _entity: 'Tutup', total_stock: i.stok ?? 0 })),
                ...sprayList.map(i => ({ id: `spray-${i.id}`, _stokId: i.id, _entity: 'Spray', total_stock: i.stok ?? 0 })),
                ...bahanList.map(i => ({ id: `bahan-${i.id}`, _stokId: i.id, _entity: 'BahanCair', total_stock: i.stok ?? 0 })),
                ...itemTambahanList.map(i => ({ id: `item-${i.id}`, _stokId: i.id, _entity: 'ItemTambahan', total_stock: i.stok ?? 0 })),
              ];
              await deductInventoryForTask(task, order, inventoryItems);
            }
          } catch (e) {
            console.error("Gagal potong stok di batch QC:", e);
          }
        }

        // Daily Production log
        const aktivitasMap = {
          racik: "mixing", maserasi: "lainnya", filling: "filling",
          packing: "packing", labeling: "packing", qc: "quality_check",
          preparation: "lainnya", other: "lainnya",
        };
        await base44.entities.DailyProduction.create({
          date: today,
          shift: getShiftFromHour(new Date().getHours()),
          activity: aktivitasMap[task.activity_type] || "quality_check",
          operator: task.pic_name || member.full_name,
          qty_produced: actualOutputQty,
          waste_qty: rejectQty,
          brand_name: task.po_brand || "Brand Kustom",
          target_po: task.po_number || "",
          notes: `[QC Batch] ${task.task_name}. Divalidasi oleh: ${currentMember?.full_name || member?.full_name}.${rejectQty > 0 ? ` (Reject: ${rejectQty} pcs)` : ''}${batchQcNotes ? " " + batchQcNotes : ""}`,
          duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
        }).catch(() => {});

        await base44.entities.ProductionTask.update(task.id, updateData);

        // Otomatis sinkronkan tugas pengemasan paralel jika filling selesai
        if (task.activity_type === 'filling') {
          const relatedPackings = tasks.filter(ex => 
            (ex.po_id === task.po_id || ex.po_number === task.po_number) &&
            (ex.activity_type === 'packing' || ex.activity_type === 'pengemasan') &&
            (ex.product_name === task.product_name || !task.product_name)
          );

          for (const pTask of relatedPackings) {
            const updatePacking = {
              target_quantity: actualOutputQty,
            };
            if ((pTask.status || '').toLowerCase() === 'waiting_filling') {
              updatePacking.status = 'waiting_qc';
              updatePacking.completed_quantity = actualOutputQty;
            }
            await base44.entities.ProductionTask.update(pTask.id, updatePacking);
          }
        }

        // Otomatis majukan tahapan ke tugas berikutnya di pipeline
        try {
          await autoAdvanceNextTask(task, actualOutputQty);
        } catch (advErr) {
          console.error("Batch QC autoAdvanceNextTask error:", advErr);
        }
      }

      toast.success(`Validasi QC Berhasil! ${batchQcModal.length} tugas selesai & tervalidasi.`);
      setSelectedSubTaskIds(prev => {
        const next = new Set(prev);
        batchQcModal.forEach(t => next.delete(t.id));
        return next;
      });
      setBatchQcModal(null);
      setBatchQcNotes("");
      setBatchRejectMode("none");
      setBatchRejectQuantities({});
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["production-orders-list"] });
    } catch (err) {
      console.error("Gagal validasi QC batch:", err);
      toast.error("Terjadi kendala saat memvalidasi QC: " + (err.message || 'Error'));
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: SPLIT SINGLE CONSOLIDATED TASK INTO INDIVIDUAL VARIANT TASKS =====
  const handleSplitSingleTaskToVariants = async (task, variantItems) => {
    if (!task || !variantItems || variantItems.length === 0) return;
    setLoading(task.id);
    try {
      const brandPrefix = (task.po_brand || "PO").split('/')[0].trim();
      const activityLabel = ACTIVITY_LABELS[task.activity_type] || task.activity_type;

      // Create individual tasks for each variant
      const promises = variantItems.map(v => {
        return base44.entities.ProductionTask.create({
          task_name: `${brandPrefix} - ${v.variant_name} - ${activityLabel}`,
          po_id: task.po_id || task.production_order_id,
          po_number: String(task.po_number || ""),
          po_brand: task.po_brand,
          activity_type: task.activity_type,
          product_name: v.variant_name,
          botol_ukuran_aktual: v.size_ml,
          target_quantity: Number(v.target_quantity) || 0,
          unit: v.unit || (task.activity_type === 'racik' ? 'liter' : 'pcs'),
          pic_id: task.pic_id || null,
          pic_name: task.pic_name || null,
          pic_role: task.pic_role || null,
          status: task.status || "assigned",
          start_time: task.start_time || null,
          duration_minutes: task.duration_minutes || null,
          notes: task.notes || null,
        });
      });
      await Promise.all(promises);
      // Delete the consolidated single task
      await base44.entities.ProductionTask.delete(task.id);
      toast.success(`🎉 Berhasil memecah menjadi ${variantItems.length} tugas varian independen! Sekarang bisa dimulai per varian.`);
      invalidate();
    } catch (err) {
      console.error("Gagal memecah tugas varian:", err);
      toast.error("Gagal memecah tugas varian");
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: CONFIRM TAKEOVER PIC (> 6 Jam Jeda) =====
  const handleConfirmTakeoverPIC = async (task) => {
    setLoading(task.id);
    const now = new Date().toISOString();

    const prevPicId = task.pic_id;
    const prevPicName = task.pic_name || "PIC Sebelumnya";

    const prevProg = prevPicId ? task.member_progress?.[prevPicId] : null;
    const prevStart = prevProg?.start_time || task.start_time;
    let prevMins = 0;
    if (prevStart) {
      const elapsed = Math.max(0, Math.floor((new Date(now) - new Date(prevStart)) / 60000));
      const pauseMins = prevProg?.total_pause_minutes || 0;
      prevMins = Math.max(1, elapsed - pauseMins);
    }

    const existingHistory = Array.isArray(task.history_pic) ? task.history_pic : [];
    const newHistory = [
      ...existingHistory,
      {
        pic_id: prevPicId || "prev",
        pic_name: prevPicName,
        duration_minutes: prevMins,
        start_time: prevStart || now,
        end_time: now,
      }
    ];

    let newMemberProgress = { ...(task.member_progress || {}) };
    if (prevPicId && newMemberProgress[prevPicId]) {
      newMemberProgress[prevPicId] = {
        ...newMemberProgress[prevPicId],
        status: "transferred",
        total_duration_minutes: prevMins,
      };
    }

    newMemberProgress[member.id] = {
      status: "in_progress",
      start_time: now,
      total_pause_minutes: 0,
    };

    const updateData = {
      pic_id: member.id,
      pic_name: member.full_name,
      status: "in_progress",
      start_time: now,
      history_pic: newHistory,
      member_progress: newMemberProgress,
    };

    await base44.entities.ProductionTask.update(task.id, updateData);
    toast.success(`Berhasil mengambil alih tugas! Anda resmi menjadi PIC baru 🚀`);
    invalidate();
    setLoading(null);
    setTakeoverTask(null);
  };

  // ===== ACTION: CONFIRM MANUAL TRANSFER PIC (Send Transfer Request) =====
  const handleConfirmManualTransferPIC = async (task, targetMember) => {
    if (!targetMember) return;
    setLoading(task.id);
    const now = new Date().toISOString();

    const updateData = {
      pending_transfer_pic_id: targetMember.id,
      pending_transfer_pic_name: targetMember.full_name,
      transfer_requested_by: member.full_name || 'PIC Sebelumnya',
      transfer_requested_at: now,
    };

    await base44.entities.ProductionTask.update(task.id, updateData);
    toast.success(`📌 Permintaan pindah PIC telah dikirim ke ${targetMember.full_name}! Menunggu konfirmasi penerimaan.`);
    invalidate();
    setLoading(null);
    setTransferTask(null);
    setTargetMemberId("");
  };

  // ===== ACTION: ACCEPT PIC TRANSFER (Destination PIC) =====
  const handleAcceptPicTransfer = async (task) => {
    setLoading(task.id);
    const now = new Date().toISOString();

    const prevPicId = task.pic_id;
    const prevPicName = task.pic_name || "PIC Sebelumnya";

    const prevProg = prevPicId ? task.member_progress?.[prevPicId] : null;
    const prevStart = prevProg?.start_time || task.start_time;
    let prevMins = 0;
    if (prevStart) {
      const elapsed = Math.max(0, Math.floor((new Date(now) - new Date(prevStart)) / 60000));
      const pauseMins = prevProg?.total_pause_minutes || 0;
      prevMins = Math.max(1, elapsed - pauseMins);
    }

    const existingHistory = Array.isArray(task.history_pic) ? task.history_pic : [];
    const newHistory = [
      ...existingHistory,
      {
        pic_id: prevPicId || "prev",
        pic_name: prevPicName,
        duration_minutes: prevMins,
        start_time: prevStart || now,
        end_time: now,
      }
    ];

    let newMemberProgress = { ...(task.member_progress || {}) };
    if (prevPicId && newMemberProgress[prevPicId]) {
      newMemberProgress[prevPicId] = {
        ...newMemberProgress[prevPicId],
        status: "transferred",
        total_duration_minutes: prevMins,
      };
    }

    newMemberProgress[member.id] = {
      status: "in_progress",
      start_time: now,
      total_pause_minutes: 0,
    };

    const updateData = {
      pic_id: member.id,
      pic_name: member.full_name,
      pending_transfer_pic_id: null,
      pending_transfer_pic_name: null,
      transfer_requested_by: null,
      transfer_requested_at: null,
      status: "in_progress",
      start_time: now,
      history_pic: newHistory,
      member_progress: newMemberProgress,
    };

    await base44.entities.ProductionTask.update(task.id, updateData);
    toast.success(`🎉 Berhasil menerima tugas! Anda resmi menjadi PIC Utama baru.`);
    invalidate();
    setLoading(null);
  };

  // ===== ACTION: REJECT PIC TRANSFER (Destination PIC) =====
  const handleRejectPicTransfer = async (task) => {
    setLoading(task.id);
    const updateData = {
      pending_transfer_pic_id: null,
      pending_transfer_pic_name: null,
      transfer_requested_by: null,
      transfer_requested_at: null,
    };

    await base44.entities.ProductionTask.update(task.id, updateData);
    toast.error(`Penyerahan tugas ditolak. Tugas tetap berada pada PIC semula.`);
    invalidate();
    setLoading(null);
  };

  // ===== ACTION: HAPUS TUGAS (Synchronized with PO Tasks) =====
  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus tugas "${task.task_name}"?`)) return;
    setLoading(task.id);
    try {
      await base44.entities.ProductionTask.delete(task.id);
      toast.success("Tugas berhasil dihapus! 🗑️");
      invalidate();
    } catch (err) {
      console.error("Gagal menghapus tugas:", err);
      toast.error("Gagal menghapus tugas");
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: EARLY START PACKING (Curi Start Pengemasan dari Tab Akan Datang) =====
  const handleEarlyStartPacking = (task) => {
    const matchedPO = productionOrders.find(p => p.id === task.po_id || p.order_number === task.po_number || p.id === task.production_order_id);
    setEarlyStartModal({ task, po: matchedPO });
  };

  const handleEarlyStartPackingGroup = (group) => {
    const matchedPO = productionOrders.find(p => p.id === group.po_id || p.order_number === group.po_number || p.id === group.tasks[0]?.production_order_id);
    setEarlyStartModal({ group, po: matchedPO, tasks: group.tasks });
  };

  const executeEarlyStartPacking = async () => {
    if (!earlyStartModal) return;
    setLoading("early_start");
    const now = new Date().toISOString();

    try {
      const { task, group, po, tasks: groupTasksList } = earlyStartModal;
      const memId = currentMember?.id || member?.id;
      const memName = currentMember?.full_name || member?.full_name;

      if (task) {
        const existingPacking = tasks.find(t => 
          (t.po_id === task.po_id || t.po_number === task.po_number || t.id === task.id) &&
          (t.activity_type === 'packing' || t.activity_type === 'pengemasan')
        );

        if (existingPacking) {
          await base44.entities.ProductionTask.update(existingPacking.id, {
            status: "in_progress",
            pic_id: memId,
            pic_name: memName,
            start_time: existingPacking.start_time || now,
            member_progress: {
              ...(existingPacking.member_progress || {}),
              [memId]: { status: "in_progress", start_time: now, role: "packing_pic" }
            }
          });
        } else {
          const brandPrefix = (task.po_brand || po?.brand_name || "PO").split('/')[0].trim();
          const bottleSize = Number(task.botol_ukuran_aktual || task.ukuran_ml || po?.botol_ukuran_aktual || po?.ukuran_botol_ml || 30);
          const isSourceLiter = (task.unit || '').toLowerCase().includes('l') || (task.unit || '').toLowerCase().includes('liter');
          const packingTargetQty = isSourceLiter 
            ? Math.round((Number(task.target_quantity) || 0) * 1000 / bottleSize)
            : (Number(task.target_quantity) || 1);

          await base44.entities.ProductionTask.create({
            task_name: `${brandPrefix} - ${task.product_name || task.task_name || 'Produk'} - Pengemasan`,
            po_id: task.po_id || po?.id,
            po_number: String(task.po_number || po?.order_number || ''),
            po_brand: task.po_brand || po?.brand_name,
            activity_type: "packing",
            stage: "pengemasan",
            product_name: task.product_name || task.task_name,
            target_quantity: packingTargetQty,
            unit: "pcs",
            botol_ukuran_aktual: bottleSize,
            pic_id: memId,
            pic_name: memName,
            start_time: now,
            status: "in_progress",
            task_date: now.split("T")[0],
            member_progress: {
              [memId]: { status: "in_progress", start_time: now, role: "packing_pic" }
            },
            notes: `[Pengemasan Dimulai Lebih Awal (Curi Start) oleh ${memName}]`
          });
        }
      } else if (group && groupTasksList?.length > 0) {
        for (const t of groupTasksList) {
          const brandPrefix = (t.po_brand || po?.brand_name || "PO").split('/')[0].trim();
          const existingPacking = tasks.find(ex => 
            (ex.po_id === t.po_id || ex.po_number === t.po_number) &&
            ex.product_name === t.product_name &&
            (ex.activity_type === 'packing' || ex.activity_type === 'pengemasan')
          );
          if (existingPacking) {
            await base44.entities.ProductionTask.update(existingPacking.id, {
              status: "in_progress",
              pic_id: memId,
              pic_name: memName,
              start_time: existingPacking.start_time || now,
              member_progress: {
                ...(existingPacking.member_progress || {}),
                [memId]: { status: "in_progress", start_time: now, role: "packing_pic" }
              }
            });
          } else {
            const bottleSize = Number(t.botol_ukuran_aktual || t.ukuran_ml || po?.botol_ukuran_aktual || po?.ukuran_botol_ml || 30);
            const isSourceLiter = (t.unit || '').toLowerCase().includes('l') || (t.unit || '').toLowerCase().includes('liter');
            const packingTargetQty = isSourceLiter 
              ? Math.round((Number(t.target_quantity) || 0) * 1000 / bottleSize)
              : (Number(t.target_quantity) || 1);

            await base44.entities.ProductionTask.create({
              task_name: `${brandPrefix} - ${t.product_name || t.task_name || 'Produk'} - Pengemasan`,
              po_id: t.po_id || po?.id,
              po_number: String(t.po_number || po?.order_number || ''),
              po_brand: t.po_brand || po?.brand_name,
              activity_type: "packing",
              stage: "pengemasan",
              product_name: t.product_name || t.task_name,
              target_quantity: packingTargetQty,
              unit: "pcs",
              botol_ukuran_aktual: bottleSize,
              pic_id: memId,
              pic_name: memName,
              start_time: now,
              status: "in_progress",
              task_date: now.split("T")[0],
              member_progress: {
                [memId]: { status: "in_progress", start_time: now, role: "packing_pic" }
              },
              notes: `[Pengemasan Dimulai Lebih Awal (Curi Start) oleh ${memName}]`
            });
          }
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["production-tasks"] });
      toast.success("Berhasil mencuri start! Tugas Pengemasan sekarang aktif di Tugas Aktif kamu.");
      setEarlyStartModal(null);
      setActiveTab("active");
    } catch (err) {
      console.error(err);
      toast.error("Gagal memulai pengemasan: " + err.message);
    } finally {
      setLoading(null);
    }
  };

  // ===== ACTION: LANJUTKAN (resume from pause) =====
  const handleLanjutkan = async (task) => {
    setLoading(task.id);
    const now = new Date().toISOString();
    const mProg = task.member_progress?.[member.id];

    const pauseDurationMins = mProg?.last_pause_time
      ? Math.round((new Date(now).getTime() - new Date(mProg.last_pause_time).getTime()) / 60000)
      : 0;

    let newMemberProgress = { ...(task.member_progress || {}) };
    newMemberProgress[member.id] = {
      ...mProg,
      status: "in_progress",
      total_pause_minutes: (mProg?.total_pause_minutes || 0) + pauseDurationMins,
    };

    await base44.entities.ProductionTask.update(task.id, {
      member_progress: newMemberProgress,
      status: "in_progress",
    });

    toast.success("Tugas dilanjutkan! Ayo gas~ 🚀");
    invalidate();
    setLoading(null);
  };

  // ===== ACTION: JEDA (open popup) =====
  const handleOpenJeda = (task) => {
    setJedaTask(task);
    setJedaReason("");
    setAsaReaction("");
    setJedaStep("ask");
    setAsaMessage(getAsaPauseMessages(sapaan, gaya));
  };

  const handleConfirmJeda = async () => {
    if (!jedaReason.trim()) {
      toast.error("Alasan jeda harus diisi ya!");
      return;
    }

    // Show ASA reaction first
    const reaction = getAsaPauseReaction(sapaan, gaya, jedaReason);
    setAsaReaction(reaction);
    setJedaStep("reaction");

    // Save pause in background
    const now = new Date().toISOString();
    const task = jedaTask;
    const mProg = task.member_progress?.[member.id];

    let newMemberProgress = { ...(task.member_progress || {}) };
    newMemberProgress[member.id] = {
      ...mProg,
      status: "on_hold",
      last_pause_time: now,
      pause_reasons: [...(mProg?.pause_reasons || []), {
        reason: jedaReason.trim(),
        time: now,
        by: member.full_name,
      }],
    };

    const existingNotes = task.notes || '';
    const jedaNote = `[JEDA ${format(new Date(), 'dd/MM HH:mm')}] ${jedaReason.trim()} — ${member.full_name}`;
    const updatedNotes = existingNotes ? `${existingNotes}\n${jedaNote}` : jedaNote;

    await base44.entities.ProductionTask.update(task.id, {
      member_progress: newMemberProgress,
      status: "on_hold",
      notes: updatedNotes,
    });

    invalidate();
  };

  const closeJedaDialog = () => {
    setJedaTask(null);
    setJedaReason("");
    setAsaReaction("");
    setJedaStep("ask");
  };

  // ===== ACTION: SELESAI (open dialog) =====
  const handleOpenSelesai = (task, defaultDiscrepancy = "") => {
    setSelesaiTask(task);
    const targetPcs = getTaskPoTargetPcs(task, productionOrders);
    const act = (task.activity_type || '').toLowerCase();
    const isBottle = ['filling', 'packing', 'pengemasan', 'labeling', 'sealing'].includes(act);

    let initQty = "";
    if (task.completed_quantity !== undefined && task.completed_quantity !== null && String(task.completed_quantity) !== "") {
      initQty = String(Math.round(Number(task.completed_quantity)));
    } else if (isBottle) {
      initQty = String(targetPcs);
    } else {
      initQty = String(task.target_quantity || "");
    }
    setCompletedQty(initQty);

    setDiscrepancyType(defaultDiscrepancy || task.discrepancy_type || "");
    setDiscrepancyReason(task.discrepancy_reason || "");
    setDiscrepancyRejectQty(task.reject_qty ? String(task.reject_qty) : "");

    setSelesaiNotes("");
    setMaserasiHari(task.maserasi_hari !== undefined && task.maserasi_hari !== null ? String(task.maserasi_hari) : "2");
    setMaserasiJam(task.maserasi_jam !== undefined && task.maserasi_jam !== null ? String(task.maserasi_jam) : "0");
    setRejects(task.rejected_items || []);

    if (isBottle) {
      setRejectStep("form");
    } else {
      setRejectStep("ask_reject");
    }
  };

  const handleAddVariantReject = (variantName = '') => {
    setRejects([...rejects, { 
      id: Date.now(), 
      variantName, 
      items: [{ id: Date.now() + 1, name: '', qty: '', notes: '' }] 
    }]);
  };

  const handleAddItemReject = (rejectId) => {
    setRejects(rejects.map(r => 
      r.id === rejectId 
        ? { ...r, items: [...r.items, { id: Date.now(), name: '', qty: '', notes: '' }] }
        : r
    ));
  };

  const updateRejectVariant = (rejectId, variantName) => {
    setRejects(rejects.map(r => r.id === rejectId ? { ...r, variantName } : r));
  };

  const updateRejectItem = (rejectId, itemId, field, value) => {
    setRejects(rejects.map(r => 
      r.id === rejectId 
        ? { ...r, items: r.items.map(it => it.id === itemId ? { ...it, [field]: value } : it) }
        : r
    ));
  };

  const removeRejectItem = (rejectId, itemId) => {
    setRejects(rejects.map(r => 
      r.id === rejectId 
        ? { ...r, items: r.items.filter(it => it.id !== itemId) }
        : r
    ).filter(r => r.items.length > 0));
  };

  const getRejectOptions = () => {
    if (selesaiTask?.activity_type === 'racik' || selesaiTask?.activity_type === 'mixing') {
      return ['Hasil Liquid Off-Spec (Aroma/Warna)', 'Bahan Baku Tumpah / Bocor', 'Endapan / Reaksi Gagal', 'Keran Tangki Stainless Bocor', 'Lainnya'];
    }
    if (selesaiTask?.activity_type === 'filling') {
      return [
        'Botol Cacat / Retak / Pecah',
        'Tutup / Cap Longgar / Rusak',
        'Spray / Pump / Pipet Mampet',
        'Liquid Tumpah saat Filling',
        'Lainnya'
      ];
    }
    return [
      'Dus / Inner Box Penyok / Sobek',
      'Stiker Label Miring / Gelembung',
      'Sablon / Cetakan / Hot Print Pudar',
      'Exp Date / Batch Number Salah Cetak',
      'Gift Card / Aksesoris Cacat',
      'Lainnya'
    ]; // pengemasan
  };

  const handleConfirmSelesai = async () => {
    if (!selesaiTask) return;
    const task = selesaiTask;
    const act = (task.activity_type || '').toLowerCase();
    const isBottle = ['filling', 'packing', 'pengemasan', 'labeling', 'sealing'].includes(act);
    const targetPO = isBottle ? getTaskPoTargetPcs(task, productionOrders) : (Number(task.target_quantity) || 0);
    const realQty = completedQty !== "" ? Number(completedQty) : targetPO;
    const selisih = realQty - targetPO;

    // Strict validation for bottle stages (filling / packing) when discrepancy exists
    if (isBottle && selisih !== 0) {
      if (!discrepancyType) {
        toast.error(`Hasil jadi (${realQty} pcs) tidak sesuai target PO (${targetPO} pcs). Harap pilih status: Dicicil, Reject, atau Toleransi!`);
        return;
      }
      if (discrepancyType !== 'dicicil' && !discrepancyReason.trim()) {
        toast.error("Harap masukkan alasan mengapa hasil jadi tidak sesuai target PO!");
        return;
      }
    }

    // ===== ALUR DICICIL / PARTIAL INSTALLMENT =====
    // Tugas TETAP AKTIF (in_progress), progres & akumulasi output dicatat bertahap
    if (discrepancyType === 'dicicil') {
      setLoading(selesaiTask.id);
      const now = new Date().toISOString();
      const mProg = task.member_progress?.[member.id];
      const prevCompleted = Number(task.completed_quantity) || 0;
      let batchQty = 0;
      let newCompleted = 0;

      if (realQty > prevCompleted) {
        // Operator menginput angka total akumulasi (misal sebelumnya 350, input 650 -> batch 300)
        batchQty = realQty - prevCompleted;
        newCompleted = realQty;
      } else if (prevCompleted > 0 && realQty <= prevCompleted) {
        // Operator menginput jumlah batch hari ini (misal sebelumnya 350, input 300 -> total 650)
        batchQty = realQty;
        newCompleted = prevCompleted + realQty;
      } else {
        // Cicilan pertama
        batchQty = realQty;
        newCompleted = realQty;
      }

      if (batchQty <= 0) {
        toast.error("Jumlah cicilan harus lebih dari 0!");
        setLoading(null);
        return;
      }

      const grossMinutes = mProg?.start_time
        ? Math.round((new Date(now).getTime() - new Date(mProg.start_time).getTime()) / 60000)
        : 0;
      const netMinutes = grossMinutes - (mProg?.total_pause_minutes || 0);
      const newLogEntry = {
        id: `batch_${Date.now()}`,
        date: now,
        operator: member.full_name,
        operator_id: member.id,
        qty: batchQty,
        total_so_far: newCompleted,
        target: targetPO,
        notes: discrepancyReason || selesaiNotes || "Cicilan output berkala",
        qc_status: "waiting_qc",
        qc_validator_name: null,
        qc_validator_id: null,
        qc_validated_at: null,
        qc_passed_qty: null,
        qc_reject_qty: 0,
        qc_notes: null,
      };

      const cleanBaseNotes = (task.notes || '')
        .replace(/\[Cicil Output[^\]]*\]/gi, '')
        .replace(/\[Output:[^\]]+\]/gi, '')
        .trim();
      const cicilNote = `[Cicil Output: Batch ${batchQty} pcs (Total: ${newCompleted}/${targetPO} pcs - Menunggu QC) oleh ${member.full_name}]`;
      const finalNotes = [cleanBaseNotes, cicilNote, selesaiNotes ? `[CATATAN] ${selesaiNotes}` : '']
        .filter(Boolean)
        .join('\n');

      const updatedPartialLogs = [...(task.partial_logs || []), newLogEntry];

      // Catat ke DailyProduction agar rekap harian pabrik real-time & presisi
      const today = now.split("T")[0];
      const aktivitasMap = {
        racik: "mixing", maserasi: "lainnya", filling: "filling",
        packing: "packing", labeling: "packing", qc: "quality_check",
        preparation: "lainnya", other: "lainnya",
      };

      await base44.entities.DailyProduction.create({
        date: today,
        shift: getShiftFromHour(new Date().getHours()),
        activity: aktivitasMap[task.activity_type] || "lainnya",
        operator: member.full_name,
        qty_produced: batchQty,
        waste_qty: 0,
        brand_name: task.po_brand || "Brand Kustom",
        target_po: task.po_number || "",
        notes: `[Cicil Output] ${task.task_name}. Batch ini: ${batchQty} pcs (Total: ${newCompleted}/${targetPO} pcs - Menunggu QC)`,
        duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
      }).catch(() => {});

      let updatedMemberProg = { ...(task.member_progress || {}) };
      if (member?.id) {
        updatedMemberProg[member.id] = {
          ...mProg,
          status: "in_progress",
          last_installment_at: now,
        };
      }

      await base44.entities.ProductionTask.update(task.id, {
        completed_quantity: newCompleted,
        target_quantity: targetPO,
        unit: isBottle ? 'pcs' : task.unit,
        status: "in_progress",
        partial_logs: updatedPartialLogs,
        has_pending_qc_cicil: true,
        pending_qc_cicil_qty: batchQty,
        notes: finalNotes,
        discrepancy_type: 'dicicil',
        discrepancy_reason: discrepancyReason || "Pengerjaan dicicil berkala",
        member_progress: updatedMemberProg,
      });

      toast.success(`Cicilan output (${batchQty} pcs) berhasil disimpan & diajukan ke QC! ⏳`);
      invalidate();
      setLoading(null);
      setSelesaiTask(null);
      return;
    }

    setLoading(selesaiTask.id);
    const now = new Date().toISOString();
    const mProg = task.member_progress?.[member.id];
    const isPic = task.pic_id === member.id;
    const isRacik = act === "racik" || act === "mixing";
    const poStatusMap = TASK_PO_STATUS_MAP[task.activity_type];

    const grossMinutes = mProg?.start_time
      ? Math.round((new Date(now).getTime() - new Date(mProg.start_time).getTime()) / 60000)
      : 0;
    const netMinutes = grossMinutes - (mProg?.total_pause_minutes || 0);

    let newMemberProgress = { ...(task.member_progress || {}) };
    newMemberProgress[member.id] = {
      ...mProg,
      status: "completed",
      end_time: now,
      gross_minutes: grossMinutes,
      duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
      rejected_items: rejects.length > 0 ? rejects : undefined,
    };

    const isRejectChoice = isBottle && selisih !== 0 && discrepancyType === 'reject';
    const rejectCount = isRejectChoice ? Number(discrepancyRejectQty || Math.abs(selisih)) : 0;

    let discrepancySummary = "";
    if (isBottle) {
      if (selisih === 0) {
        discrepancySummary = `[Output: ${realQty} pcs / Target PO: ${targetPO} pcs (SESUAI TARGET 100%)]`;
      } else if (discrepancyType === 'reject') {
        discrepancySummary = `[Output: ${realQty} pcs / Target PO: ${targetPO} pcs (Selisih: ${selisih} pcs | REJECT: ${rejectCount} pcs | Alasan: ${discrepancyReason})]`;
      } else {
        discrepancySummary = `[Output: ${realQty} pcs / Target PO: ${targetPO} pcs (Selisih: ${selisih} pcs | TOLERANSI PO / BUKAN REJECT | Alasan: ${discrepancyReason})]`;
      }
    }

    const cleanBaseNotes = (task.notes || '')
      .replace(/\[Selesai Dikerjakan\]\s*Menunggu Validasi QC/gi, '')
      .replace(/\[Output:[^\]]+\]/gi, '')
      .trim();

    const finalNotes = [cleanBaseNotes, discrepancySummary, selesaiNotes ? `[CATATAN] ${selesaiNotes}` : '']
      .filter(Boolean)
      .join('\n');

    const updateData = {
      member_progress: newMemberProgress,
      notes: finalNotes,
      completed_quantity: realQty,
      target_quantity: targetPO,
      unit: isBottle ? 'pcs' : task.unit,
      discrepancy_type: isBottle ? (selisih === 0 ? 'sesuai' : discrepancyType) : undefined,
      discrepancy_reason: isBottle ? (selisih === 0 ? '' : discrepancyReason) : undefined,
      reject_qty: isBottle ? rejectCount : (rejects.reduce((s, r) => s + (r.items || []).reduce((is, it) => is + (Number(it.qty) || 0), 0), 0)),
      is_reject: isRejectChoice,
      partial_logs: task.partial_logs || undefined,
    };

    // Auto-save ke DailyProduction
    const today = now.split("T")[0];
    const aktivitasMap = {
      racik: "mixing", maserasi: "lainnya", filling: "filling",
      packing: "packing", labeling: "packing", qc: "quality_check",
      preparation: "lainnya", other: "lainnya",
    };

    await base44.entities.DailyProduction.create({
      date: today,
      shift: getShiftFromHour(new Date().getHours()),
      activity: aktivitasMap[task.activity_type] || "lainnya",
      operator: member.full_name,
      qty_produced: realQty,
      waste_qty: updateData.reject_qty || 0,
      brand_name: task.po_brand || "Brand Kustom",
      target_po: task.po_number || "",
      notes: `[Tugas] ${task.task_name}. ${discrepancySummary}${selesaiNotes ? ' ' + selesaiNotes : ''}`,
      duration_minutes: netMinutes > 0 ? netMinutes : grossMinutes,
    }).catch(() => {});

    const isWaitingQC = task.status === "waiting_qc";
    const canCompleteDirectly = isWaitingQC || isQCUser || canValidateQC || isDevOrOwner;

    if (canCompleteDirectly) {
      updateData.status = "completed";
      updateData.end_time = now;
      if (canValidateQC || isQCUser) {
        updateData.qc_validator_id = currentMember?.id || member?.id;
        updateData.qc_validator_name = currentMember?.full_name || member?.full_name;
        updateData.validated_by = currentMember?.full_name || member?.full_name;
      }

      const taskGrossMinutes = task.start_time ? Math.round((new Date(now).getTime() - new Date(task.start_time).getTime()) / 60000) : grossMinutes;
      const taskNetMinutes = taskGrossMinutes - (mProg?.total_pause_minutes || 0);

      updateData.gross_minutes = taskGrossMinutes;
      updateData.total_pause_minutes = mProg?.total_pause_minutes || 0;
      updateData.duration_minutes = taskNetMinutes > 0 ? taskNetMinutes : taskGrossMinutes;

      if (isRacik) {
        // Pertahankan maserasi_start yang sudah di-set saat PIC mixing klik selesai
        const preservedStart = task.maserasi_start || now;
        let finalHari = task.maserasi_hari !== undefined && task.maserasi_hari !== null ? Number(task.maserasi_hari) : (maserasiHari === "" ? 2 : Number(maserasiHari));
        let finalJam = task.maserasi_jam !== undefined && task.maserasi_jam !== null ? Number(task.maserasi_jam) : (maserasiJam === "" ? 0 : Number(maserasiJam));
        updateData.maserasi_jam = finalJam;
        updateData.maserasi_hari = finalHari;
        updateData.maserasi_start = preservedStart;

        if (task.po_id) {
          await base44.entities.ProductionOrder.update(task.po_id, {
            status: "maserasi",
            maserasi_start: preservedStart,
            maserasi_durasi_jam: finalJam,
            maserasi_durasi_hari: finalHari,
          }).catch(() => {});
        }
      } else if (task.po_id && poStatusMap?.onComplete) {
        if (poStatusMap.onComplete === "siap_kirim") {
          const isReady = await checkIfPOReadyForSiapKirim(task.po_id);
          await base44.entities.ProductionOrder.update(task.po_id, {
            status: isReady ? "siap_kirim" : "packing",
          }).catch(() => {});
        } else {
          await base44.entities.ProductionOrder.update(task.po_id, {
            status: poStatusMap.onComplete,
          }).catch(() => {});
        }
      }

      // Task-based stock deduction
      if (task.po_id) {
        try {
          const orders = await base44.entities.ProductionOrder.filter({ id: task.po_id });
          if (orders.length > 0) {
            const order = orders[0];
            const inventoryItems = [
              ...botolList.map(i => ({ id: `botol-${i.id}`, _stokId: i.id, _entity: 'Botol', total_stock: i.stok ?? 0 })),
              ...tutupList.map(i => ({ id: `tutup-${i.id}`, _stokId: i.id, _entity: 'Tutup', total_stock: i.stok ?? 0 })),
              ...sprayList.map(i => ({ id: `spray-${i.id}`, _stokId: i.id, _entity: 'Spray', total_stock: i.stok ?? 0 })),
              ...bahanList.map(i => ({ id: `bahan-${i.id}`, _stokId: i.id, _entity: 'BahanCair', total_stock: i.stok ?? 0 })),
              ...rawInventory,
            ];
            await deductStockForTask(order, task.activity_type, inventoryItems, bahanList);
          }
        } catch (e) { console.error("Stock deduction error:", e); }
      }

      // Jika tahapan Filling selesai -> Otomatis sinkronkan jumlah output riil & status Pengemasan paralel
      if (act === 'filling') {
        const relatedPackings = tasks.filter(ex => 
          (ex.po_id === task.po_id || ex.po_number === task.po_number) &&
          (ex.activity_type === 'packing' || ex.activity_type === 'pengemasan') &&
          (ex.product_name === task.product_name || !task.product_name)
        );

        for (const pTask of relatedPackings) {
          const updatePacking = {
            target_quantity: realQty,
            unit: 'pcs',
            available_from_filling: realQty,
          };
          if ((pTask.status || '').toLowerCase() === 'waiting_filling') {
            updatePacking.status = 'waiting_qc';
            updatePacking.completed_quantity = realQty;
          }
          await base44.entities.ProductionTask.update(pTask.id, updatePacking);
        }
      }

      // Otomatis buatkan tugas kelanjutan di pipeline jika belum ada
      try {
        await autoAdvanceNextTask(task, realQty);
      } catch (e) {
        console.error("Auto advance task error:", e);
      }

      toast.success("Tugas tervalidasi & selesai sempurna! 🎉");
    } else {
      // PIC submitting to QC
      const isPacking = act === 'packing' || act === 'pengemasan' || act === 'labeling' || act === 'sealing';
      const relatedFilling = tasks.find(t => 
        (t.po_id === task.po_id || t.po_number === task.po_number) &&
        (t.activity_type === 'filling') &&
        (!task.product_name || !t.product_name || t.product_name.toLowerCase().trim() === task.product_name.toLowerCase().trim()) &&
        !['completed', 'selesai'].includes((t.status || '').toLowerCase())
      );
      if (isPacking && relatedFilling) {
        toast.error(`Pengemasan ${task.product_name ? `(${task.product_name}) ` : ''}tidak bisa diselesaikan karena proses Filling masih berjalan! Harap tunggu hingga seluruh botol selesai di-filling.`);
        setLoading(null);
        setSelesaiTask(null);
        return;
      }

      // Tahap Maserasi DIMULAI LANGSUNG saat PIC Mixing klik selesai & set waktu maserasi!
      if (isRacik) {
        const finalHari = maserasiHari === "" ? 2 : Number(maserasiHari);
        const finalJam = maserasiJam === "" ? 0 : Number(maserasiJam);
        updateData.maserasi_hari = finalHari;
        updateData.maserasi_jam = finalJam;
        updateData.maserasi_start = now;

        if (task.po_id) {
          await base44.entities.ProductionOrder.update(task.po_id, {
            status: "maserasi",
            maserasi_start: now,
            maserasi_durasi_jam: finalJam,
            maserasi_durasi_hari: finalHari,
          }).catch(() => {});
        }
        toast.success(`Racikan selesai! PO resmi masuk tahap Maserasi (${finalHari} Hari ${finalJam > 0 ? `${finalJam} Jam` : ''}) & hitungan mundur dimulai seketika ⏳`);
      } else {
        toast.success("Tugas diselesaikan & diajukan ke QC dengan rincian output riil! ⏳");
      }

      updateData.status = "waiting_qc";
      updateData.end_time = now;
    }

    await base44.entities.ProductionTask.update(task.id, updateData);
    invalidate();
    setLoading(null);
    setSelesaiTask(null);
  };

  // ===== ACTION: OPEN QC CICIL MODAL =====
  const handleOpenCicilQc = (task) => {
    const pendingLog = [...(task.partial_logs || [])].reverse().find(l => l.qc_status === 'waiting_qc');
    const batchQty = pendingLog?.qty || task.pending_qc_cicil_qty || 0;
    
    setCicilQcModal({ task, batch: pendingLog });
    setCicilQcPassedQty(String(batchQty));
    setCicilQcRejectQty("0");
    setCicilQcNotes("");
    setCicilQcChecklist({
      volumeMl: true,
      cappingSeal: true,
      leakFree: true,
      cleanParticle: true,
    });
  };

  // ===== ACTION: CONFIRM QC CICIL =====
  const handleConfirmCicilQc = async () => {
    if (!cicilQcModal) return;
    const { task, batch } = cicilQcModal;
    const passedNum = Number(cicilQcPassedQty) || 0;
    const rejectNum = Number(cicilQcRejectQty) || 0;

    if (passedNum < 0 || rejectNum < 0) {
      toast.error("Jumlah botol lolos dan reject tidak boleh negatif!");
      return;
    }

    setLoading(task.id);
    const now = new Date().toISOString();

    const updatedPartialLogs = (task.partial_logs || []).map(l => {
      if ((batch?.id && l.id === batch.id) || (!batch?.id && l.qc_status === 'waiting_qc')) {
        return {
          ...l,
          qc_status: 'approved',
          qc_passed_qty: passedNum,
          qc_reject_qty: rejectNum,
          qc_validator_name: currentMember?.full_name || member?.full_name,
          qc_validator_id: currentMember?.id || member?.id,
          qc_validated_at: now,
          qc_notes: cicilQcNotes || "Validasi QC cicilan lolos",
        };
      }
      return l;
    });

    const prevQcPassed = Number(task.qc_passed_quantity) || 0;
    const newQcPassed = prevQcPassed + passedNum;
    const prevReject = Number(task.reject_qty) || 0;
    const newReject = prevReject + rejectNum;

    const stillWaiting = updatedPartialLogs.some(l => l.qc_status === 'waiting_qc');

    await base44.entities.ProductionTask.update(task.id, {
      partial_logs: updatedPartialLogs,
      has_pending_qc_cicil: stillWaiting,
      pending_qc_cicil_qty: stillWaiting ? updatedPartialLogs.find(l => l.qc_status === 'waiting_qc')?.qty || 0 : 0,
      qc_passed_quantity: newQcPassed,
      reject_qty: newReject,
    });

    // Otomatis sinkronkan botol lolos ke tugas Pengemasan paralel
    const act = (task.activity_type || '').toLowerCase();
    if (act === 'filling') {
      const relatedPackings = tasks.filter(ex => 
        (ex.po_id === task.po_id || ex.po_number === task.po_number) &&
        (ex.activity_type === 'packing' || ex.activity_type === 'pengemasan') &&
        (ex.product_name === task.product_name || !task.product_name)
      );

      for (const pTask of relatedPackings) {
        await base44.entities.ProductionTask.update(pTask.id, {
          available_from_filling: newQcPassed,
        }).catch(() => {});
      }
    }

    toast.success(`Cicilan ${passedNum} pcs resmi divalidasi QC & dinyatakan LOLOS! 🎉`);
    invalidate();
    setLoading(null);
    setCicilQcModal(null);
  };

  // ===== RENDER TASK CARD =====
  const renderTaskCard = (task, isSubCard = false, isSelected = false, onToggleSelect = null) => {
    const statusCfg = getTaskStatusConfig(task);
    const StatusIcon = statusCfg.icon;
    const actCls = ACTIVITY_COLORS[task.activity_type] || ACTIVITY_COLORS.other;
    const mProg = task.member_progress?.[member.id];
    const isPic = task.pic_id === member.id;
    const isAssigned = task.status === "assigned" || task.status === "belum_dimulai" || task.status === "pending" || !task.status;
    const isTaskInProgress = task.status === "in_progress";
    const isInProgress = mProg?.status === "in_progress" || (task.status === "in_progress" && !mProg);
    const isPaused = task.status === "on_hold" || mProg?.status === "on_hold";
    const isCompleted = task.status === "completed";
    const isProcessing = loading === task.id;

    // Retrieve start_time from current member, or PIC member_progress, or task.start_time
    const picProgress = task.pic_id ? task.member_progress?.[task.pic_id] : Object.values(task.member_progress || {})[0];
    const startTime = mProg?.start_time || picProgress?.start_time || task.start_time;
    const pauseMins = mProg?.total_pause_minutes || picProgress?.total_pause_minutes || 0;
    const pauseReasons = mProg?.pause_reasons?.length ? mProg.pause_reasons : (picProgress?.pause_reasons || []);

    // Hitung durasi jeda dalam jam
    const lastPauseObj = pauseReasons?.length ? pauseReasons[pauseReasons.length - 1] : null;
    const lastPauseTime = lastPauseObj?.time || task.updated_date;
    const pauseHours = isPaused && lastPauseTime ? (new Date().getTime() - new Date(lastPauseTime).getTime()) / (1000 * 60 * 60) : 0;
    const isPauseOver6h = isPaused && pauseHours >= 6;

    const act = (task.activity_type || '').toLowerCase();
    const isProduksiTask = act === 'racik' || act === 'mixing' || act === 'maserasi' || act === 'filling';
    const isPengemasanTask = act === 'packing' || act === 'pengemasan' || act === 'labeling' || act === 'sealing' || act === 'packaging';
    const isDevOrOwner = member?.role === 'developer' || member?.role === 'owner' || (member?.position || '').toLowerCase().includes('developer');
    const isProductionTeammate = isDevOrOwner || (isProduksiTask && isProduksiTeam) || (isPengemasanTask && isPengemasanTeam);
    const isSameTeam = isProductionTeammate || isQCUser || canValidateQC;

    // Teammates can takeover if paused > 6 hours OR if user is Kepala / Supervisor / Dev
    const canTakeover = isPaused && !isPic && isProductionTeammate && (isPauseOver6h || isKepala || isDevOrOwner);
    // Active PIC or Kepala/Supervisor/Dev can manually transfer PIC (only if task is active & not waiting_qc or completed)
    const canManualTransfer = (isPic || isKepala || isDevOrOwner) && isProductionTeammate && !isCompleted && task.status !== "waiting_qc";

    const isMixingTask = act === 'racik' || act === 'mixing';
    const cardMatchedPO = productionOrders.find(p => p.id === task.po_id || p.order_number === task.po_number || p.id === task.production_order_id);
    const matchedProduct = cardMatchedPO?.products?.find(p => 
      (task.product_name && p.product_name && (p.product_name.toLowerCase().trim() === task.product_name.toLowerCase().trim() || task.product_name.toLowerCase().includes(p.product_name.toLowerCase()))) ||
      (task.task_name && p.product_name && task.task_name.toLowerCase().includes(p.product_name.toLowerCase()))
    ) || cardMatchedPO?.products?.[0];

    const taskRacikan = task.racikan || task.racikan_breakdown || matchedProduct?.racikan || matchedProduct?.racikan_digunakan || cardMatchedPO?.racikan || cardMatchedPO?.racikan_digunakan || [];
    const bottleSize = Number(task.botol_ukuran_aktual || task.ukuran_ml || matchedProduct?.size_ml || matchedProduct?.ukuran_botol_ml || cardMatchedPO?.botol_ukuran_aktual || cardMatchedPO?.ukuran_botol_ml || 30);
    const isBottleStage = act === 'filling' || isPengemasanTask;
    let displayTargetQty = Number(task.target_quantity) || 0;
    let displayUnit = task.unit || 'pcs';

    if (isBottleStage) {
      displayUnit = 'pcs';
      if ((task.unit || '').toLowerCase().includes('l') || (task.unit || '').toLowerCase().includes('liter')) {
        displayTargetQty = Math.round(displayTargetQty * 1000 / bottleSize);
      } else {
        displayTargetQty = Math.round(displayTargetQty);
      }
    }

    const isLiterUnit = !isBottleStage && ((displayUnit || '').toLowerCase().includes('l') || (displayUnit || '').toLowerCase().includes('liter'));
    let cardDual = null;
    let cardVolMl = 0;
    if (isLiterUnit) {
      cardVolMl = displayTargetQty * 1000;
      cardDual = cardVolMl > 0 ? calculateRacikanDual(cardVolMl, taskRacikan) : null;
    } else {
      const targetPcs = displayTargetQty;
      cardVolMl = targetPcs * bottleSize;
      cardDual = cardVolMl > 0 ? calculateRacikanDual(cardVolMl, taskRacikan) : null;
    }

    return (
      <Card key={task.id} className={`transition-all hover:shadow-md border-l-4 ${isSubCard ? 'bg-white shadow-2xs border border-slate-200/80 rounded-xl' : ''} ${isSelected ? 'ring-2 ring-blue-500/70 bg-blue-50/20' : ''}`} style={{
        borderLeftColor: (isTaskInProgress || isInProgress) ? '#3b82f6' : isPaused ? '#f59e0b' : isCompleted ? '#22c55e' : '#e2e8f0'
      }}>
        <CardContent className={isSubCard ? "p-3 sm:p-3.5" : "p-4"}>
          <div className="flex items-start gap-3">
            {isSubCard && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(); }}
                className="mt-1 text-slate-400 hover:text-blue-600 shrink-0 cursor-pointer"
                title={isSelected ? "Batalkan pilihan" : "Pilih varian ini"}
              >
                {isSelected ? (
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                ) : (
                  <Square className="w-5 h-5 text-slate-300 hover:text-slate-500" />
                )}
              </button>
            )}
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${actCls}`}>
              {isMixingTask ? <FlaskConical className="w-5 h-5" /> : act === 'filling' ? <Droplets className="w-5 h-5" /> : <Package className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-sm leading-tight">{task.task_name}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <Badge className={`text-[10px] px-1.5 py-0 ${actCls}`}>
                      {ACTIVITY_LABELS[task.activity_type] || task.activity_type}
                    </Badge>
                    {task.po_number && (
                      <span className="text-[11px] text-muted-foreground font-medium">
                        {String(task.po_number).toLowerCase().startsWith('po') ? task.po_number : `PO #${task.po_number}`}
                      </span>
                    )}
                    {task.po_brand && (
                      <span className="text-[11px] text-muted-foreground font-medium">{task.po_brand}</span>
                    )}
                    {isPic && (
                      <Badge className="text-[9px] px-1.5 py-0 bg-[#F9F5EC] text-[#8C6208] border border-[#E6D5B8]">PIC Utama</Badge>
                    )}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-medium shrink-0 ${statusCfg.cls}`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusCfg.label}
                </span>
              </div>

              {/* Meta info & History */}
              <div className="flex flex-wrap items-center gap-2.5 mt-2.5 text-xs text-muted-foreground">
                {/* PIC Info */}
                <span className="flex items-center gap-1 bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-md border border-slate-200">
                  <strong>PIC:</strong> {task.pic_name || (task.pic_id ? "Ditugaskan" : "Belum Ada PIC")}
                </span>

                {displayTargetQty > 0 && (
                  <span className="flex items-center gap-1">
                    <span>Target: <strong className="text-foreground">{isBottleStage ? Math.round(displayTargetQty).toLocaleString('id-ID') : formatQty(displayTargetQty)} {displayUnit}</strong></span>
                    {cardDual && (
                      <span className="font-mono font-bold text-purple-900 bg-purple-100/80 px-2 py-0.5 rounded text-[11px] ml-1">
                        {isLiterUnit ? cardDual.weightText : cardDual.text}
                      </span>
                    )}
                  </span>
                )}

                {task.completed_quantity !== undefined && task.completed_quantity !== null && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 font-semibold text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Output: <strong>{isBottleStage ? Math.round(Number(task.completed_quantity)).toLocaleString('id-ID') : formatQty(task.completed_quantity)} {displayUnit}</strong>
                  </span>
                )}

                {/* Discrepancy Status Badges */}
                {task.discrepancy_type === 'reject' && (
                  <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                    <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                    Reject: {task.reject_qty || 0} pcs {task.discrepancy_reason ? `(${task.discrepancy_reason})` : ''}
                  </span>
                )}
                {task.discrepancy_type === 'tidak_reject' && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-md text-[11px] font-medium">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                    Tidak Reject {task.discrepancy_reason ? `(${task.discrepancy_reason})` : ''}
                  </span>
                )}
                {task.discrepancy_type === 'sesuai' && (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-md text-[11px] font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Sesuai Target PO
                  </span>
                )}
                {task.discrepancy_type === 'dicicil' && (
                  <span className="inline-flex items-center gap-1 bg-[#FDFBF7] text-[#8C6208] border border-[#E6D5B8] px-2 py-0.5 rounded-md text-[11px] font-semibold">
                    <span>⏱</span>
                    <span>Dicicil {task.partial_logs?.length ? `(${task.partial_logs.length}x)` : ''}</span>
                  </span>
                )}
                {task.has_pending_qc_cicil && (
                  <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-800 border border-purple-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                    <span>⏱</span>
                    <span>Cicilan ({task.pending_qc_cicil_qty || 0} pcs) Menunggu QC</span>
                  </span>
                )}
                {Number(task.qc_passed_quantity) > 0 && (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-md text-[11px] font-semibold">
                    <span>✓</span>
                    <span>Lolos QC: <strong>{Number(task.qc_passed_quantity).toLocaleString('id-ID')} pcs</strong></span>
                  </span>
                )}

                {/* Net Duration */}
                {isCompleted && task.duration_minutes && (
                  <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 font-medium">
                    <Clock className="w-3.5 h-3.5" />
                    Waktu Kerja: {task.duration_minutes >= 60
                      ? `${Math.floor(task.duration_minutes / 60)}j ${task.duration_minutes % 60}m`
                      : `${task.duration_minutes}m`}
                  </span>
                )}

                {(isTaskInProgress || isInProgress) && startTime && (
                  <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200 font-medium">
                    <Clock className="w-3.5 h-3.5 text-blue-600" />
                    Durasi Berjalan: <LiveTimer startTime={startTime} pauseMinutes={pauseMins} />
                  </span>
                )}

                {/* Pause Duration */}
                {(isPaused || pauseMins > 0) && (
                  <span className="flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200 font-medium">
                    <Pause className="w-3.5 h-3.5" /> Total Jeda: {isPaused ? <LivePauseTimer lastPauseTime={lastPauseTime} storedPauseMins={pauseMins} /> : `${pauseMins}m`}
                  </span>
                )}

                {/* Visual Progress Bar for Partial / Installment Output */}
                {displayTargetQty > 0 && Number(task.completed_quantity) > 0 && (
                  <div className="w-full mt-2 pt-2 border-t border-slate-100">
                    <div className="flex items-center justify-between text-[11px] font-medium text-slate-600 mb-1">
                      <span className="flex items-center gap-1">
                        <span className="text-[#8C6208] font-semibold">Progres Output:</span>
                        <strong className="text-slate-900 font-mono">
                          {Math.round(Number(task.completed_quantity)).toLocaleString('id-ID')} / {Math.round(displayTargetQty).toLocaleString('id-ID')} {displayUnit}
                        </strong>
                      </span>
                      <span className="font-mono font-bold text-slate-700">
                        {Math.min(100, Math.round((Number(task.completed_quantity) / displayTargetQty) * 100))}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200/60">
                      <div 
                        className={`h-full transition-all duration-300 rounded-full ${
                          Number(task.completed_quantity) >= displayTargetQty
                            ? 'bg-emerald-500'
                            : 'bg-gradient-to-r from-amber-500 to-[#8C6208]'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(2, (Number(task.completed_quantity) / displayTargetQty) * 100))}%` }}
                      />
                    </div>
                    {task.partial_logs?.length > 0 && (
                      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
                        <span>⏱ Dicicil {task.partial_logs.length}x</span>
                        <span>Terakhir: +{task.partial_logs[task.partial_logs.length - 1].qty} {displayUnit} ({task.partial_logs[task.partial_logs.length - 1].operator || 'PIC'})</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Info Pasokan Botol dari Filling (Khusus Tahap Pengemasan) */}
                {isPengemasanTask && (() => {
                  const relatedFilling = tasks.find(t => 
                    (t.po_id === task.po_id || t.po_number === task.po_number) &&
                    (t.activity_type === 'filling') &&
                    (!task.product_name || !t.product_name || t.product_name.toLowerCase().trim() === task.product_name.toLowerCase().trim())
                  );
                  if (!relatedFilling) return null;

                  const fillingPassed = Number(relatedFilling.qc_passed_quantity) || 
                    (relatedFilling.partial_logs || []).filter(l => l.qc_status === 'approved').reduce((s, l) => s + (Number(l.qc_passed_qty) || 0), 0) ||
                    (['completed', 'selesai'].includes((relatedFilling.status || '').toLowerCase()) ? Number(relatedFilling.completed_quantity) : 0);

                  const pendingFillingCicil = relatedFilling.has_pending_qc_cicil ? Number(relatedFilling.pending_qc_cicil_qty || 0) : 0;

                  return (
                    <div className="w-full mt-2.5 p-2.5 rounded-xl bg-[#FDFBF7] border border-[#E6D5B8] text-xs flex flex-wrap items-center justify-between gap-2 shadow-2xs">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">📦</span>
                        <div>
                          <span className="font-bold text-[#302621]">Pasokan Botol dari Filling:</span>
                          <div className="flex flex-wrap items-center gap-2 text-[11px] mt-0.5">
                            <span className="text-emerald-800 font-semibold flex items-center gap-1">
                              <span>✓</span>
                              <span><strong>{fillingPassed.toLocaleString('id-ID')} pcs</strong> Siap Dikemas (Lolos QC)</span>
                            </span>
                            {pendingFillingCicil > 0 && (
                              <span className="text-purple-700 font-medium">
                                • ⏱ {pendingFillingCicil} pcs sedang diinspeksi QC
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {fillingPassed > 0 && Number(task.completed_quantity || 0) < fillingPassed && (
                        <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                          Bisa Dikemas Sekarang
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Detailed Pause History */}
              {pauseReasons.length > 0 && (
                <div className="mt-2.5 space-y-1">
                  {pauseReasons.map((pr, i) => (
                    <div key={i} className="text-[11px] text-amber-800 bg-amber-50/80 border border-amber-200/80 rounded-lg px-2.5 py-1 flex items-start gap-1.5">
                      <Pause className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                      <span><strong>Catatan Jeda:</strong> {pr.reason} {pr.time && <span className="opacity-75">({format(new Date(pr.time), "HH:mm")})</span>}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Reject items breakdown */}
              {task.rejected_items?.length > 0 && (
                <div className="mt-2.5 pt-2 border-t border-amber-200/60 text-xs">
                  <span className="font-semibold text-amber-900 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-700" /> Item Reject ({task.rejected_items.reduce((s, r) => s + (r.items || []).reduce((is, it) => is + (Number(it.qty) || 0), 0), 0)} pcs):
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {task.rejected_items.flatMap(r => (r.items || []).filter(it => it.name && Number(it.qty) > 0).map((it, idx) => (
                      <span key={idx} className="bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1">
                        <strong>{it.name}:</strong> {it.qty} {it.unit || 'pcs'} {it.notes ? `(${it.notes})` : ''}
                      </span>
                    )))}
                  </div>
                </div>
              )}

              {/* Notes - Sembunyikan takaran bahan otomatis agar kartu tidak penuh */}
              {task.notes && (() => {
                const cleanNotes = task.notes
                  .split(/\s*\|\s*|\n+/)
                  .filter(segment => !segment.toLowerCase().includes('takaran bahan'))
                  .join(' | ')
                  .replace(/\[Selesai Dikerjakan\]\s*Menunggu Validasi QC/gi, '')
                  .trim();

                if (!cleanNotes) return null;

                return (
                  <div className="mt-2.5 pt-2 border-t border-slate-100 text-xs">
                    <p className="text-slate-600 italic bg-slate-50 p-2 rounded-lg border border-slate-100 whitespace-pre-line leading-relaxed">
                      "{cleanNotes}"
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Notice for PIC when waiting for QC */}
          {task.status === "waiting_qc" && !isQCUser && (
                <div className="mt-2 text-xs text-purple-700 bg-purple-50 border border-purple-100 rounded-lg p-2.5 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600 shrink-0" />
                  <span>Tugas telah diselesaikan operator & sedang menunggu konfirmasi validasi dari Quality Control.</span>
                </div>
              )}

              {/* Notice for Destination PIC when there is a pending transfer request */}
              {task.pending_transfer_pic_id === member.id && (
                <div className="mt-2.5 p-3.5 text-xs bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl space-y-2 text-blue-950 shadow-sm">
                  <div className="flex items-center justify-between font-bold">
                    <span className="flex items-center gap-1.5 text-blue-900">
                      <UserPlus className="w-4 h-4 text-blue-600" />
                      📌 Penyerahan PIC dari {task.transfer_requested_by || 'PIC Sebelumnya'}
                    </span>
                    <Badge className="bg-blue-200 text-blue-900 border-blue-300 text-[10px]">Perlu Konfirmasi Anda</Badge>
                  </div>
                  <p className="text-[11px] text-slate-700 leading-relaxed">
                    Apakah Anda bersedia menerima dan resmi mengambil alih pengerjaan tugas <strong className="text-slate-900">{task.task_name}</strong> ini sebagai PIC Utama baru?
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-100"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleRejectPicTransfer(task); }}
                    >
                      ✕ Tolak Penyerahan
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 h-8 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold gap-1 shadow-sm"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleAcceptPicTransfer(task); }}
                    >
                      ✓ Terima & Ambil Alih PIC
                    </Button>
                  </div>
                </div>
              )}

              {/* Pending transfer badge for sender / other teammates */}
              {task.pending_transfer_pic_id && task.pending_transfer_pic_id !== member.id && (
                <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2.5 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>⏳ Penyerahan PIC ke <strong>{task.pending_transfer_pic_name}</strong> sedang menunggu konfirmasi penerimaan dari yang bersangkutan.</span>
                </div>
              )}

              {/* ===== ACTION BUTTONS ===== */}
              {canEdit && !isCompleted && isSameTeam && activeTab !== "incoming" && (
                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  {/* MULAI (Hanya Staf Produksi/Pengemasan & Kepala se-departemen, TIDAK UNTUK QC atau Tim Sebelah di tab Akan Datang) */}
                  {isAssigned && !isInProgress && !isPaused && task.status !== "waiting_qc" && canStart && !isQCUser && (
                    <Button
                      size="sm"
                      className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 rounded-lg shadow-sm"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleOpenMulai(task); }}
                    >
                      <Play className="w-3.5 h-3.5" />
                      {isProcessing ? "Memulai..." : "Mulai Sekarang"}
                    </Button>
                  )}

                  {/* JEDA (Khusus PIC yang mengerjakan) */}
                  {isInProgress && isPic && task.status !== "waiting_qc" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50 text-xs h-8 rounded-lg"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleOpenJeda(task); }}
                    >
                      <Pause className="w-3.5 h-3.5" />
                      Jeda
                    </Button>
                  )}

                  {/* LANJUTKAN (from pause - Khusus PIC yang mengerjakan) */}
                  {isPaused && isPic && task.status !== "waiting_qc" && (
                    <Button
                      size="sm"
                      className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 rounded-lg shadow-sm"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleLanjutkan(task); }}
                    >
                      <Play className="w-3.5 h-3.5" />
                      {isProcessing ? "Melanjutkan..." : "Lanjutkan"}
                    </Button>
                  )}

                  {/* TAKEOVER PIC (Jika jeda > 6 jam atau Kepala se-departemen) */}
                  {canTakeover && (
                    <Button
                      size="sm"
                      className="gap-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-xs h-8 rounded-lg shadow-sm font-semibold"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); setTakeoverTask(task); }}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Lanjutkan & Ambil Alih PIC {isPauseOver6h && "(Jeda >6j)"}
                    </Button>
                  )}

                  {/* PINDAHKAN PIC (PIC Aktif / Kepala / Supervisor se-departemen) */}
                  {canManualTransfer && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-slate-700 border-slate-300 hover:bg-slate-100 text-xs h-8 rounded-lg"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); setTransferTask(task); setTargetMemberId(""); }}
                    >
                      <UserPlus className="w-3.5 h-3.5 text-blue-600" />
                      Pindahkan PIC
                    </Button>
                  )}

                  {/* QUICK CICIL OUTPUT (Khusus PIC saat tugas berjalan / jeda & bukan waiting_qc) */}
                  {(isInProgress || isPaused) && isPic && task.status !== "waiting_qc" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-[#8C6208] border-[#E6D5B8] bg-[#FDFBF7] hover:bg-[#F7EFE1] text-xs h-8 rounded-lg font-medium shadow-2xs"
                      disabled={isProcessing}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenSelesai(task, 'dicicil');
                      }}
                      title="Catat progres output yang sudah selesai secara bertahap / dicicil"
                    >
                      <span className="text-xs">⏱</span>
                      <span>Cicil Output</span>
                    </Button>
                  )}

                  {/* SELESAI (Khusus PIC -> Ajukan ke QC) */}
                  {(isInProgress || isPaused) && isPic && task.status !== "waiting_qc" && (() => {
                    const isPacking = ['packing', 'pengemasan', 'labeling', 'sealing'].includes(task.activity_type);
                    const relatedFilling = isPacking && tasks.find(t => 
                      (t.po_id === task.po_id || t.po_number === task.po_number) &&
                      (t.activity_type === 'filling') &&
                      (!task.product_name || !t.product_name || t.product_name.toLowerCase().trim() === task.product_name.toLowerCase().trim()) &&
                      !['completed', 'selesai'].includes((t.status || '').toLowerCase())
                    );

                    if (isPacking && relatedFilling) {
                      return (
                        <div 
                          className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-300 font-semibold shadow-2xs cursor-help"
                          title="Tugas pengemasan belum bisa diselesaikan karena proses pengisian botol (Filling) untuk varian ini masih berjalan."
                        >
                          <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span>Menunggu Filling Selesai</span>
                        </div>
                      );
                    }

                    return (
                      <Button
                        size="sm"
                        className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 rounded-lg shadow-sm font-semibold cursor-pointer"
                        disabled={isProcessing}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleOpenSelesai(task);
                        }}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {isProcessing ? "Mengajukan..." : "Selesai & Ajukan ke QC"}
                      </Button>
                    );
                  })()}

                  {/* KONFIRMASI QC (QC User / Hak Akses Validasi QC -> Hanya ketika tugas berstatus waiting_qc) */}
                  {(isQCUser || canValidateQC) && task.status === "waiting_qc" && (
                    <Button
                      size="sm"
                      className="gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs h-8 rounded-lg shadow-sm font-semibold"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleOpenSelesai(task); }}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Konfirmasi & Validasi QC
                    </Button>
                  )}

                  {/* KONFIRMASI QC CICILAN (Khusus QC User / Hak Akses Validasi QC -> saat ada batch cicilan yang menunggu QC) */}
                  {(isQCUser || canValidateQC) && task.has_pending_qc_cicil && task.status !== "waiting_qc" && (
                    <Button
                      size="sm"
                      className="gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs h-8 rounded-lg shadow-sm font-semibold"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleOpenCicilQc(task); }}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Validasi QC Cicilan ({task.pending_qc_cicil_qty || 0} pcs)
                    </Button>
                  )}

                  {/* LIHAT DETAIL & PROGRES PO */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100 text-xs h-8 rounded-lg font-medium"
                    onClick={(e) => { e.stopPropagation(); setDetailTask(task); }}
                  >
                    <Eye className="w-3.5 h-3.5 text-slate-600" />
                    Lihat Detail & Progres PO
                  </Button>

                  {/* HAPUS TUGAS (Synchronized with PO Tasks) */}
                  {(isKepala || isDevOrOwner) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 text-xs h-8 rounded-lg font-semibold ml-auto"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleDeleteTask(task); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Hapus
                    </Button>
                  )}
                </div>
              )}

              {/* READ-ONLY VIEW BUTTON & CURI START FOR INCOMING / CROSS-TEAM */}
              {(!isSameTeam || activeTab === "incoming") && (
                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  {/* Tombol Mulai Pengemasan Lebih Awal Khusus Tim Sekunder */}
                  {isPengemasanTeam && activeTab === "incoming" && (
                    <Button
                      size="sm"
                      className="gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-xs h-8 rounded-lg shadow-sm"
                      disabled={isProcessing}
                      onClick={(e) => { e.stopPropagation(); handleEarlyStartPacking(task); }}
                    >
                      <Package className="w-3.5 h-3.5" />
                      Mulai Pengemasan Lebih Awal (Curi Start)
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100 text-xs h-8 rounded-lg font-medium"
                    onClick={(e) => { e.stopPropagation(); setDetailTask(task); }}
                  >
                    <Eye className="w-3.5 h-3.5 text-slate-600" />
                    Lihat Detail & Progres PO
                  </Button>
                </div>
              )}
        </CardContent>
      </Card>
    );
  };

  // ===== RENDER HEAD CARD & VARIANT SUB-TASKS (GROUP) =====
  const renderTaskGroup = (rawGroup) => {
    const matchedPO = productionOrders.find(p => p.id === rawGroup.po_id || p.order_number === rawGroup.po_number || p.id === rawGroup.tasks[0]?.production_order_id);

    // Otomatis pecah menjadi tugas-tugas varian mandiri (Image 4), BUKAN tampilan Image 3 dengan tombol manual!
    let effectiveTasks = [...rawGroup.tasks];
    if (effectiveTasks.length === 1) {
      const vItems = getVariantItems(effectiveTasks[0], matchedPO);
      if (vItems && vItems.length > 1) {
        const t = effectiveTasks[0];
        const brandPrefix = (t.po_brand || matchedPO?.brand_name || "PO").split('/')[0].trim();
        const activityLabel = ACTIVITY_LABELS[t.activity_type] || t.activity_type;
        effectiveTasks = vItems.map((v, vIdx) => ({
          ...t,
          id: `${t.id}__var__${v.variant_name || vIdx}`,
          _rawTaskId: t.id,
          is_virtual_subtask: true,
          task_name: `${brandPrefix} - ${v.variant_name} - ${activityLabel}`,
          product_name: v.variant_name,
          botol_ukuran_aktual: v.size_ml,
          target_quantity: v.target_quantity,
          unit: v.unit || (t.activity_type === 'racik' ? 'liter' : 'pcs'),
          status: t.status || "assigned",
          start_time: t.start_time || null,
          duration_minutes: t.duration_minutes || null,
          completed_quantity: t.status === 'completed' ? v.target_quantity : null,
        }));
      }
    }

    const group = { ...rawGroup, tasks: effectiveTasks };

    // If completely single standalone task (no multiple tasks, no multiple variants):
    if (group.tasks.length === 1) {
      return renderTaskCard(group.tasks[0], false);
    }

    const isExpanded = expandedGroupIds.has(group.id);
    const actCls = ACTIVITY_COLORS[group.activity_type] || "bg-gray-100 text-gray-700";
    const actLabel = ACTIVITY_LABELS[group.activity_type] || group.activity_type;

    // MULTI-TASK GROUP (Separate DB tasks for same PO & stage)
    const totalTasks = group.tasks.length;
    const assignedTasksInGroup = group.tasks.filter(t => (t.status || 'assigned').toLowerCase() === 'assigned' || (t.status || '').toLowerCase() === 'belum_dimulai');
    const inProgressTasksInGroup = group.tasks.filter(t => (t.status || '').toLowerCase() === 'in_progress' || (t.status || '').toLowerCase() === 'berjalan');
    const onHoldTasksInGroup = group.tasks.filter(t => (t.status || '').toLowerCase() === 'on_hold' || (t.status || '').toLowerCase() === 'dijeda');
    const waitingQcTasksInGroup = group.tasks.filter(t => (t.status || '').toLowerCase() === 'waiting_qc' || (t.status || '').toLowerCase() === 'menunggu_qc');
    const completedTasksInGroup = group.tasks.filter(t => (t.status || '').toLowerCase() === 'completed' || (t.status || '').toLowerCase() === 'selesai');

    const isProduksiGroup = ['racik', 'mixing', 'maserasi', 'filling'].includes(group.activity_type);
    const isPengemasanGroup = ['packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes(group.activity_type);
    const isSameTeamGroup = isDevOrOwner || (isProduksiGroup && isProduksiTeam) || (isPengemasanGroup && isPengemasanTeam);

    const selectedInGroup = group.tasks.filter(t => selectedSubTaskIds.has(t.id));
    const allSelectedInGroup = selectedInGroup.length === totalTasks;
    const groupPO = productionOrders.find(p => p.id === group.po_id || p.order_number === group.po_number || p.id === group.tasks[0]?.production_order_id);

    const isRacikGroup = group.activity_type === 'racik' || group.activity_type === 'mixing';
    const isBottleStageGroup = ['filling', 'packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes(group.activity_type);
    const totalTargetQty = group.tasks.reduce((sum, t) => {
      const qty = Number(t.target_quantity) || 0;
      if (isBottleStageGroup && ((t.unit || '').toLowerCase().includes('l') || (t.unit || '').toLowerCase().includes('liter'))) {
        const pSize = Number(t.botol_ukuran_aktual || t.ukuran_ml || 30);
        return sum + Math.round(qty * 1000 / pSize);
      }
      return sum + (isBottleStageGroup ? Math.round(qty) : qty);
    }, 0);
    const totalCompletedQty = group.tasks.reduce((sum, t) => {
      const qty = Number(t.completed_quantity) || 0;
      if (isBottleStageGroup && ((t.unit || '').toLowerCase().includes('l') || (t.unit || '').toLowerCase().includes('liter'))) {
        const pSize = Number(t.botol_ukuran_aktual || t.ukuran_ml || 30);
        return sum + Math.round(qty * 1000 / pSize);
      }
      return sum + (isBottleStageGroup ? Math.round(qty) : qty);
    }, 0);

    let totalVolMl = 0;
    if (isRacikGroup) {
      const pSize = Number(group.tasks[0]?.botol_ukuran_aktual || group.tasks[0]?.ukuran_ml || 30);
      totalVolMl = totalTargetQty * 1000;
    } else {
      group.tasks.forEach(t => {
        const pSize = Number(t.botol_ukuran_aktual || t.ukuran_ml || 30);
        let pQty = Number(t.target_quantity) || 1;
        if (isBottleStageGroup && ((t.unit || '').toLowerCase().includes('l') || (t.unit || '').toLowerCase().includes('liter'))) {
          pQty = Math.round(pQty * 1000 / pSize);
        }
        totalVolMl += (pSize * pQty);
      });
    }
    const groupPo = productionOrders.find(p => p.id === group.po_id || p.order_number === group.po_number);
    const groupRacikan = groupPo?.racikan || groupPo?.racikan_digunakan || group.tasks?.[0]?.racikan || [];
    const groupDual = totalVolMl > 0 ? calculateRacikanDual(totalVolMl, groupRacikan) : null;

    const groupBorderColor = inProgressTasksInGroup.length > 0 
      ? '#3b82f6' 
      : onHoldTasksInGroup.length > 0 
        ? '#f59e0b' 
        : waitingQcTasksInGroup.length > 0 
          ? '#a855f7' 
          : completedTasksInGroup.length === totalTasks 
            ? '#22c55e' 
            : '#94a3b8';

    return (
      <div key={group.id} className="space-y-2">
        {/* ===== HEAD CARD (KARTU INDUK GRUP PO & TAHAPAN) ===== */}
        <Card className="transition-all hover:shadow-md border-l-4 overflow-hidden shadow-xs" style={{ borderLeftColor: groupBorderColor }}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
              {/* Left Identity */}
              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-xs ${actCls}`}>
                  {group.activity_type === 'racik' || group.activity_type === 'mixing' ? (
                    <FlaskConical className="w-5 h-5 text-purple-700" />
                  ) : group.activity_type === 'filling' ? (
                    <Droplets className="w-5 h-5 text-cyan-700" />
                  ) : (
                    <Package className="w-5 h-5" />
                  )}
                </div>

                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className={`text-[10px] px-2 py-0.5 font-bold ${actCls}`}>
                      {actLabel}
                    </Badge>
                    <span className="font-bold text-sm text-slate-900 truncate">
                      {group.po_brand ? `${group.po_brand} — ` : ''}{group.po_number ? (String(group.po_number).toLowerCase().startsWith('po') ? group.po_number : `PO #${group.po_number}`) : 'PO Produksi'}
                    </span>
                    <Badge variant="outline" className="text-[10px] font-semibold bg-slate-50 text-slate-700 border-slate-300">
                      <Layers className="w-3 h-3 mr-1 text-slate-500" />
                      {totalTasks} Varian / SKU
                    </Badge>
                    {groupPO?.status === 'maserasi' && groupPO?.maserasi_start && (
                      <MaserasiTimer order={groupPO} small={true} />
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground pt-0.5">
                    <span>
                      Target Total:{" "}
                      <strong className="text-slate-800 font-bold">
                        {isRacikGroup ? `${formatQty(totalTargetQty)} Liter` : `${Math.round(totalTargetQty).toLocaleString('id-ID')} pcs`}
                      </strong>
                    </span>
                    {groupDual && (
                      <span className="font-mono font-bold text-purple-900 bg-purple-100/80 px-2 py-0.5 rounded text-[11px]">
                        {groupDual.text}
                      </span>
                    )}
                    {totalCompletedQty > 0 && (
                      <span className="text-emerald-700 font-medium bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                        {isBottleStageGroup ? Math.round(totalCompletedQty).toLocaleString('id-ID') : formatQty(totalCompletedQty)} Selesai
                      </span>
                    )}
                  </div>

                  {/* Status Badges summary */}
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    {assignedTasksInGroup.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        {assignedTasksInGroup.length} Belum Dimulai
                      </span>
                    )}
                    {inProgressTasksInGroup.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        {inProgressTasksInGroup.length} Sedang Berjalan
                      </span>
                    )}
                    {onHoldTasksInGroup.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                        <Pause className="w-2.5 h-2.5" />
                        {onHoldTasksInGroup.length} Dijeda
                      </span>
                    )}
                    {waitingQcTasksInGroup.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-300">
                        <Clock className="w-2.5 h-2.5" />
                        {waitingQcTasksInGroup.length} Menunggu QC
                      </span>
                    )}
                    {completedTasksInGroup.length === totalTasks && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300">
                        <CheckCircle2 className="w-2.5 h-2.5" />
                        Semua Selesai
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Dropdown Chevron Button (v / ^) */}
              <div className="flex items-center gap-2 self-start sm:self-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`h-9 px-3.5 rounded-xl font-bold text-xs flex items-center gap-2 transition-all shadow-xs ${
                    isExpanded 
                      ? "bg-slate-900 text-white hover:bg-slate-800 border-slate-900" 
                      : "bg-white hover:bg-slate-100 text-slate-800 border-slate-300"
                  }`}
                  onClick={() => toggleGroupExpand(group.id)}
                >
                  <span>{isExpanded ? "Tutup Varian" : `Buka ${totalTasks} Varian / SKU`}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
                </Button>
              </div>
            </div>

            {/* ===== QUICK ACTIONS ON HEAD CARD ===== */}
            {canEdit && isSameTeamGroup && activeTab !== "incoming" && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                {/* MULAI SEMUA SEKALIGUS */}
                {assignedTasksInGroup.length > 0 && !isQCUser && (
                  <Button
                    size="sm"
                    className="gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs h-8 rounded-lg shadow-sm font-semibold"
                    disabled={loading === "batch"}
                    onClick={() => handleBatchStartTasks(assignedTasksInGroup)}
                  >
                    <Play className="w-3.5 h-3.5" />
                    {loading === "batch" ? "Memulai..." : `Mulai Semua (${assignedTasksInGroup.length} Varian) Sekaligus`}
                  </Button>
                )}

                {/* LANJUTKAN SEMUA */}
                {onHoldTasksInGroup.length > 0 && !isQCUser && (
                  <Button
                    size="sm"
                    className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 rounded-lg shadow-sm font-semibold"
                    disabled={loading === "batch"}
                    onClick={() => handleBatchResumeTasks(onHoldTasksInGroup)}
                  >
                    <Play className="w-3.5 h-3.5" />
                    {loading === "batch" ? "Melanjutkan..." : `Lanjutkan Semua (${onHoldTasksInGroup.length} Varian)`}
                  </Button>
                )}

                {/* LIHAT DETAIL & PROGRES PO */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100 text-xs h-8 rounded-lg font-medium"
                  onClick={() => setDetailTask(group.tasks[0])}
                >
                  <Eye className="w-3.5 h-3.5 text-slate-600" />
                  Lihat Detail & Progres PO
                </Button>

                {/* Status expand reminder */}
                {!isExpanded && (
                  <span className="text-[11px] text-slate-400 ml-auto hidden sm:inline-block">
                    💡 Ingin memilih varian tertentu? Klik tombol <strong>Buka {totalTasks} Varian</strong> di atas.
                  </span>
                )}
              </div>
            )}

            {/* Read-only Lihat Detail & Curi Start button for incoming tab or cross team */}
            {(!isSameTeamGroup || activeTab === "incoming") && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                {/* Tombol Mulai Pengemasan Lebih Awal Khusus Tim Sekunder */}
                {isPengemasanTeam && activeTab === "incoming" && (
                  <Button
                    size="sm"
                    className="gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-xs h-8 rounded-lg shadow-sm"
                    disabled={loading === "early_start" || loading === "batch"}
                    onClick={() => handleEarlyStartPackingGroup(group)}
                  >
                    <Package className="w-3.5 h-3.5" />
                    Mulai Pengemasan Lebih Awal (Curi Start)
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100 text-xs h-8 rounded-lg font-medium"
                  onClick={() => setDetailTask(group.tasks[0])}
                >
                  <Eye className="w-3.5 h-3.5 text-slate-600" />
                  Lihat Detail & Progres PO
                </Button>
              </div>
            )}
          </CardContent>

          {/* ===== ACCORDION DRAWER: SUB-TUGAS SETIAP VARIAN / SKU ===== */}
          {isExpanded && (() => {
            const qGlobal = (deferredSearchQuery || '').toLowerCase().trim();
            const qLocal = (localSearch[group.id] || '').toLowerCase().trim();
            const displayedGroupTasks = group.tasks.filter(t => {
              const tName = (t.task_name || '').toLowerCase();
              const pName = (t.product_name || '').toLowerCase();
              const vars = (t.variants || []).map(v => String(v).toLowerCase()).join(' ');

              if (qLocal && !tName.includes(qLocal) && !pName.includes(qLocal) && !vars.includes(qLocal)) return false;
              if (qGlobal) {
                const poMatch = (t.po_number || '').toLowerCase().includes(qGlobal) || (t.po_brand || '').toLowerCase().includes(qGlobal);
                if (!poMatch && !tName.includes(qGlobal) && !pName.includes(qGlobal) && !vars.includes(qGlobal)) return false;
              }
              return true;
            });

            return (
              <div className="bg-slate-50/80 border-t border-slate-200/90 p-3 sm:p-4 space-y-3 animate-in fade-in-50 duration-200">
                {/* Batch Action Toolbar inside Dropdown (Sticky Top) */}
                <div className="sticky top-2 z-20 flex items-center justify-between gap-2 flex-wrap bg-white/95 backdrop-blur-md p-2.5 rounded-xl border border-slate-200/90 shadow-md">
                  <div className="flex items-center gap-3 flex-wrap flex-1">
                    <button
                      type="button"
                      onClick={() => handleSelectAllInGroup(group)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 cursor-pointer shrink-0"
                    >
                      {allSelectedInGroup ? (
                        <CheckSquare className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                      <span>{allSelectedInGroup ? "Batalkan Pilih Semua" : "Pilih Semua Varian"}</span>
                    </button>
                    {selectedInGroup.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-800 text-[10px] font-bold">
                        {selectedInGroup.length} Terpilih
                      </Badge>
                    )}

                    {/* Mini Search inside PO Drawer */}
                    {group.tasks.length > 3 && (
                      <div className="relative flex-1 max-w-xs min-w-[160px]">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder={`Cari varian di ${group.po_brand || 'PO'}...`}
                          value={localSearch[group.id] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setLocalSearch(prev => ({ ...prev, [group.id]: val }));
                          }}
                          className="pl-7 pr-6 h-7 bg-slate-50 border-slate-200 rounded-lg text-xs font-medium"
                        />
                        {localSearch[group.id] && (
                          <button
                            type="button"
                            onClick={() => setLocalSearch(prev => ({ ...prev, [group.id]: "" }))}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Batch buttons for selected variants */}
                  {selectedInGroup.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {selectedInGroup.some(t => t.status === 'assigned') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 rounded-lg shadow-2xs"
                          disabled={loading === 'batch'}
                          onClick={() => handleBatchStartTasks(selectedInGroup.filter(t => t.status === 'assigned'))}
                        >
                          <Play className="w-3 h-3 text-white" />
                          Mulai ({selectedInGroup.filter(t => t.status === 'assigned').length})
                        </Button>
                      )}
                      {selectedInGroup.some(t => t.status === 'on_hold') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold gap-1.5 rounded-lg shadow-2xs"
                          disabled={loading === 'batch'}
                          onClick={() => handleBatchResumeTasks(selectedInGroup.filter(t => t.status === 'on_hold'))}
                        >
                          <Play className="w-3 h-3 text-white" />
                          Lanjutkan ({selectedInGroup.filter(t => t.status === 'on_hold').length})
                        </Button>
                      )}
                      {selectedInGroup.some(t => t.status === 'in_progress') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-1.5 rounded-lg shadow-2xs"
                          disabled={loading === 'batch'}
                          onClick={() => handleBatchFinishToQC(selectedInGroup.filter(t => t.status === 'in_progress'))}
                        >
                          <CheckCircle2 className="w-3 h-3 text-white" />
                          Selesai Pengerjaan ({selectedInGroup.filter(t => t.status === 'in_progress').length})
                        </Button>
                      )}
                      {canValidateQC && selectedInGroup.some(t => t.status === 'waiting_qc' || t.status === 'menunggu_qc') && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-purple-700 hover:bg-purple-800 text-white font-semibold gap-1.5 rounded-lg shadow-2xs"
                          disabled={loading === 'batch_qc'}
                          onClick={() => setBatchQcModal(selectedInGroup.filter(t => t.status === 'waiting_qc' || t.status === 'menunggu_qc'))}
                        >
                          <ShieldCheck className="w-3.5 h-3.5 text-white" />
                          Validasi QC ({selectedInGroup.filter(t => t.status === 'waiting_qc' || t.status === 'menunggu_qc').length})
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-1.5 rounded-lg"
                        onClick={() => {
                          setBatchPicModal(selectedInGroup);
                          setBatchTargetPicId(selectedInGroup[0]?.pic_id || "");
                        }}
                      >
                        <UserPlus className="w-3 h-3 text-slate-600" />
                        Ganti PIC ({selectedInGroup.length})
                      </Button>
                    </div>
                  )}
                </div>

                {/* Sub-cards for each variant / SKU with progressive rendering */}
                {(() => {
                  const isSearchActive = !!qLocal || !!qGlobal;
                  const currentVisible = isSearchActive ? displayedGroupTasks.length : (visibleCountMap[group.id] || 25);
                  const paginatedTasks = displayedGroupTasks.slice(0, currentVisible);

                  return (
                    <div className="space-y-2.5">
                      {displayedGroupTasks.length === 0 ? (
                        <div className="text-center py-6 text-slate-400 text-xs bg-white rounded-xl border border-dashed border-slate-200">
                          Tidak ada varian yang cocok dengan pencarian "{qLocal || qGlobal}".
                        </div>
                      ) : (
                        paginatedTasks.map(t => {
                          const isSelected = selectedSubTaskIds.has(t.id);
                          return renderTaskCard(t, true, isSelected, () => toggleSelectSubTask(t.id));
                        })
                      )}

                      {/* Progressive Load More button */}
                      {!isSearchActive && displayedGroupTasks.length > currentVisible && (
                        <div className="flex items-center justify-center gap-3 pt-3 pb-1 flex-wrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border-slate-300 shadow-2xs gap-1.5 rounded-xl h-8 cursor-pointer"
                            onClick={() => setVisibleCountMap(prev => ({
                              ...prev,
                              [group.id]: Math.min(displayedGroupTasks.length, (prev[group.id] || 25) + 25)
                            }))}
                          >
                            <ChevronDown className="w-3.5 h-3.5 text-blue-600" />
                            Tampilkan 25 Varian Lagi (Menampilkan {currentVisible} dari {displayedGroupTasks.length})
                          </Button>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                            onClick={() => setVisibleCountMap(prev => ({
                              ...prev,
                              [group.id]: displayedGroupTasks.length
                            }))}
                          >
                            Tampilkan Semua ({displayedGroupTasks.length} Varian)
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {member.photo_url ? (
            <img src={member.photo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-border" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-bold text-primary">
                {member.full_name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold leading-tight">Halo, {member.full_name?.split(" ")[0]}!</h1>
            <p className="text-xs text-muted-foreground capitalize">{member.position || member.role} · {member.department || "Tim Produksi"}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5 text-muted-foreground">
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:block">Keluar</span>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Tugas Terlibat", value: myTasks.length, icon: ClipboardList, cls: "text-foreground" },
          { label: "Sedang Aktif", value: activeTasks.length, icon: PlayCircle, cls: "text-blue-600" },
          { label: "Antrean Masuk", value: incomingTasks.length, icon: Hourglass, cls: "text-purple-600" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <Icon className={`w-5 h-5 shrink-0 ${s.cls}`} />
                <div>
                  <p className="text-xl font-bold leading-none">{s.value}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-xl w-full">
        <button
          onClick={() => { setActiveTab("incoming"); setStatusFilter("all"); }}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "incoming" ? "bg-card shadow-sm text-purple-700 font-bold" : "text-muted-foreground hover:text-foreground"}`}
        >
          Akan Datang ({incomingTasks.length})
        </button>
        <button
          onClick={() => { setActiveTab("active"); setStatusFilter("all"); }}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "active" ? "bg-card shadow-sm text-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}
        >
          Tugas Aktif ({activeTasks.length})
        </button>
        <button
          onClick={() => setActiveTab("done")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "done" ? "bg-card shadow-sm text-green-700 font-bold" : "text-muted-foreground hover:text-foreground"}`}
        >
          Selesai ({doneTasks.length})
        </button>
      </div>

      {/* Banner Penjelasan Tab Akan Datang */}
      {activeTab === "incoming" && (
        <div className="p-3 bg-purple-50/70 border border-purple-200 rounded-xl text-xs text-purple-900 flex items-start gap-2.5">
          <Sparkles className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Tab Antrean Masuk (Incoming Tasks):</p>
            <p className="text-purple-700/90 leading-relaxed mt-0.5">
              {isPengemasanTeam ? (
                <>Menampilkan PO yang sedang di-<strong>Mixing</strong>, <strong>Maserasi</strong>, atau <strong>Filling</strong> oleh Tim Primer. Anda bisa mempersiapkan bahan kemas lebih awal dari gudang atau klik <strong>"Mulai Pengemasan"</strong> untuk mencuri start!</>
              ) : (
                <>Menampilkan antrean tugas tahap persiapan atau racikan berikutnya.</>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Activity / Tahap Filter Berdasarkan Role (Primer: Mixing, Filling, QC | Sekunder: Pengemasan, QC | Dev/Kepala: Semua) */}
      {activeTab === "active" && (
        <div className="flex items-center gap-1.5 p-1 bg-slate-100/90 rounded-xl w-fit border border-slate-200/80 text-xs flex-wrap">
          <span className="text-[11px] font-bold text-slate-500 px-2 flex items-center gap-1">
            <Layers className="w-3.5 h-3.5 text-slate-500" />
            Tahap:
          </span>
          <button
            type="button"
            onClick={() => setActivityFilter("all")}
            className={`px-3 py-1 rounded-lg font-bold transition-all ${
              activityFilter === "all"
                ? "bg-white text-slate-900 shadow-xs border border-slate-200"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Semua ({tasksForStatus.length})
          </button>

          {/* Mixing (Khusus Tim Primer & Dev/Kepala) */}
          {(isPrimerTeam || isDevOrOwner || isKepala) && (
            <button
              type="button"
              onClick={() => setActivityFilter("mixing")}
              className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                activityFilter === "mixing"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-purple-700 hover:bg-purple-50"
              }`}
            >
              <FlaskConical className="w-3.5 h-3.5" />
              Mixing / Racik ({mixingTasksCount})
            </button>
          )}

          {/* Filling (Khusus Tim Primer & Dev/Kepala) */}
          {(isPrimerTeam || isDevOrOwner || isKepala) && (
            <button
              type="button"
              onClick={() => setActivityFilter("filling")}
              className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                activityFilter === "filling"
                  ? "bg-cyan-600 text-white shadow-xs"
                  : "text-cyan-700 hover:bg-cyan-50"
              }`}
            >
              <Droplets className="w-3.5 h-3.5" />
              Filling ({fillingTasksCount})
            </button>
          )}

          {/* Pengemasan (Khusus Tim Sekunder & Dev/Kepala) */}
          {(isSekunderTeam || isDevOrOwner || isKepala) && (
            <button
              type="button"
              onClick={() => setActivityFilter("packing")}
              className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                activityFilter === "packing"
                  ? "bg-amber-600 text-white shadow-xs"
                  : "text-amber-700 hover:bg-amber-50"
              }`}
            >
              <Package className="w-3.5 h-3.5" />
              Pengemasan ({packingTasksCount})
            </button>
          )}

          {/* Validasi QC (Ada di Tim Primer, Sekunder, maupun Dev/Kepala) */}
          <button
            type="button"
            onClick={() => setActivityFilter("waiting_qc")}
            className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
              activityFilter === "waiting_qc"
                ? "bg-purple-700 text-white shadow-xs"
                : "text-purple-800 hover:bg-purple-50"
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Validasi QC ({stageQcTasksCount})
          </button>
        </div>
      )}

      {/* Sub-filters for Active Tasks */}
      {activeTab === "active" && (
        <div className="flex items-center gap-1.5 overflow-x-auto py-1 text-xs">
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all shrink-0 ${
              statusFilter === "all"
                ? "bg-slate-900 text-white font-semibold shadow-xs"
                : "bg-muted/60 text-muted-foreground hover:bg-muted"
            }`}
          >
            Semua ({tasksForStage.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("assigned")}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1.5 ${
              statusFilter === "assigned"
                ? "bg-blue-600 text-white font-semibold shadow-xs"
                : "bg-blue-50 text-blue-700 hover:bg-blue-100/70"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
            Belum Dimulai ({assignedTasksCount})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("in_progress")}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1.5 ${
              statusFilter === "in_progress"
                ? "bg-emerald-600 text-white font-semibold shadow-xs"
                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100/70"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            Sedang Dikerjakan ({inProgressTasksCount})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("on_hold")}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1.5 ${
              statusFilter === "on_hold"
                ? "bg-amber-600 text-white font-semibold shadow-xs"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100/70"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
            Dijeda ({onHoldTasksCount})
          </button>
        </div>
      )}

      {/* Search Input Bar */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          type="text"
          placeholder="Cari tugas, nama varian / SKU, brand, nomor PO, activity, atau PIC..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-8 h-10 bg-white border-slate-200 rounded-xl text-xs font-medium focus-visible:ring-amber-400 shadow-2xs"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Task List (Grouped by PO & Stage with Head Cards) */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Memuat tugas...</div>
      ) : (
        <div className="space-y-3">
          {(activeTab === "active" ? filteredActiveTasks : activeTab === "incoming" ? incomingTasks : doneTasks).length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">
                {activeTab === "active"
                  ? statusFilter === "assigned"
                    ? "Tidak ada tugas yang belum dimulai."
                    : statusFilter === "in_progress"
                    ? "Tidak ada tugas sedang dikerjakan."
                    : statusFilter === "on_hold"
                    ? "Tidak ada tugas yang dijeda."
                    : "Tidak ada tugas aktif."
                  : activeTab === "incoming"
                  ? "Tidak ada antrean tugas masuk saat ini."
                  : "Belum ada tugas selesai."}
              </p>
            </div>
          ) : (
            groupTasks(activeTab === "active" ? filteredActiveTasks : activeTab === "incoming" ? incomingTasks : doneTasks).map(renderTaskGroup)
          )}
        </div>
      )}

      {/* ===== JEDA DIALOG (ASA Popup) ===== */}
      <Dialog open={!!jedaTask} onOpenChange={(o) => !o && closeJedaDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center overflow-hidden border border-amber-200 shadow-sm">
                <img src="/asa-profile.png" alt="Asa Profile" className="w-full h-full object-cover" />
              </div>
              ASA — Jeda Tugas
            </DialogTitle>
          </DialogHeader>

          {jedaTask && (
            <div className="space-y-4">
              {/* Task info */}
              <div className="bg-muted/50 rounded-xl p-3 space-y-1">
                <p className="font-semibold text-sm">{jedaTask.task_name}</p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {ACTIVITY_LABELS[jedaTask.activity_type] || jedaTask.activity_type}
                  </Badge>
                  {jedaTask.po_number && <span className="text-xs text-muted-foreground">PO: {jedaTask.po_number}</span>}
                </div>
              </div>

              {jedaStep === "ask" ? (
                <>
                  {/* ASA message */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5 overflow-hidden border border-amber-200 shadow-sm">
                      <img src="/asa-profile.png" alt="Asa Profile" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl rounded-tl-sm px-4 py-3">
                      <p className="text-sm text-amber-900 font-medium">{asaMessage}</p>
                    </div>
                  </div>

                  {/* Input reason */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Alasan Jeda</Label>
                    <Textarea
                      placeholder="Contoh: Istirahat sholat, bahan habis, mesin rusak..."
                      value={jedaReason}
                      onChange={(e) => setJedaReason(e.target.value)}
                      rows={3}
                      className="text-sm"
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={closeJedaDialog}>Batal</Button>
                    <Button
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                      onClick={handleConfirmJeda}
                      disabled={!jedaReason.trim()}
                    >
                      <Pause className="w-4 h-4" /> Jeda Sekarang
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* ASA Reaction */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5 overflow-hidden border border-amber-200 shadow-sm">
                      <img src="/asa-profile.png" alt="Asa Profile" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl rounded-tl-sm px-4 py-3">
                      <p className="text-sm text-green-800 font-medium">{asaReaction}</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-3 flex items-start gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-slate-700">Tugas berhasil dijeda!</p>
                      <p className="mt-0.5">Alasan: <em>"{jedaReason}"</em> sudah tersimpan sebagai catatan.</p>
                    </div>
                  </div>

                  <Button className="w-full" onClick={closeJedaDialog}>Oke, Mengerti 👍</Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== SELESAI DIALOG ===== */}
      <Dialog open={!!selesaiTask} onOpenChange={(o) => !o && setSelesaiTask(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selesaiTask?.status === "waiting_qc" ? (
                <>
                  <ShieldCheck className="w-5 h-5 text-indigo-600" />
                  Konfirmasi & Validasi QC
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Selesaikan Tugas
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selesaiTask && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-xl p-3 space-y-1">
                <p className="font-semibold text-sm">{selesaiTask.task_name}</p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {ACTIVITY_LABELS[selesaiTask.activity_type] || selesaiTask.activity_type}
                  </Badge>
                  {selesaiTask.po_number && (
                    <span className="text-xs text-muted-foreground font-medium">
                      {String(selesaiTask.po_number).toLowerCase().startsWith('po') ? selesaiTask.po_number : `PO #${selesaiTask.po_number}`}
                    </span>
                  )}
                </div>
              </div>

              {rejectStep === 'ask_reject' && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl rounded-tl-sm px-4 py-3">
                      <p className="text-sm text-amber-900 font-medium">Halo {sapaan}, sebelum diselesaikan, apakah ada barang yang reject/cacat selama proses {ACTIVITY_LABELS[selesaiTask.activity_type]}?</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1 text-slate-500" onClick={() => { setRejects([]); setRejectStep('form'); }}>Tidak Ada</Button>
                    <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white gap-1.5" onClick={() => {
                      const defaultVar = selesaiPO?.products?.length > 0 ? selesaiPO.products[0].product_name : selesaiPO?.product_name || 'Hasil Mixing';
                      handleAddVariantReject(defaultVar);
                      setRejectStep('fill_reject');
                    }}>Ada Reject</Button>
                  </div>
                </div>
              )}

              {rejectStep === 'fill_reject' && (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                  <h4 className="font-bold text-sm">Catat Item Reject</h4>
                  
                  {/* Dedicated Reject Form for Racik / Mixing Tasks */}
                  {selesaiTask && (selesaiTask.activity_type === "racik" || selesaiTask.activity_type === "mixing") ? (
                    <div className="space-y-3.5 border border-purple-200 bg-gradient-to-br from-purple-50/70 to-amber-50/50 rounded-xl p-3.5 shadow-sm">
                      <div className="flex items-center gap-2 text-purple-900 font-bold text-xs border-b border-purple-200/80 pb-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Pencatatan Liquid / Formula Gagal (Mixing)
                      </div>

                      {rejects.map((r, idx) => (
                        <div key={r.id} className="space-y-2.5 bg-white p-3 rounded-xl border border-purple-100 shadow-sm relative">
                          {rejects.length > 1 && (
                            <button className="absolute top-2 right-2 text-slate-300 hover:text-red-500" onClick={() => setRejects(rejects.filter(rj => rj.id !== r.id))}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-7">
                              <Label className="text-[10px] font-semibold text-slate-600 mb-1 block">Alasan / Penyebab Reject *</Label>
                              <select 
                                className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white font-medium"
                                value={r.items[0]?.name || ''} 
                                onChange={e => updateRejectItem(r.id, r.items[0]?.id, 'name', e.target.value)}
                              >
                                <option value="">Pilih Alasan Reject...</option>
                                <option value="Hasil Liquid Off-Spec (Aroma/Warna)">Hasil Liquid Off-Spec (Aroma/Warna)</option>
                                <option value="Bahan Baku Tumpah / Bocor">Bahan Baku Tumpah / Bocor</option>
                                <option value="Endapan / Reaksi Gagal">Endapan / Reaksi Gagal</option>
                                <option value="Keran Tangki Stainless Bocor">Keran Tangki Stainless Bocor</option>
                                <option value="Lainnya">Lainnya</option>
                              </select>
                            </div>
                            <div className="col-span-5">
                              <Label className="text-[10px] font-semibold text-slate-600 mb-1 block">Jumlah Volume *</Label>
                              <div className="flex items-center gap-1">
                                <Input 
                                  type="number" 
                                  placeholder="0" 
                                  className="h-8 text-xs" 
                                  value={r.items[0]?.qty || ''} 
                                  onChange={e => updateRejectItem(r.id, r.items[0]?.id, 'qty', e.target.value)} 
                                />
                                <select 
                                  className="h-8 text-[11px] border border-purple-200 rounded-md px-1 bg-purple-50 font-bold text-purple-800 shrink-0"
                                  value={r.items[0]?.unit || 'L'} 
                                  onChange={e => updateRejectItem(r.id, r.items[0]?.id, 'unit', e.target.value)}
                                >
                                  <option value="L">L</option>
                                  <option value="mL">mL</option>
                                  <option value="kg">kg</option>
                                  <option value="pcs">pcs</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div>
                            <Input 
                              placeholder="Catatan rincian (misal: formula keruh setelah pengadukan 30m)..." 
                              className="h-8 text-xs" 
                              value={r.items[0]?.notes || ''} 
                              onChange={e => updateRejectItem(r.id, r.items[0]?.id, 'notes', e.target.value)} 
                            />
                          </div>
                        </div>
                      ))}

                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-xs border-dashed text-purple-700 bg-white hover:bg-purple-50"
                        onClick={() => handleAddVariantReject(selesaiPO?.product_name || 'Hasil Mixing')}
                      >
                        + Tambah Catatan Reject Formula Lain
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {rejects.map((r, rIdx) => (
                        <div key={r.id} className="border border-amber-200 rounded-xl p-3 bg-amber-50/40 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            {selesaiPO?.products?.length > 1 ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Label className="text-xs font-semibold text-slate-700 shrink-0">Varian:</Label>
                                <select
                                  className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white flex-1 font-semibold text-slate-800"
                                  value={r.variantName}
                                  onChange={e => updateRejectVariant(r.id, e.target.value)}
                                >
                                  <option value="">Pilih Varian...</option>
                                  {selesaiPO.products.map(p => (
                                    <option key={p.product_name} value={p.product_name}>{p.product_name}</option>
                                  ))}
                                </select>
                              </div>
                            ) : (
                              <span className="text-xs font-bold text-amber-900">{r.variantName || 'Produk Utama'}</span>
                            )}
                            {rejects.length > 1 && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:bg-red-100" onClick={() => setRejects(rejects.filter(rj => rj.id !== r.id))}>Hapus Varian</Button>
                            )}
                          </div>
                          
                          <div className="space-y-2 pl-8">
                            {r.items.map((it) => (
                              <div key={it.id} className="flex flex-wrap gap-2 items-start bg-white p-2 rounded-lg border border-amber-100 shadow-sm relative pr-6">
                                <div className="flex-1 min-w-[120px]">
                                  <Label className="text-[10px] text-slate-500 mb-1 block">Item Reject</Label>
                                  <select 
                                    className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                                    value={it.name} 
                                    onChange={e => updateRejectItem(r.id, it.id, 'name', e.target.value)}
                                  >
                                    <option value="">Pilih Item...</option>
                                    {getRejectOptions().map(opt => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="w-20">
                                  <Label className="text-[10px] text-slate-500 mb-1 block">Qty</Label>
                                  <Input type="number" placeholder="0" className="h-8 text-xs" value={it.qty} onChange={e => updateRejectItem(r.id, it.id, 'qty', e.target.value)} />
                                </div>
                                <div className="w-full">
                                  <Input placeholder="Catatan (opsional)..." className="h-8 text-xs" value={it.notes} onChange={e => updateRejectItem(r.id, it.id, 'notes', e.target.value)} />
                                </div>
                                <button className="absolute top-2 right-2 text-slate-300 hover:text-red-500" onClick={() => removeRejectItem(r.id, it.id)}>
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <Button variant="outline" size="sm" className="h-7 text-[10px] border-dashed w-full text-amber-700 bg-white" onClick={() => handleAddItemReject(r.id)}>
                              + Tambah Item Reject di Varian ini
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {selesaiPO?.products?.length > 1 && (
                        <Button variant="outline" size="sm" className="w-full border-dashed text-amber-700 hover:bg-amber-50 border-amber-300" onClick={() => handleAddVariantReject('')}>
                          + Tambah Varian Produk Lain
                        </Button>
                      )}
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-2 border-t">
                    <Button variant="outline" className="flex-1" onClick={() => setRejectStep('ask_reject')}>Kembali</Button>
                    <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold" onClick={() => setRejectStep('form')}>Lanjut ke Form Selesai ➔</Button>
                  </div>
                </div>
              )}

              {rejectStep === 'form' && (() => {
                const act = (selesaiTask.activity_type || '').toLowerCase();
                const isBottle = ['filling', 'packing', 'pengemasan', 'labeling', 'sealing'].includes(act);
                const targetPO = isBottle ? getTaskPoTargetPcs(selesaiTask, productionOrders) : (Number(selesaiTask.target_quantity) || 0);
                const realNum = completedQty !== "" ? Number(completedQty) : targetPO;
                const selisih = realNum - targetPO;

                return (
                  <>
                    {isBottle ? (
                      <div className="space-y-3.5">
                        {/* Dual Target PO vs Hasil Jadi Card */}
                        <div className="grid grid-cols-2 gap-2.5">
                          {/* Target PO Card */}
                          <div className="bg-gradient-to-br from-[#F9F5EC] to-[#F1EAD9] border border-[#E6D5B8] rounded-xl p-3 shadow-xs space-y-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-[#8C6208] flex items-center gap-1">
                              🎯 Target PO
                            </span>
                            <div className="text-xl font-extrabold text-[#302621]">
                              {targetPO} <span className="text-xs font-semibold text-[#8C6208]">pcs</span>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-tight">
                              Target resmi dari pesanan PO
                            </p>
                          </div>

                          {/* Hasil Jadi Input Card */}
                          <div className="bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border border-blue-200 rounded-xl p-3 shadow-xs space-y-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1">
                              📦 Jadinya Berapa *
                            </span>
                            <div className="flex items-center gap-1.5">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={completedQty}
                                onChange={(e) => setCompletedQty(e.target.value)}
                                placeholder={String(targetPO)}
                                className="h-9 text-base font-extrabold text-slate-900 bg-white border-blue-300 focus:border-blue-600 shadow-2xs"
                              />
                              <span className="text-xs font-bold text-blue-900 shrink-0">pcs</span>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-tight">
                              Total botol jadi hasil {selesaiTask.activity_type === 'filling' ? 'filling' : 'pengemasan'}
                            </p>
                          </div>
                        </div>

                        {/* Realtime Selisih Analysis */}
                        {completedQty !== "" && (
                          selisih === 0 ? (
                            <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 shadow-2xs">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                              <div>
                                <p className="font-bold text-emerald-950">Sesuai Target PO (100% Sempurna)</p>
                                <p className="text-[11px] text-emerald-700 leading-tight">
                                  Hasil jadi {completedQty} pcs persis sama dengan target pesanan {targetPO} pcs tanpa selisih.
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="border border-amber-300 bg-amber-50/70 rounded-xl p-3.5 space-y-3 shadow-xs animate-in fade-in-50 duration-200">
                              {/* Discrepancy Header */}
                              <div className="flex items-start gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-2xs font-mono font-bold text-xs">
                                  {selisih < 0 ? "⏱" : "✓"}
                                </div>
                                <div>
                                  <p className="font-bold text-xs text-amber-950">
                                    Hasil Jadi ({completedQty || 0} pcs) {selisih < 0 ? `Kurang ${Math.abs(selisih)} pcs` : `Berlebih +${selisih} pcs`} dari Target PO ({targetPO} pcs)
                                  </p>
                                  <p className="text-[11px] text-amber-800 font-medium leading-tight mt-0.5">
                                    {selisih < 0 
                                      ? "Tentukan apakah pengerjaan ini dicicil berkala, selesai karena reject, atau toleransi/formula habis:"
                                      : "Hasil jadi melebihi target PO, konfirmasi penyelesaian tugas:"}
                                  </p>
                                </div>
                              </div>

                              {/* 3-Way Selector for Shortage (Dicicil, Reject, Toleransi) */}
                              {selisih < 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                  {/* Option 1: Dicicil */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDiscrepancyType('dicicil');
                                      setDiscrepancyRejectQty('0');
                                      if (!discrepancyReason || discrepancyReason.includes('Cacat') || discrepancyReason.includes('Rusak') || discrepancyReason.includes('habis')) {
                                        setDiscrepancyReason('');
                                      }
                                    }}
                                    className={`p-2.5 rounded-xl border text-xs transition-all text-left flex flex-col gap-1 cursor-pointer ${
                                      discrepancyType === 'dicicil'
                                        ? 'bg-[#FDFBF7] border-[#8C6208] text-[#302621] ring-2 ring-[#8C6208]/30 shadow-xs'
                                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between font-bold">
                                      <span className="flex items-center gap-1.5 text-xs text-[#8C6208]">
                                        <span>⏱</span>
                                        <span>Dicicil (Belum Selesai)</span>
                                      </span>
                                      <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                                        discrepancyType === 'dicicil' ? 'bg-[#8C6208] text-white font-bold' : 'border border-slate-300'
                                      }`}>
                                        {discrepancyType === 'dicicil' ? '✓' : ''}
                                      </span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 leading-tight">
                                      Output baru selesai sebagian ({completedQty || 0} pcs). Sisa dicicil berikutnya. Tugas tetap berjalan.
                                    </p>
                                  </button>

                                  {/* Option 2: Reject Fisik */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDiscrepancyType('reject');
                                      if (!discrepancyRejectQty || discrepancyRejectQty === '0') {
                                        setDiscrepancyRejectQty(String(Math.abs(selisih)));
                                      }
                                    }}
                                    className={`p-2.5 rounded-xl border text-xs transition-all text-left flex flex-col gap-1 cursor-pointer ${
                                      discrepancyType === 'reject'
                                        ? 'bg-rose-50/60 border-rose-400 text-rose-950 ring-2 ring-rose-400/30 shadow-xs'
                                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between font-bold">
                                      <span className="flex items-center gap-1.5 text-xs text-rose-700">
                                        <span>⊘</span>
                                        <span>Ada Reject Fisik</span>
                                      </span>
                                      <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                                        discrepancyType === 'reject' ? 'bg-rose-600 text-white font-bold' : 'border border-slate-300'
                                      }`}>
                                        {discrepancyType === 'reject' ? '✓' : ''}
                                      </span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 leading-tight">
                                      Tugas selesai. Selisih {Math.abs(selisih)} pcs karena barang cacat, bocor, pecah, atau rusak.
                                    </p>
                                  </button>

                                  {/* Option 3: Toleransi / Formula Habis */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDiscrepancyType('tidak_reject');
                                      setDiscrepancyRejectQty('0');
                                      if (!discrepancyReason || discrepancyReason.includes('Cacat') || discrepancyReason.includes('Rusak')) {
                                        setDiscrepancyReason('');
                                      }
                                    }}
                                    className={`p-2.5 rounded-xl border text-xs transition-all text-left flex flex-col gap-1 cursor-pointer ${
                                      discrepancyType === 'tidak_reject'
                                        ? 'bg-amber-50/80 border-amber-500 text-amber-950 ring-2 ring-amber-500/30 shadow-xs'
                                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between font-bold">
                                      <span className="flex items-center gap-1.5 text-xs text-amber-800">
                                        <span>✓</span>
                                        <span>Toleransi / Habis</span>
                                      </span>
                                      <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                                        discrepancyType === 'tidak_reject' ? 'bg-amber-600 text-white font-bold' : 'border border-slate-300'
                                      }`}>
                                        {discrepancyType === 'tidak_reject' ? '✓' : ''}
                                      </span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 leading-tight">
                                      Tugas selesai tanpa reject. Liquid habis atau susut toleransi pabrik wajar.
                                    </p>
                                  </button>
                                </div>
                              ) : (
                                <div className="p-2.5 bg-emerald-50 border border-emerald-300 rounded-xl text-xs text-emerald-900 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-emerald-700 text-sm">✓</span>
                                    <div>
                                      <p className="font-bold">Hasil Jadi Melebihi Target PO (+{selisih} pcs)</p>
                                      <p className="text-[11px] text-emerald-700">Tugas diselesaikan dengan hasil berlebih (ekstra output).</p>
                                    </div>
                                  </div>
                                  <Badge className="bg-emerald-600 text-white text-[10px] font-bold">Extra Output</Badge>
                                </div>
                              )}

                              {/* Conditional Detail: If DICICIL */}
                              {discrepancyType === 'dicicil' && (
                                <div className="bg-white p-3 rounded-xl border border-[#E6D5B8] space-y-2.5 animate-in fade-in-50 duration-200">
                                  <div className="flex items-center justify-between text-xs pb-1.5 border-b border-slate-100">
                                    <span className="font-semibold text-slate-700">Status Tugas:</span>
                                    <span className="font-bold text-[#8C6208] bg-[#FDFBF7] border border-[#E6D5B8] px-2 py-0.5 rounded-md text-[11px] flex items-center gap-1">
                                      <span>⏱</span>
                                      <span>Tetap Berjalan (In Progress)</span>
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-200/60 space-y-1.5 font-mono">
                                    <div className="flex justify-between">
                                      <span className="font-sans text-slate-500">Target PO:</span>
                                      <strong className="text-slate-800">{targetPO.toLocaleString('id-ID')} pcs</strong>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="font-sans text-slate-500">Akumulasi Output Saat Ini:</span>
                                      <strong className="text-[#8C6208] font-bold">
                                        {Number(completedQty || 0).toLocaleString('id-ID')} pcs ({Math.min(100, Math.round(((Number(completedQty || 0)) / targetPO) * 100))}%)
                                      </strong>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="font-sans text-slate-500">Sisa Yang Belum Selesai:</span>
                                      <strong className="text-slate-800">{Math.max(0, targetPO - Number(completedQty || 0)).toLocaleString('id-ID')} pcs</strong>
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-[11px] font-semibold text-slate-600 mb-1.5 block">
                                      Pilih atau Tulis Catatan Cicilan (Opsional)
                                    </Label>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                      {[
                                        'Selesai shift 1, lanjut shift berikutnya',
                                        'Sisa botol/tutup menyusul dari gudang',
                                        'Target batch harian tercapai, lanjut besok',
                                        'Lainnya'
                                      ].map((chip) => (
                                        <button
                                          key={chip}
                                          type="button"
                                          onClick={() => setDiscrepancyReason(chip === 'Lainnya' ? '' : chip)}
                                          className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                                            discrepancyReason === chip
                                              ? 'bg-[#8C6208] text-white border-[#8C6208] shadow-2xs'
                                              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-amber-50 hover:text-[#8C6208]'
                                          }`}
                                        >
                                          {chip}
                                        </button>
                                      ))}
                                    </div>
                                    <Input
                                      placeholder="Tulis catatan pengerjaan bertahap..."
                                      value={discrepancyReason}
                                      onChange={(e) => setDiscrepancyReason(e.target.value)}
                                      className="h-8 text-xs border-slate-200 focus:border-[#8C6208]"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Conditional Detail: If REJECT */}
                              {discrepancyType === 'reject' && (
                                <div className="bg-white p-3 rounded-xl border border-red-200 space-y-2.5 animate-in fade-in-50 duration-200">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold text-red-900 flex items-center gap-1.5">
                                      <span className="w-2 h-2 rounded-full bg-red-600" />
                                      Jumlah Barang Reject *
                                    </Label>
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="number"
                                        min="1"
                                        value={discrepancyRejectQty}
                                        onChange={(e) => setDiscrepancyRejectQty(e.target.value)}
                                        className="h-8 w-24 text-xs font-bold text-center border-red-300 focus:border-red-500"
                                        placeholder={String(Math.abs(selisih))}
                                      />
                                      <span className="text-xs text-red-900 font-semibold">pcs</span>
                                    </div>
                                  </div>

                                  <div>
                                    <Label className="text-[11px] font-semibold text-slate-600 mb-1.5 block">
                                      Pilih Alasan Reject (Wajib) *
                                    </Label>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                      {[
                                        'Botol Cacat / Retak / Pecah',
                                        'Tutup / Cap Rusak / Longgar',
                                        'Spray / Pipet Mampet / Bocor',
                                        'Liquid Tumpah saat Filling',
                                        'Gagal Crimp / Capping Miring',
                                        'Lainnya'
                                      ].map((chip) => (
                                        <button
                                          key={chip}
                                          type="button"
                                          onClick={() => setDiscrepancyReason(chip === 'Lainnya' ? '' : chip)}
                                          className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                                            discrepancyReason === chip
                                              ? 'bg-red-600 text-white border-red-600 shadow-2xs'
                                              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-red-50 hover:text-red-700'
                                          }`}
                                        >
                                          {chip}
                                        </button>
                                      ))}
                                    </div>
                                    <Input
                                      placeholder="Tuliskan rincian alasan reject..."
                                      value={discrepancyReason}
                                      onChange={(e) => setDiscrepancyReason(e.target.value)}
                                      className="h-8 text-xs border-red-200 focus:border-red-500"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Conditional Detail: If TIDAK REJECT */}
                              {discrepancyType === 'tidak_reject' && (
                                <div className="bg-white p-3 rounded-xl border border-amber-200 space-y-2.5 animate-in fade-in-50 duration-200">
                                  <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-amber-600" />
                                    Catatan Status: Tidak Ada Reject Fisik
                                  </div>
                                  <div>
                                    <Label className="text-[11px] font-semibold text-slate-600 mb-1.5 block">
                                      Pilih Alasan Mengapa Selisih Target (Wajib) *
                                    </Label>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                      {[
                                        'Takaran liquid mixing menyusut',
                                        'Evaporasi alkohol saat maserasi',
                                        'Toleransi pengisian botol berlebih',
                                        'Bahan kemasan kurang dari supplier',
                                        'Lainnya'
                                      ].map((chip) => (
                                        <button
                                          key={chip}
                                          type="button"
                                          onClick={() => setDiscrepancyReason(chip === 'Lainnya' ? '' : chip)}
                                          className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-all cursor-pointer ${
                                            discrepancyReason === chip
                                              ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                                              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-amber-50 hover:text-amber-800'
                                          }`}
                                        >
                                          {chip}
                                        </button>
                                      ))}
                                    </div>
                                    <Input
                                      placeholder="Tuliskan alasan mengapa hasil tidak sesuai target..."
                                      value={discrepancyReason}
                                      onChange={(e) => setDiscrepancyReason(e.target.value)}
                                      className="h-8 text-xs border-amber-200 focus:border-amber-500"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Output quantity for Mixing */}
                        <div className="space-y-2 border border-blue-200 bg-blue-50/40 rounded-xl p-3.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-bold flex items-center gap-1.5 text-slate-800">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              Jumlah Output Hasil Mixing
                            </Label>
                            <span className="text-[11px] text-slate-500 font-medium">
                              Target: <strong className="text-slate-800">{selesaiTask.target_quantity || 0} {selesaiTask.unit || 'Liter'}</strong>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder={String(selesaiTask.target_quantity || "0")}
                              value={completedQty}
                              onChange={(e) => setCompletedQty(e.target.value)}
                              min="0"
                              className="bg-white font-bold text-slate-900 border-slate-300 focus:border-blue-500 text-sm h-10 shadow-2xs"
                            />
                            <span className="text-sm text-slate-600 font-semibold px-2 shrink-0">{selesaiTask.unit || "Liter"}</span>
                          </div>
                        </div>

                        {/* Ringkasan Reject jika ada reject tercatat */}
                        {rejects.filter(r => r.items?.some(it => it.name && Number(it.qty) > 0)).length > 0 && (
                          <div className="border border-amber-300 bg-amber-50/90 rounded-xl p-3 space-y-2 text-xs shadow-sm">
                            <div className="flex items-center justify-between font-bold text-amber-900 border-b border-amber-200 pb-1.5">
                              <span className="flex items-center gap-1.5">
                                <AlertCircle className="w-4 h-4 text-amber-600" />
                                Ringkasan Item Reject ({rejects.reduce((sum, r) => sum + r.items.filter(it => it.name && Number(it.qty) > 0).length, 0)} item)
                              </span>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-6 text-[11px] text-amber-800 hover:text-amber-950 hover:bg-amber-100 font-semibold px-2" 
                                onClick={() => setRejectStep('fill_reject')}
                              >
                                Ubah Reject ✏️
                              </Button>
                            </div>
                            <div className="space-y-1.5">
                              {rejects.filter(r => r.items?.some(it => it.name && Number(it.qty) > 0)).map((r, i) => (
                                <div key={i} className="bg-white/90 rounded-lg p-2 border border-amber-200/70 space-y-1">
                                  <p className="font-semibold text-slate-800 text-[11px]">{r.variantName || 'Produk Utama'}</p>
                                  {r.items.filter(it => it.name && Number(it.qty) > 0).map((it, j) => (
                                    <div key={j} className="flex items-center justify-between text-[11px] text-slate-600 pl-2">
                                      <span>• {it.name}{it.notes ? ` (${it.notes})` : ''}</span>
                                      <span className="font-bold text-amber-900 bg-amber-100/70 px-1.5 py-0.5 rounded">{it.qty} {it.unit || 'L'}</span>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Maserasi fields for racik/mixing tasks */}
                        {(selesaiTask.activity_type === "racik" || selesaiTask.activity_type === "mixing" || selesaiTask.type === "racik" || selesaiTask.type === "mixing") && (
                          selesaiTask.status === "waiting_qc" ? (
                            <div className="border border-purple-200 bg-purple-50/70 rounded-xl p-3 space-y-1">
                              <p className="text-xs font-bold text-purple-800 flex items-center gap-1.5">
                                <Timer className="w-3.5 h-3.5 text-purple-600" />
                                Rencana Durasi Maserasi
                              </p>
                              <p className="text-xs text-purple-900 font-semibold">
                                {selesaiTask.maserasi_hari ?? (maserasiHari || 2)} Hari {selesaiTask.maserasi_jam ?? (maserasiJam || 0)} Jam
                                <span className="text-[11px] text-purple-600 block font-normal mt-0.5">
                                  (Durasi telah ditetapkan oleh PIC Mixing{selesaiTask.pic_name ? `: ${selesaiTask.pic_name}` : ''})
                                </span>
                              </p>
                            </div>
                          ) : (
                            <div className="border border-purple-200 bg-purple-50 rounded-xl p-3 space-y-2">
                              <p className="text-xs font-semibold text-purple-700 flex items-center gap-1.5">
                                <Timer className="w-3.5 h-3.5" />
                                Tentukan Waktu Maserasi
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs text-muted-foreground">Hari</Label>
                                  <Input
                                    type="number"
                                    value={maserasiHari}
                                    onChange={(e) => setMaserasiHari(e.target.value)}
                                    placeholder="2"
                                    min="0"
                                    className="bg-white"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">Jam</Label>
                                  <Input
                                    type="number"
                                    value={maserasiJam}
                                    onChange={(e) => setMaserasiJam(e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    max="23"
                                    className="bg-white"
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </>
                    )}

                    {/* QC Checklist for final packing QC validation */}
                    {isQCUser && (selesaiTask.activity_type === "packing" || selesaiTask.activity_type === "pengemasan" || selesaiTask.activity_type === "labeling") && (
                      <div className="border border-indigo-200 bg-indigo-50/70 rounded-xl p-3 space-y-2.5">
                        <div className="flex items-center gap-2 text-indigo-900 font-bold text-xs">
                          <ShieldCheck className="w-4 h-4 text-indigo-600" />
                          Audit Standar Kualitas Akhir (Quality Control):
                        </div>
                        <div className="space-y-1.5 text-xs">
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2 rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-50/50">
                            <input
                              type="checkbox"
                              checked={qcChecklist.packaging}
                              onChange={(e) => setQcChecklist({ ...qcChecklist, packaging: e.target.checked })}
                              className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                            />
                            <span className="font-medium text-slate-700">Kerapihan Kemasan & Sealing Produk</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2 rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-50/50">
                            <input
                              type="checkbox"
                              checked={qcChecklist.labeling}
                              onChange={(e) => setQcChecklist({ ...qcChecklist, labeling: e.target.checked })}
                              className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                            />
                            <span className="font-medium text-slate-700">Stiker Label & Cetakan Kualitas Baik & Presisi</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2 rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-50/50">
                            <input
                              type="checkbox"
                              checked={qcChecklist.expDate}
                              onChange={(e) => setQcChecklist({ ...qcChecklist, expDate: e.target.checked })}
                              className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                            />
                            <span className="font-medium text-slate-700">Tanggal Kadaluarsa (Exp Date) & Batch Number Tertera Jelas</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2 rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-50/50">
                            <input
                              type="checkbox"
                              checked={qcChecklist.sealBox}
                              onChange={(e) => setQcChecklist({ ...qcChecklist, sealBox: e.target.checked })}
                              className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                            />
                            <span className="font-medium text-slate-700">Kelengkapan Dus / Inner Box & Segel Box</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2 rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-50/50">
                            <input
                              type="checkbox"
                              checked={qcChecklist.qtyMatch}
                              onChange={(e) => setQcChecklist({ ...qcChecklist, qtyMatch: e.target.checked })}
                              className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                            />
                            <span className="font-medium text-slate-700">Jumlah Qty Akhir Sesuai Target & Bebas Cacat</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Catatan (opsional)</Label>
                      <Textarea
                        placeholder="Ada catatan tambahan?"
                        value={selesaiNotes}
                        onChange={(e) => setSelesaiNotes(e.target.value)}
                        rows={2}
                      />
                    </div>

                    <div className="bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-700">
                      ✓ Setelah selesai, aktivitas akan otomatis masuk ke <strong>Catatan Harian</strong> Anda.
                      {rejects.filter(r => r.items?.some(it => it.name && Number(it.qty) > 0)).length > 0 && <span> (Termasuk {rejects.reduce((sum, r) => sum + r.items.filter(it => it.name && Number(it.qty) > 0).length, 0)} catatan item reject).</span>}
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 cursor-pointer" 
                        onClick={() => isBottle ? setSelesaiTask(null) : setRejectStep('ask_reject')}
                      >
                        {isBottle ? 'Batal' : 'Kembali'}
                      </Button>
                      <Button 
                        className={`flex-1 text-white gap-1.5 cursor-pointer ${
                          discrepancyType === 'dicicil'
                            ? 'bg-[#8C6208] hover:bg-[#724f06] font-bold shadow-sm'
                            : selesaiTask.status === 'waiting_qc' 
                              ? 'bg-indigo-600 hover:bg-indigo-700 font-bold' 
                              : 'bg-green-600 hover:bg-green-700 font-semibold'
                        }`} 
                        onClick={handleConfirmSelesai} 
                        disabled={
                          loading === selesaiTask.id || 
                          (isBottle && completedQty === "") || 
                          (isBottle && selisih !== 0 && (!discrepancyType || (discrepancyType !== 'dicicil' && !discrepancyReason.trim())))
                        }
                      >
                        {discrepancyType === 'dicicil' ? (
                          <>
                            <span>⏱</span>
                            <span>{loading === selesaiTask.id ? "Menyimpan..." : `Simpan Cicilan (${completedQty || 0}/${targetPO} pcs)`}</span>
                          </>
                        ) : selesaiTask.status === 'waiting_qc' ? (
                          <>
                            <ShieldCheck className="w-4 h-4" />
                            {loading === selesaiTask.id ? "Memvalidasi..." : "Validasi QC & Selesaikan"}
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            {loading === selesaiTask.id ? "Menyimpan..." : (isBottle ? "Selesai & Ajukan ke QC" : "Selesaikan & Simpan")}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                );
              })()}

            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG KONFIRMASI MULAI (ASA AI ANALYST) ===== */}
      <Dialog open={!!mulaiTask} onOpenChange={() => setMulaiTask(null)}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border border-[#E6D5B8] shadow-2xl rounded-2xl">
          {mulaiTask && (
            <div className="flex flex-col bg-gradient-to-b from-[#FFFDF9] to-[#F9F5EC]">
              {/* Header ASA Avatar */}
              <div className="bg-gradient-to-r from-[#8C6208] via-[#a1730d] to-[#b8860b] p-5 text-white flex items-center gap-3.5 relative overflow-hidden">
                <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center shrink-0 shadow-lg">
                  <Sparkles className="w-6 h-6 text-amber-200" />
                </div>
                <div>
                  <h3 className="font-bold text-base leading-snug flex items-center gap-1.5">
                    Konfirmasi Mulai Tugas PO 🚀
                  </h3>
                  <p className="text-xs text-amber-100 opacity-90 font-medium">
                    ASA (AI Assistant Analyst)
                  </p>
                </div>
              </div>

              {/* Body Content */}
              <div className="p-6 space-y-4 text-slate-800">
                <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-semibold text-[#734f06] flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-[#8C6208]" />
                    Halo {sapaan}! ✨
                  </p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Apakah {sapaan} yakin sudah siap untuk mulai mengerjakan tugas <strong className="text-slate-900">{mulaiTask.task_name}</strong> {mulaiTask.po_number ? `(PO #${mulaiTask.po_number})` : ''}?
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-1.5 shadow-sm">
                  <span className="text-[11px] text-amber-800 font-bold uppercase tracking-wider block">⚠️ Catatan Tanggung Jawab PIC:</span>
                  <p className="text-xs text-slate-600 leading-normal">
                    Dengan menekan tombol mulai, {sapaan} akan otomatis tercatat sebagai <strong className="text-slate-900">PIC Utama (Penanggung Jawab Pengerjaan)</strong> tugas ini. Pastikan persiapan alat dan bahan sudah siap ya!
                  </p>
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-2.5 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 text-xs h-10 border-slate-300 hover:bg-slate-100"
                    onClick={() => setMulaiTask(null)}
                    disabled={loading === mulaiTask.id}
                  >
                    Batal / Nanti Dulu
                  </Button>
                  <Button
                    className="flex-1 text-xs h-10 bg-gradient-to-r from-[#8C6208] to-[#b8860b] hover:from-[#734f06] hover:to-[#966d09] text-white font-semibold gap-1.5 shadow-md"
                    onClick={() => handleConfirmMulai(mulaiTask)}
                    disabled={loading === mulaiTask.id}
                  >
                    <Play className="w-4 h-4" />
                    {loading === mulaiTask.id ? "Memproses..." : "Ya, Saya Siap Jadi PIC!"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG KONFIRMASI TAKEOVER PIC (>6 JAM JEDA) ===== */}
      <Dialog open={!!takeoverTask} onOpenChange={() => setTakeoverTask(null)}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border border-[#E6D5B8] shadow-2xl rounded-2xl">
          {takeoverTask && (
            <div className="flex flex-col bg-gradient-to-b from-[#FFFDF9] to-[#F9F5EC]">
              <div className="bg-gradient-to-r from-amber-600 via-amber-700 to-orange-700 p-5 text-white flex items-center gap-3.5 relative overflow-hidden">
                <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center shrink-0 shadow-lg">
                  <RefreshCw className="w-6 h-6 text-amber-200" />
                </div>
                <div>
                  <h3 className="font-bold text-base leading-snug flex items-center gap-1.5">
                    Ambil Alih PIC Tugas 🚀
                  </h3>
                  <p className="text-xs text-amber-100 opacity-90 font-medium">
                    ASA (AI Assistant Analyst)
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-4 text-slate-800">
                <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-semibold text-[#734f06] flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-[#8C6208]" />
                    Halo {sapaan}!
                  </p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Tugas <strong className="text-slate-900">{takeoverTask.task_name}</strong> {takeoverTask.po_number ? `(PO #${takeoverTask.po_number})` : ''} sebelumnya dikerjakan oleh <strong className="text-slate-900">{takeoverTask.pic_name || 'PIC Sebelumnya'}</strong>.
                  </p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Apakah {sapaan} ingin melanjutkan pengerjaan dan resmi mengambil alih tugas ini sebagai <strong className="text-slate-900">PIC Utama Baru</strong>?
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-3.5 space-y-1.5 shadow-sm">
                  <span className="text-[11px] text-amber-800 font-bold uppercase tracking-wider block">📊 Pelacakan Kontributor Multi-PIC:</span>
                  <p className="text-xs text-slate-600 leading-normal">
                    Durasi kerja PIC sebelumnya ({takeoverTask.pic_name}) akan otomatis disimpan di laporan, dan durasi pengerjaan {sapaan} akan mulai dihitung secara presisi dari sekarang.
                  </p>
                </div>

                <div className="flex gap-2.5 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 text-xs h-10 border-slate-300 hover:bg-slate-100"
                    onClick={() => setTakeoverTask(null)}
                    disabled={loading === takeoverTask.id}
                  >
                    Batal
                  </Button>
                  <Button
                    className="flex-1 text-xs h-10 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold gap-1.5 shadow-md"
                    onClick={() => handleConfirmTakeoverPIC(takeoverTask)}
                    disabled={loading === takeoverTask.id}
                  >
                    <RefreshCw className="w-4 h-4" />
                    {loading === takeoverTask.id ? "Memproses..." : "Ya, Ambil Alih & Mulai!"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG PINDAHKAN PIC (MANUAL HANDOVER BY ACTIVE PIC / KEPALA) ===== */}
      <Dialog open={!!transferTask} onOpenChange={() => { setTransferTask(null); setTargetMemberId(""); setMemberSearchQuery(""); }}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border border-[#E6D5B8] shadow-2xl rounded-2xl">
          {transferTask && (() => {
            const act = (transferTask.activity_type || '').toLowerCase();
            const isProduksi = act === 'racik' || act === 'mixing' || act === 'maserasi' || act === 'filling';
            const isPengemasan = act === 'packing' || act === 'pengemasan' || act === 'labeling';
            const targetDeptLabel = isProduksi ? 'Tim Produksi' : isPengemasan ? 'Tim Pengemasan' : 'Tim Produksi & Pengemasan';

            const departmentCandidates = teamMembers.filter(tm => {
              if (tm.id === transferTask.pic_id) return false;
              const posRole = `${tm.position || ''} ${tm.role || ''} ${tm.department || ''}`.toLowerCase();

              // Exclude non-factory / administrative / non-production roles completely
              const isNonFactory =
                posRole.includes('qc') || posRole.includes('quality') ||
                posRole.includes('developer') || posRole.includes('owner') || posRole.includes('direktur') ||
                posRole.includes('finance') || posRole.includes('marketing') || posRole.includes('purchasing') ||
                posRole.includes('admin') || posRole.includes('hrd');

              if (isNonFactory) return false;

              const subDiv = tm.sub_divisi?.toLowerCase();
              const tmIsPengemasan = subDiv === 'sekunder' || posRole.includes('pengemasan') || posRole.includes('packing') || posRole.includes('labeling') || posRole.includes('sekunder');
              const tmIsProduksi = subDiv === 'primer' || (!tmIsPengemasan && (posRole.includes('produksi') || posRole.includes('primer')));

              if (isProduksi) return tmIsProduksi;
              if (isPengemasan) return tmIsPengemasan;
              return tmIsProduksi || tmIsPengemasan;
            });

            const filteredCandidates = departmentCandidates.filter(tm => {
              if (!memberSearchQuery) return true;
              const q = memberSearchQuery.toLowerCase();
              return tm.full_name?.toLowerCase().includes(q) || (tm.position || '').toLowerCase().includes(q);
            });

            return (
              <div className="flex flex-col bg-gradient-to-b from-[#FFFDF9] to-[#F9F5EC]">
                <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 p-5 text-white flex items-center gap-3.5 relative overflow-hidden">
                  <div className="w-12 h-12 rounded-2xl bg-white/20  border border-white/30 flex items-center justify-center shrink-0 shadow-lg">
                    <UserPlus className="w-6 h-6 text-blue-200" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base leading-snug flex items-center gap-1.5">
                      Pindahkan PIC Tugas 👥
                    </h3>
                    <p className="text-xs text-blue-100 opacity-90 font-medium">
                      Handover Pengerjaan Khusus ({targetDeptLabel})
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-4 text-slate-800">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                      <span>Pilih Rekan {targetDeptLabel}:</span>
                      <span className="text-[10px] text-slate-400 font-normal">{filteredCandidates.length} Anggota Tersedia</span>
                    </Label>
                    
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="🔍 Cari nama anggota tim..."
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                        className="pl-8 text-xs h-9 border-slate-300 bg-white rounded-lg"
                      />
                    </div>

                    {/* Member Selectable List */}
                    <div className="max-h-48 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-1.5 bg-white shadow-inner">
                      {filteredCandidates.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-4">Tidak ada anggota {targetDeptLabel} yang cocok.</p>
                      ) : (
                        filteredCandidates.map(tm => {
                          const isSelected = targetMemberId === tm.id;
                          return (
                            <div
                              key={tm.id}
                              onClick={() => setTargetMemberId(tm.id)}
                              className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-all border ${
                                isSelected
                                  ? 'bg-blue-50 border-blue-400 text-blue-900 font-bold shadow-xs ring-1 ring-blue-400/50'
                                  : 'hover:bg-slate-50 border-transparent text-slate-700'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                  isSelected ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                                }`}>
                                  {tm.full_name?.charAt(0) || 'U'}
                                </div>
                                <div>
                                  <p className="text-xs font-semibold leading-tight">{tm.full_name}</p>
                                  <p className="text-[10px] text-slate-500">{tm.position || tm.role || 'Staf'}</p>
                                </div>
                              </div>
                              {isSelected && <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50/80 border border-blue-200/80 rounded-xl p-3.5 space-y-1.5">
                    <span className="text-[11px] text-blue-800 font-bold uppercase tracking-wider block">ℹ️ Catatan Handover PIC:</span>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Tugas <strong className="text-slate-900">{transferTask.task_name}</strong> akan dialihkan dari <strong className="text-slate-900">{transferTask.pic_name || 'PIC Saat Ini'}</strong> ke PIC baru. Riwayat PIC lama tetap tersimpan aman di laporan.
                    </p>
                  </div>

                  <div className="flex gap-2.5 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1 text-xs h-10 border-slate-300 hover:bg-slate-100"
                      onClick={() => { setTransferTask(null); setTargetMemberId(""); setMemberSearchQuery(""); }}
                      disabled={loading === transferTask.id}
                    >
                      Batal
                    </Button>
                    <Button
                      className="flex-1 text-xs h-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold gap-1.5 shadow-md"
                      onClick={() => {
                        const targetMember = teamMembers.find(m => m.id === targetMemberId);
                        handleConfirmManualTransferPIC(transferTask, targetMember);
                      }}
                      disabled={!targetMemberId || loading === transferTask.id}
                    >
                      <UserPlus className="w-4 h-4" />
                      {loading === transferTask.id ? "Memindahkan..." : "Konfirmasi Pindahkan PIC"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG LIHAT DETAIL & PROGRES PO (COMPLETE 5-STAGE PIPELINE) ===== */}
      <Dialog open={!!detailTask} onOpenChange={() => setDetailTask(null)}>
        <DialogContent className="sm:max-w-[760px] p-0 overflow-hidden border border-[#E6D5B8] shadow-2xl rounded-2xl">
          {detailTask && (() => {
            const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const poId = detailTask.po_id || detailTask.production_order_id;
            const poNum = detailTask.po_number;

            const currentPO = productionOrders.find(p => (poId && p.id === poId) || (poNum && p.order_number === poNum) || (poNum && norm(p.order_number) === norm(poNum)));

            let matchingDbTasks = tasks.filter(t => {
              if (poId && (t.po_id === poId || t.production_order_id === poId)) return true;
              if (poNum && norm(t.po_number) && norm(t.po_number) === norm(poNum)) return true;
              return false;
            });

            const getPauseMinsFromTask = (t) => {
              if (t.total_pause_minutes && Number(t.total_pause_minutes) > 0) return Number(t.total_pause_minutes);
              if (t.member_progress) {
                const mpPause = Object.values(t.member_progress).reduce((s, m) => s + (Number(m.total_pause_minutes) || 0), 0);
                if (mpPause > 0) return mpPause;
              }
              const noteStr = t.notes || '';
              const jMatch = noteStr.match(/Jeda:\s*(\d+)m/i);
              if (jMatch && jMatch[1]) return parseInt(jMatch[1], 10);
              return 0;
            };

            // Calculate total PO Volume & Weight
            let totalPoVolumeMl = 0;
            if (currentPO?.products?.length > 0) {
              currentPO.products.forEach(p => {
                const q = Number(p.quantity) || 0;
                const sz = Number(p.size_ml || p.ukuran_ml || currentPO.botol_ukuran_aktual || currentPO.ukuran_botol_ml || 30);
                totalPoVolumeMl += (q * sz);
              });
            } else {
              const q = Number(detailTask.target_quantity || currentPO?.target_qty || 0);
              const sz = Number(currentPO?.botol_ukuran_aktual || currentPO?.ukuran_botol_ml || 30);
              totalPoVolumeMl = q * sz;
            }
            const poDual = totalPoVolumeMl > 0 ? calculateRacikanDual(totalPoVolumeMl, currentPO?.racikan || currentPO?.racikan_digunakan || detailTask.racikan) : detailTask.dual;

            const poStatus = (currentPO?.status || '').toLowerCase();
            const brand = detailTask.po_brand || currentPO?.brand_name || 'PO';
            const targetPcs = currentPO?.target_qty || detailTask.target_quantity || 0;

            // 1. Mixing stage
            const mixingTasks = matchingDbTasks.filter(t => ['racik', 'mixing'].includes((t.activity_type || '').toLowerCase()));
            const mixingDone = mixingTasks.some(t => t.status === 'completed') || ['maserasi', 'filling', 'packing', 'qc', 'completed', 'ready_to_ship'].includes(poStatus);
            const mixingRunning = !mixingDone && (mixingTasks.some(t => t.status === 'in_progress' || t.status === 'on_hold') || poStatus === 'mixing');
            const mixingPic = mixingRunning || mixingDone ? (mixingTasks[0]?.pic_name || currentPO?.mixing_pic || (mixingDone ? 'PIC Mixing (Tercatat)' : 'Belum Ada PIC')) : '-';
            const mixingDur = mixingDone ? (mixingTasks[0]?.duration_minutes || 45) : null;
            const mixingRejects = mixingTasks.reduce((s, t) => s + (t.reject_quantity || 0), 0);

            // 2. Maserasi stage
            const maserasiDays = currentPO?.maserasi_durasi_hari ?? (mixingTasks[0]?.maserasi_hari ?? 2);
            const maserasiHours = currentPO?.maserasi_durasi_jam ?? (mixingTasks[0]?.maserasi_jam ?? 0);
            const maserasiDone = ['filling', 'packing', 'qc', 'completed', 'ready_to_ship'].includes(poStatus);
            const maserasiRunning = !maserasiDone && poStatus === 'maserasi';

            // 3. Filling stage
            const fillingTasks = matchingDbTasks.filter(t => (t.activity_type || '').toLowerCase() === 'filling');
            const fillingDone = fillingTasks.length > 0 ? fillingTasks.every(t => t.status === 'completed') : ['packing', 'qc', 'completed', 'ready_to_ship'].includes(poStatus);
            const fillingRunning = !fillingDone && (fillingTasks.some(t => t.status === 'in_progress' || t.status === 'on_hold') || poStatus === 'filling');
            const fillingPic = fillingRunning || fillingDone ? (fillingTasks[0]?.pic_name || (fillingDone ? 'PIC Filling' : 'Belum Ada PIC')) : '-';
            const fillingDur = fillingDone ? (fillingTasks[0]?.duration_minutes || null) : null;
            const fillingOutput = fillingDone ? (fillingTasks.reduce((s, t) => s + (Number(t.completed_quantity) || 0), 0) || targetPcs) : fillingRunning ? fillingTasks.reduce((s, t) => s + (Number(t.completed_quantity) || 0), 0) : 0;
            const fillingRejects = fillingTasks.reduce((s, t) => s + (t.reject_quantity || 0), 0);

            // 4. Pengemasan stage
            const packingTasks = matchingDbTasks.filter(t => ['packing', 'pengemasan', 'labeling'].includes((t.activity_type || '').toLowerCase()));
            const packingDone = packingTasks.length > 0 ? packingTasks.every(t => t.status === 'completed') : ['qc', 'completed', 'ready_to_ship'].includes(poStatus);
            const packingRunning = !packingDone && (packingTasks.some(t => t.status === 'in_progress' || t.status === 'on_hold') || poStatus === 'packing');
            const packingPic = packingRunning || packingDone ? (packingTasks[0]?.pic_name || (packingDone ? 'PIC Pengemasan' : 'Belum Ada PIC')) : '-';
            const packingDur = packingDone ? (packingTasks[0]?.duration_minutes || null) : null;
            const packingOutput = packingDone ? (packingTasks.reduce((s, t) => s + (Number(t.completed_quantity) || 0), 0) || targetPcs) : packingRunning ? packingTasks.reduce((s, t) => s + (Number(t.completed_quantity) || 0), 0) : 0;
            const packingRejects = packingTasks.reduce((s, t) => s + (t.reject_quantity || 0), 0);

            // 5. QC stage
            const qcTasks = matchingDbTasks.filter(t => (t.activity_type || '').toLowerCase() === 'qc');
            const qcDone = qcTasks.some(t => t.status === 'completed') || ['completed', 'ready_to_ship'].includes(poStatus);
            const qcRunning = !qcDone && (qcTasks.some(t => t.status === 'in_progress' || t.status === 'waiting_qc') || poStatus === 'qc');
            const qcValidator = qcRunning || qcDone ? (qcTasks[0]?.qc_validator_name || currentPO?.qc_validator_name || (qcDone ? 'Tim Quality Control' : '-')) : '-';

            const pipelineStages = [
              {
                id: 'mixing_stage',
                title: 'Racik & Mixing (Pencampuran Formula)',
                activity_type: 'mixing',
                status: mixingDone ? 'completed' : mixingRunning ? 'in_progress' : 'assigned',
                pic_name: mixingPic,
                duration_minutes: mixingDur,
                total_pause_minutes: mixingTasks[0] ? getPauseMinsFromTask(mixingTasks[0]) : 0,
                output_label: poDual ? `Takaran Total: ${poDual.text}` : `${targetPcs} pcs`,
                output_val: poDual ? poDual.text : `${targetPcs} pcs`,
                reject_qty: mixingRejects,
                is_mixing: true,
                badge_text: mixingDone ? 'Mixing Selesai' : mixingRunning ? 'Sedang Dimixing' : 'Belum Dimulai',
              },
              {
                id: 'maserasi_stage',
                title: 'Pematangan / Maserasi',
                activity_type: 'maserasi',
                status: maserasiDone ? 'completed' : maserasiRunning ? 'in_progress' : 'assigned',
                pic_name: 'Sistem Timer Otomatis',
                duration_minutes: null,
                total_pause_minutes: 0,
                output_label: 'Rencana Durasi',
                output_val: `${maserasiDays} Hari ${maserasiHours} Jam`,
                reject_qty: 0,
                is_maserasi: true,
                badge_text: maserasiDone ? 'Maserasi Selesai' : maserasiRunning ? 'Sedang Maserasi' : 'Belum Dimulai',
              },
              {
                id: 'filling_stage',
                title: 'Pengisian Botol (Filling)',
                activity_type: 'filling',
                status: fillingDone ? 'completed' : fillingRunning ? 'in_progress' : 'assigned',
                pic_name: fillingPic,
                duration_minutes: fillingDur,
                total_pause_minutes: fillingTasks[0] ? getPauseMinsFromTask(fillingTasks[0]) : 0,
                output_label: 'Output Filling',
                output_val: `${fillingOutput} / ${targetPcs} pcs`,
                reject_qty: fillingRejects,
                badge_text: fillingDone ? 'Filling Selesai' : fillingRunning ? 'Sedang Di-filling' : 'Belum Dimulai',
              },
              {
                id: 'packing_stage',
                title: 'Pengemasan & Labeling',
                activity_type: 'packing',
                status: packingDone ? 'completed' : packingRunning ? 'in_progress' : 'assigned',
                pic_name: packingPic,
                duration_minutes: packingDur,
                total_pause_minutes: packingTasks[0] ? getPauseMinsFromTask(packingTasks[0]) : 0,
                output_label: 'Output Pengemasan',
                output_val: `${packingOutput} / ${targetPcs} pcs`,
                reject_qty: packingRejects,
                badge_text: packingDone ? 'Pengemasan Selesai' : packingRunning ? 'Sedang Dikemas' : 'Belum Dimulai',
              },
              {
                id: 'qc_stage',
                title: 'Validasi Akhir Quality Control (QC)',
                activity_type: 'qc',
                status: qcDone ? 'completed' : qcRunning ? 'in_progress' : 'assigned',
                pic_name: qcValidator,
                duration_minutes: null,
                total_pause_minutes: 0,
                output_label: 'Hasil Validasi',
                output_val: qcDone ? 'Lolos QC 100%' : 'Menunggu Validasi',
                reject_qty: 0,
                badge_text: qcDone ? 'Lolos & Terverifikasi' : qcRunning ? 'Pemeriksaan QC' : 'Belum Dimulai',
              },
            ];

            const completedCount = pipelineStages.filter(s => s.status === 'completed').length;
            const progressPct = Math.round((completedCount / pipelineStages.length) * 100);

            const fmtDur = (m) => m >= 60 ? `${Math.floor(m / 60)}j ${m % 60}m` : `${m}m`;

            return (
              <div className="flex flex-col bg-gradient-to-b from-[#FFFDF9] to-[#F9F5EC] max-h-[85vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-800 p-5 text-white flex items-center justify-between relative overflow-hidden">
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 shadow-lg">
                      <BarChart3 className="w-6 h-6 text-amber-300" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base leading-snug flex items-center gap-2">
                        {detailTask.task_name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-300 font-medium mt-1">
                        <span>{detailTask.po_number ? (String(detailTask.po_number).toLowerCase().startsWith('po') ? detailTask.po_number : `PO #${detailTask.po_number}`) : 'PO Produksi'}</span>
                        {brand && <span>· {brand}</span>}
                        <span className="bg-amber-400/20 text-amber-300 px-2.5 py-0.5 rounded-md border border-amber-400/30 text-xs font-bold flex items-center gap-1.5">
                          <Target className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                          Target Total: {targetPcs} pcs
                        </span>
                        {poDual && (
                          <span className="bg-purple-400/20 text-purple-200 px-2.5 py-0.5 rounded-md border border-purple-400/30 text-xs font-mono font-bold">
                            {poDual.text}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30 text-xs px-3 py-1 font-bold shrink-0">
                    {progressPct}% Selesai
                  </Badge>
                </div>

                <div className="p-5 space-y-5 text-slate-800">
                  {/* Step-by-Step Title Header */}
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                    <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-600" /> Alur 5 Tahapan Lengkap PO ({completedCount}/5 Tahap Selesai)
                    </h4>
                    <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-200">
                      {progressPct}% Selesai
                    </span>
                  </div>

                  {/* Vertical Timeline Stepper */}
                  <div className="relative pl-7 space-y-4 before:absolute before:left-3 before:top-3 before:bottom-3 before:w-0.5 before:bg-slate-200">
                    {pipelineStages.map((pt, idx) => {
                      const isDone = pt.status === 'completed';
                      const isRunning = pt.status === 'in_progress';
                      const isAssigned = pt.status === 'assigned';
                      const hasReject = pt.reject_qty > 0;

                      const netMins = pt.duration_minutes || 0;
                      const pauseMins = pt.total_pause_minutes || 0;
                      const grossMins = netMins + pauseMins;

                      return (
                        <div key={pt.id} className="relative group">
                          {/* Step Circle Badge */}
                          <div className={`absolute -left-7 top-3 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shadow-sm z-10 ${
                            isDone
                              ? 'bg-emerald-600 text-white ring-2 ring-emerald-200'
                              : isRunning
                              ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                              : 'bg-white border-2 border-slate-300 text-slate-500'
                          }`}>
                            {isDone ? '✓' : idx + 1}
                          </div>

                          {/* Step Content Box */}
                          <div className={`bg-white border rounded-2xl p-4 shadow-sm space-y-3 ${
                            isDone
                              ? 'border-emerald-200/90 bg-emerald-50/10'
                              : isRunning
                              ? 'border-blue-400 ring-2 ring-blue-100 shadow-md bg-blue-50/10'
                              : 'border-slate-200'
                          }`}>
                            {/* Step Title & PIC Sub-header */}
                            <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-2">
                              <div>
                                <h4 className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                                  <span className="uppercase tracking-wide font-extrabold text-blue-900">{idx + 1}. {pt.title}</span>
                                </h4>
                                <div className="text-[11px] text-slate-600 font-medium flex items-center gap-1.5 mt-1">
                                  <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span className="text-slate-500 font-semibold">Penanggung Jawab:</span>
                                  <span className="text-slate-900 font-bold bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                                    {pt.pic_name}
                                  </span>
                                </div>
                              </div>
                              <Badge className={`text-[10px] px-2.5 py-0.5 font-bold shrink-0 ${
                                isDone
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : isRunning
                                  ? 'bg-blue-100 text-blue-800 border-blue-300'
                                  : 'bg-slate-100 text-slate-600 border-slate-300'
                              }`}>
                                {pt.badge_text}
                              </Badge>
                            </div>

                            {/* 3 Spacious Columns Grid: Waktu Kerja, Output / Takaran, Reject */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                              {/* 1. Waktu Kerja */}
                              <div className="bg-slate-50/90 p-2.5 rounded-xl border border-slate-100 space-y-1">
                                <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                  <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span>Waktu Pengerjaan</span>
                                </div>
                                {pt.is_maserasi ? (
                                  <span className="font-bold text-purple-900 text-xs block">
                                    {pt.output_val}
                                  </span>
                                ) : isDone && pt.duration_minutes ? (
                                  <>
                                    <span className="font-bold text-emerald-700 text-xs block leading-tight">
                                      {fmtDur(netMins)} <span className="text-[9px] text-slate-500 font-normal">(Bersih)</span>
                                    </span>
                                    <span className="text-[9px] text-slate-500 block truncate font-medium">
                                      {pauseMins > 0 ? `Total: ${fmtDur(grossMins)} · Jeda: ${pauseMins}m` : `Bebas Jeda`}
                                    </span>
                                  </>
                                ) : isRunning ? (
                                  <span className="font-bold text-blue-600 text-xs block">
                                    Sedang Berjalan...
                                  </span>
                                ) : (
                                  <span className="font-medium text-slate-400 text-xs block">
                                    - (Belum Dimulai)
                                  </span>
                                )}
                              </div>

                              {/* 2. Output / Takaran */}
                              <div className="bg-slate-50/90 p-2.5 rounded-xl border border-slate-100 space-y-1">
                                <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                  <Box className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span>{pt.output_label}</span>
                                </div>
                                <span className={`font-bold text-xs block ${pt.is_mixing ? 'text-purple-950 font-mono' : isDone ? 'text-emerald-700' : isRunning ? 'text-blue-700' : 'text-slate-500'}`}>
                                  {isAssigned && !pt.is_maserasi && !pt.is_mixing ? `0 / ${targetPcs} pcs` : pt.output_val}
                                </span>
                              </div>

                              {/* 3. Status Reject */}
                              <div className={`p-2.5 rounded-xl border space-y-1 ${hasReject ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50/60 border-emerald-100 text-emerald-800'}`}>
                                <div className="flex items-center gap-1.5 text-[10px] opacity-75 font-bold uppercase tracking-wider">
                                  <AlertTriangle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span>Status Reject</span>
                                </div>
                                <span className="font-bold text-xs block">
                                  {hasReject ? `Reject: ${pt.reject_qty} pcs` : 'Bebas Reject (0)'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button variant="outline" className="text-xs h-9 px-6 border-slate-300 font-semibold" onClick={() => setDetailTask(null)}>
                      Tutup Detail
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ASA DIALOG: MULAI PENGEMASAN LEBIH AWAL (CURI START) */}
      <Dialog open={!!earlyStartModal} onOpenChange={(o) => !o && setEarlyStartModal(null)}>
        <DialogContent className="sm:max-w-md bg-gradient-to-b from-amber-50/60 via-white to-white border border-amber-200/80 shadow-xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-950 font-bold text-base">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Mulai Pengemasan Lebih Awal (Curi Start)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs text-slate-700">
            <div className="p-3 bg-amber-100/70 border border-amber-200/90 rounded-xl space-y-1">
              <p className="font-bold text-amber-950 text-sm">
                {earlyStartModal?.task?.po_brand || earlyStartModal?.po?.brand_name || earlyStartModal?.group?.po_brand || "PO Produksi"}
              </p>
              <p className="text-[11px] text-amber-800 font-medium">
                PO: #{earlyStartModal?.task?.po_number || earlyStartModal?.po?.order_number || earlyStartModal?.group?.po_number}
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed text-xs">
              Halo <strong>{member.full_name?.split(" ")[0]}</strong>! Tahapan primer (mixing/maserasi/filling) tetap akan berlangsung secara independen oleh Tim Primer. Dengan <strong>Mulai Pengemasan Lebih Awal</strong>, kamu bisa langsung mempersiapkan botol kemas, dus, segel, dan coding di tab <strong>Tugas Aktif</strong> tanpa perlu menunggu proses hulu selesai!
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={() => setEarlyStartModal(null)} disabled={loading === "early_start"}>
              Batal
            </Button>
            <Button
              size="sm"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold shadow-sm"
              onClick={executeEarlyStartPacking}
              disabled={loading === "early_start"}
            >
              {loading === "early_start" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Package className="w-4 h-4 mr-1.5" />}
              Mulai Pengemasan Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== MODAL VALIDASI QC MASSAL ===== */}
      <Dialog open={!!batchQcModal} onOpenChange={(open) => !open && setBatchQcModal(null)}>
        <DialogContent className="sm:max-w-[620px] p-0 overflow-hidden border border-slate-200/90 shadow-2xl rounded-2xl bg-slate-50/50">
          {batchQcModal && (() => {
            const bAct = (batchQcModal?.[0]?.activity_type || '').toLowerCase();
            const isMixing = bAct === 'racik' || bAct === 'mixing';
            const isFilling = bAct === 'filling';
            const stageTitle = isMixing ? 'Mixing & Racikan Liquid' : isFilling ? 'Filling & Pengisian Botol' : 'Pengemasan & Produk Jadi';

            const totalBaseQty = batchQcModal.reduce((sum, t) => {
              const base = t.completed_quantity !== undefined && t.completed_quantity !== null
                ? Number(t.completed_quantity)
                : (Number(t.target_quantity) || 0);
              return sum + base;
            }, 0);

            const totalRejectQty = batchRejectMode === 'custom'
              ? Object.values(batchRejectQuantities).reduce((sum, v) => sum + (Number(v) || 0), 0)
              : 0;

            const totalNetQty = Math.max(0, totalBaseQty - totalRejectQty);
            const unit = batchQcModal[0]?.unit || (isMixing ? 'liter' : 'pcs');

            const allChecklistSelected = isMixing
              ? (batchQcChecklist.aromaColor && batchQcChecklist.cappingSeal && batchQcChecklist.qtyMatch)
              : isFilling
              ? (batchQcChecklist.qtyMatch && batchQcChecklist.cappingSeal && batchQcChecklist.aromaColor)
              : (batchQcChecklist.labelingExp && batchQcChecklist.aromaColor && batchQcChecklist.cappingSeal && batchQcChecklist.qtyMatch);

            const toggleAllChecklist = () => {
              const nextState = !allChecklistSelected;
              setBatchQcChecklist({
                aromaColor: nextState,
                cappingSeal: nextState,
                labelingExp: nextState,
                qtyMatch: nextState,
              });
            };

            return (
              <div className="flex flex-col bg-white">
                {/* Header with Luxury Brand Accent */}
                <div className="bg-gradient-to-r from-[#211714] via-[#35251d] to-[#1f1512] p-5 text-white flex items-center justify-between border-b border-amber-900/40 relative overflow-hidden">
                  <div className="absolute -right-6 -top-6 w-28 h-28 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
                  <div className="flex items-center gap-3.5 relative z-10">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-400/30 flex items-center justify-center shrink-0 shadow-inner">
                      <ShieldCheck className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-base text-white tracking-tight">
                          Validasi Mutu QC Massal
                        </h3>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-400/20 text-amber-300 border border-amber-400/30">
                          {stageTitle}
                        </span>
                      </div>
                      <p className="text-xs text-amber-200/80 mt-0.5">
                        Memeriksa kelayakan mutu untuk <strong>{batchQcModal.length} varian</strong> terpilih
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-white/10 hover:bg-white/15 text-white border border-white/20 text-xs font-semibold px-3 py-1 rounded-lg backdrop-blur-xs shrink-0">
                    {batchQcModal.length} Varian
                  </Badge>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* Live KPI Summary Ribbon */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50/80 p-3 rounded-xl border border-slate-200/80">
                    <div className="bg-white p-2.5 rounded-lg border border-slate-100 shadow-2xs">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">Varian</span>
                      <span className="text-sm font-bold text-slate-800">{batchQcModal.length} Varian</span>
                    </div>
                    <div className="bg-white p-2.5 rounded-lg border border-slate-100 shadow-2xs">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">Total Fisik</span>
                      <span className="text-sm font-bold text-indigo-900">{totalBaseQty.toLocaleString('id-ID')} {unit}</span>
                    </div>
                    <div className={`p-2.5 rounded-lg border shadow-2xs transition-all ${totalRejectQty > 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-white border-slate-100 text-slate-700'}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider block opacity-75">Reject QC</span>
                      <span className={`text-sm font-bold ${totalRejectQty > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                        {totalRejectQty > 0 ? `${totalRejectQty.toLocaleString('id-ID')} ${unit}` : '0 (Nihil)'}
                      </span>
                    </div>
                    <div className="bg-emerald-50/90 border border-emerald-200 p-2.5 rounded-lg shadow-2xs">
                      <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider block">Bersih Lolos</span>
                      <span className="text-sm font-extrabold text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {totalNetQty.toLocaleString('id-ID')} {unit}
                      </span>
                    </div>
                  </div>

                  {/* Reject Mode Switcher (Card Selector) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                      <span>Status Hasil Temuan Uji Mutu:</span>
                      <span className="text-[10px] font-normal text-slate-500">Pilih kondisi produk di lapangan</span>
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => {
                          setBatchRejectMode('none');
                          setBatchRejectQuantities({});
                        }}
                        className={`p-3 rounded-xl border-2 text-left transition-all flex items-start gap-3 cursor-pointer ${
                          batchRejectMode === 'none'
                            ? 'bg-emerald-50/70 border-emerald-500 text-emerald-950 shadow-xs'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                          batchRejectMode === 'none' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold">100% Bagus (Bebas Reject)</p>
                          <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                            Semua varian memenuhi standar kelayakan mutu secara sempurna.
                          </p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setBatchRejectMode('custom')}
                        className={`p-3 rounded-xl border-2 text-left transition-all flex items-start gap-3 cursor-pointer ${
                          batchRejectMode === 'custom'
                            ? 'bg-amber-50/80 border-amber-500 text-amber-950 shadow-xs'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                          batchRejectMode === 'custom' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold">Ada Temuan Reject</p>
                          <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                            Tentukan jumlah reject/cacat khusus untuk varian tertentu di bawah.
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Checklist Standar Mutu Sesuai Tahapan */}
                  <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-purple-700" />
                        {isMixing
                          ? 'Kriteria Mutu Liquid & Formula (Mixing):'
                          : isFilling
                          ? 'Kriteria Mutu Pengisian & Capping (Filling):'
                          : 'Kriteria Mutu Kemasan Akhir (Pengemasan):'}
                      </span>
                      <button
                        type="button"
                        onClick={toggleAllChecklist}
                        className="text-[11px] font-bold text-purple-700 hover:text-purple-900 underline cursor-pointer"
                      >
                        {allChecklistSelected ? 'Batal Semua' : 'Centang Semua'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      {isMixing ? (
                        <>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.aromaColor}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, aromaColor: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Aroma, warna & homogenitas cairan sesuai master standard</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.cappingSeal}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, cappingSeal: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Kejernihan liquid & bebas dari partikel asing / endapan</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.qtyMatch}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, qtyMatch: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Volume & rasio takaran bahan baku pas sesuai formula</span>
                          </label>
                        </>
                      ) : isFilling ? (
                        <>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.qtyMatch}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, qtyMatch: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Volume takaran isi botol presisi (ml/g) sesuai target</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.cappingSeal}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, cappingSeal: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Capping, spray & sealing kencang (Bebas rembes/kebocoran)</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.aromaColor}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, aromaColor: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Botol bersih & bebas dari cipratan cairan luar</span>
                          </label>
                        </>
                      ) : (
                        <>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.labelingExp}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, labelingExp: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Label stiker lurus, bersih, simetris & bebas gelembung</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.aromaColor}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, aromaColor: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Exp Date & Batch No tertera jelas dan sesuai dokumen PO</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.cappingSeal}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, cappingSeal: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Dus / Inner box rapi & segel kemasan rapat utuh</span>
                          </label>
                          <label className="flex items-center gap-2.5 cursor-pointer bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-purple-50/30 transition-colors">
                            <input
                              type="checkbox"
                              checked={batchQcChecklist.qtyMatch}
                              onChange={(e) => setBatchQcChecklist({ ...batchQcChecklist, qtyMatch: e.target.checked })}
                              className="rounded text-purple-700 focus:ring-purple-600 w-4 h-4"
                            />
                            <span className="font-medium text-slate-800">Jumlah output fisik kemasan sesuai total target PO</span>
                          </label>
                        </>
                      )}
                    </div>
                  </div>

                  {/* List of Tasks / Variants */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                        Rincian Varian ({batchQcModal.length}):
                      </span>
                      <span className="text-[11px] text-slate-500">
                        {batchRejectMode === 'custom' ? 'Sesuaikan reject per varian bila ada' : 'Semua varian dinyatakan lolos 100%'}
                      </span>
                    </div>
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {batchQcModal.map((t, idx) => {
                        const curReject = batchRejectQuantities[t.id] !== undefined ? batchRejectQuantities[t.id] : '';
                        const baseQty = t.completed_quantity !== undefined && t.completed_quantity !== null
                          ? Number(t.completed_quantity)
                          : (Number(t.target_quantity) || 0);
                        const numReject = Number(curReject) || 0;
                        const netQty = Math.max(0, baseQty - numReject);

                        return (
                          <div 
                            key={t.id || idx} 
                            className={`p-3 rounded-xl border text-xs transition-all space-y-2.5 ${
                              numReject > 0 
                                ? 'bg-amber-50/50 border-amber-300 shadow-2xs' 
                                : 'bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="space-y-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-bold text-slate-900 truncate text-xs sm:text-sm">
                                    {t.product_name || t.task_name}
                                  </p>
                                  {t.po_number && (
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 font-semibold">
                                      #{t.po_number}
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500 flex items-center gap-2">
                                  <span>PIC: <strong>{t.pic_name || 'Tidak ada'}</strong></span>
                                  {t.po_brand && <span>· Brand: <strong>{t.po_brand}</strong></span>}
                                </p>
                              </div>

                              <div className="text-right shrink-0">
                                {t.completed_quantity !== undefined && t.completed_quantity !== null && Number(t.completed_quantity) !== Number(t.target_quantity) ? (
                                  <div className="flex flex-col items-end gap-0.5">
                                    <span className="text-[10px] text-slate-400 line-through">Target: {t.target_quantity}</span>
                                    <Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-[10px] font-bold px-2 py-0.5">
                                      Output: {t.completed_quantity} {t.unit || 'pcs'}
                                    </Badge>
                                  </div>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-800 border border-slate-200 text-[10px] font-bold px-2 py-0.5">
                                    Target: {t.target_quantity || 0} {t.unit || 'pcs'}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Reject Interactive Controller in custom mode */}
                            {batchRejectMode === 'custom' && (
                              <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 bg-amber-50/40 -mx-3 -mb-3 p-2.5 rounded-b-xl">
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-900">
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                  <span>Reject QC:</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center bg-white rounded-lg border border-amber-300 shadow-2xs overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = Math.max(0, numReject - 1);
                                        setBatchRejectQuantities(prev => ({ ...prev, [t.id]: next === 0 ? '' : next }));
                                      }}
                                      className="px-2.5 py-1 text-slate-600 hover:bg-slate-100 font-bold text-xs cursor-pointer"
                                    >
                                      -
                                    </button>
                                    <Input
                                      type="number"
                                      min="0"
                                      placeholder="0"
                                      value={curReject}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setBatchRejectQuantities(prev => ({ ...prev, [t.id]: val }));
                                      }}
                                      className="h-7 w-16 text-xs text-center border-0 focus-visible:ring-0 font-bold text-amber-950 p-0"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = numReject + 1;
                                        setBatchRejectQuantities(prev => ({ ...prev, [t.id]: next }));
                                      }}
                                      className="px-2.5 py-1 text-slate-600 hover:bg-slate-100 font-bold text-xs cursor-pointer"
                                    >
                                      +
                                    </button>
                                  </div>
                                  <span className="text-[11px] text-slate-500 font-medium">{t.unit || 'pcs'}</span>

                                  {/* Live Clean Passed Pill */}
                                  <span className={`text-[10px] font-extrabold px-2 py-1 rounded-md border flex items-center gap-1 ${
                                    numReject > 0 
                                      ? 'bg-amber-100 text-amber-900 border-amber-300' 
                                      : 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                  }`}>
                                    Lolos: {netQty} {t.unit || 'pcs'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Optional Notes */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">Catatan Audit Mutu QC (Opsional)</Label>
                    <Textarea
                      placeholder="Contoh: No lot botol lolos uji tekan, pengeleman label presisi, siap dilanjutkan..."
                      value={batchQcNotes}
                      onChange={(e) => setBatchQcNotes(e.target.value)}
                      rows={2}
                      className="text-xs rounded-xl border-slate-200 focus:ring-purple-600"
                    />
                  </div>
                </div>

                {/* Footer with High-Impact Validation Button */}
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-500 hidden sm:block">
                    Total Disahkan: <strong className="text-slate-900">{totalNetQty.toLocaleString('id-ID')} {unit}</strong>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBatchQcModal(null)}
                      disabled={loading === 'batch_qc'}
                      className="text-xs h-9 border-slate-300 font-medium rounded-xl cursor-pointer"
                    >
                      Batal
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleBatchConfirmQC}
                      disabled={loading === 'batch_qc'}
                      className="text-xs h-9 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white font-bold gap-2 px-4 rounded-xl shadow-md cursor-pointer transition-all hover:shadow-lg"
                    >
                      {loading === 'batch_qc' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                          Memvalidasi Mutu...
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4 text-white" />
                          Sahkan & Validasi {batchQcModal.length} Varian ({totalNetQty.toLocaleString('id-ID')} {unit})
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ===== MODAL VALIDASI QC CICILAN OUTPUT ===== */}
      <Dialog open={!!cicilQcModal} onOpenChange={(open) => !open && setCicilQcModal(null)}>
        <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden border border-[#E6D5B8] shadow-2xl rounded-2xl">
          {cicilQcModal && (() => {
            const { task, batch } = cicilQcModal;
            const submittedQty = batch?.qty || task.pending_qc_cicil_qty || 0;
            const passedNum = Number(cicilQcPassedQty) || 0;
            const rejectNum = Number(cicilQcRejectQty) || 0;
            const act = (task.activity_type || '').toLowerCase();
            const isFilling = act === 'filling';

            return (
              <div className="flex flex-col bg-white">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-800 via-indigo-800 to-purple-900 p-5 text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0 border border-white/20">
                      <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">Validasi Mutu QC — Cicilan Output</h3>
                      <p className="text-[11px] text-purple-200 truncate max-w-[280px]">
                        {task.task_name}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-white/20 text-white border-white/30 text-[10px] font-mono">
                    Batch Cicilan
                  </Badge>
                </div>

                <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                  {/* Summary Card */}
                  <div className="p-3 bg-[#FDFBF7] border border-[#E6D5B8] rounded-xl text-xs space-y-1.5 font-mono">
                    <div className="flex justify-between text-slate-600 font-sans">
                      <span>Diajukan Oleh:</span>
                      <strong className="text-slate-900">{batch?.operator || task.pic_name || 'Operator'}</strong>
                    </div>
                    <div className="flex justify-between text-slate-600 font-sans">
                      <span>Waktu Penyerahan:</span>
                      <strong className="text-slate-900">
                        {batch?.date ? format(new Date(batch.date), 'dd/MM/yyyy HH:mm') : 'Hari Ini'}
                      </strong>
                    </div>
                    <div className="flex justify-between text-slate-600 font-sans border-t border-slate-200/60 pt-1">
                      <span>Jumlah Output Batch Ini:</span>
                      <strong className="text-purple-900 font-bold text-sm">
                        {submittedQty.toLocaleString('id-ID')} pcs
                      </strong>
                    </div>
                  </div>

                  {/* Checklist Standar Mutu */}
                  <div className="p-3 bg-purple-50/60 border border-purple-200/70 rounded-xl space-y-2">
                    <span className="text-xs font-bold text-purple-950 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                      Poin Pemeriksaan Kualitas QC:
                    </span>
                    <div className="space-y-1.5 text-[11px] text-slate-700">
                      <label className="flex items-center gap-2 cursor-pointer bg-white p-2 rounded-lg border border-purple-100 shadow-2xs">
                        <input
                          type="checkbox"
                          checked={cicilQcChecklist.volumeMl}
                          onChange={(e) => setCicilQcChecklist({ ...cicilQcChecklist, volumeMl: e.target.checked })}
                          className="rounded text-purple-600 focus:ring-purple-500 w-3.5 h-3.5"
                        />
                        <span>{isFilling ? "Ketinggian & volume cairan pas sesuai ukuran botol" : "Kerapihan kemasan & kelengkapan isi"}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer bg-white p-2 rounded-lg border border-purple-100 shadow-2xs">
                        <input
                          type="checkbox"
                          checked={cicilQcChecklist.cappingSeal}
                          onChange={(e) => setCicilQcChecklist({ ...cicilQcChecklist, cappingSeal: e.target.checked })}
                          className="rounded text-purple-600 focus:ring-purple-500 w-3.5 h-3.5"
                        />
                        <span>{isFilling ? "Capping / tutup botol kencang & crimp presisi" : "Segel dus / hologram tertempel rapi"}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer bg-white p-2 rounded-lg border border-purple-100 shadow-2xs">
                        <input
                          type="checkbox"
                          checked={cicilQcChecklist.leakFree}
                          onChange={(e) => setCicilQcChecklist({ ...cicilQcChecklist, leakFree: e.target.checked })}
                          className="rounded text-purple-600 focus:ring-purple-500 w-3.5 h-3.5"
                        />
                        <span>Bebas rembesan, tidak bocor saat dibalik</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer bg-white p-2 rounded-lg border border-purple-100 shadow-2xs">
                        <input
                          type="checkbox"
                          checked={cicilQcChecklist.cleanParticle}
                          onChange={(e) => setCicilQcChecklist({ ...cicilQcChecklist, cleanParticle: e.target.checked })}
                          className="rounded text-purple-600 focus:ring-purple-500 w-3.5 h-3.5"
                        />
                        <span>Permukaan produk bersih & bebas goresan/debu</span>
                      </label>
                    </div>
                  </div>

                  {/* Input Hasil Inspeksi: Lolos vs Reject */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-1">
                      <Label className="text-xs font-bold text-emerald-950 flex items-center gap-1">
                        <span>✓</span>
                        <span>Jumlah Lolos QC *</span>
                      </Label>
                      <div className="flex items-center gap-1.5 pt-1">
                        <Input
                          type="number"
                          min="0"
                          max={submittedQty}
                          value={cicilQcPassedQty}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCicilQcPassedQty(val);
                            const num = Number(val) || 0;
                            if (num <= submittedQty) {
                              setCicilQcRejectQty(String(Math.max(0, submittedQty - num)));
                            }
                          }}
                          className="h-9 text-xs font-bold text-center bg-white border-emerald-300 focus:border-emerald-500"
                        />
                        <span className="text-xs font-semibold text-emerald-900">pcs</span>
                      </div>
                    </div>

                    <div className="p-3 bg-rose-50/70 border border-rose-200 rounded-xl space-y-1">
                      <Label className="text-xs font-bold text-rose-950 flex items-center gap-1">
                        <span>⊘</span>
                        <span>Jumlah Reject</span>
                      </Label>
                      <div className="flex items-center gap-1.5 pt-1">
                        <Input
                          type="number"
                          min="0"
                          max={submittedQty}
                          value={cicilQcRejectQty}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCicilQcRejectQty(val);
                            const num = Number(val) || 0;
                            if (num <= submittedQty) {
                              setCicilQcPassedQty(String(Math.max(0, submittedQty - num)));
                            }
                          }}
                          className="h-9 text-xs font-bold text-center bg-white border-rose-300 focus:border-rose-500"
                        />
                        <span className="text-xs font-semibold text-rose-900">pcs</span>
                      </div>
                    </div>
                  </div>

                  {/* Catatan QC */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-slate-700">Catatan QC (Opsional)</Label>
                    <Input
                      placeholder="Contoh: No lot lulus uji vakum, siap diserahkan ke meja pengemasan..."
                      value={cicilQcNotes}
                      onChange={(e) => setCicilQcNotes(e.target.value)}
                      className="h-9 text-xs border-slate-200 focus:border-purple-600 rounded-xl"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-600 font-medium">
                    Disahkan: <strong className="text-emerald-700">{passedNum} pcs Lolos</strong>
                    {rejectNum > 0 && <span className="text-rose-600"> ({rejectNum} Reject)</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCicilQcModal(null)}
                      disabled={loading === task.id}
                      className="text-xs h-9 border-slate-300 rounded-xl"
                    >
                      Batal
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleConfirmCicilQc}
                      disabled={loading === task.id || passedNum + rejectNum === 0}
                      className="text-xs h-9 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white font-bold gap-1.5 px-4 rounded-xl shadow-md"
                    >
                      {loading === task.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Memvalidasi...
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4" />
                          Sahkan & Loloskan QC ({passedNum} pcs)
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ===== MODAL GANTI PIC MASSAL ===== */}
      <Dialog open={!!batchPicModal} onOpenChange={(open) => !open && setBatchPicModal(null)}>
        <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden border border-slate-200 shadow-2xl rounded-2xl">
          {batchPicModal && (
            <div className="flex flex-col bg-white">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-5 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                    <UserPlus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base leading-snug">
                      Tugaskan PIC Massal
                    </h3>
                    <p className="text-xs text-slate-300">
                      Ganti penanggung jawab untuk {batchPicModal.length} tugas sekaligus
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 text-xs">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-800">
                    Pilih Anggota Tim / PIC Baru:
                  </Label>
                  <Select value={batchTargetPicId} onValueChange={setBatchTargetPicId}>
                    <SelectTrigger className="w-full text-xs h-9">
                      <SelectValue placeholder="Pilih anggota tim..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.id} value={String(m.id)} className="text-xs">
                          {m.full_name} ({m.position || m.role || 'Staff'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1 text-slate-600">
                  <span className="font-semibold text-slate-900 block">Tugas yang akan diperbarui:</span>
                  <div className="max-h-36 overflow-y-auto space-y-1 pr-1 font-mono text-[11px]">
                    {batchPicModal.map((t, idx) => (
                      <div key={t.id || idx} className="truncate">
                        • {t.task_name} (Saat ini: {t.pic_name || 'Kosong'})
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBatchPicModal(null)}
                  disabled={loading === 'batch'}
                  className="text-xs h-9 border-slate-300 font-medium"
                >
                  Batal
                </Button>
                <Button
                  size="sm"
                  onClick={handleBatchAssignPic}
                  disabled={loading === 'batch' || !batchTargetPicId}
                  className="text-xs h-9 bg-slate-900 hover:bg-black text-white font-bold gap-1.5 shadow-sm"
                >
                  {loading === 'batch' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-white" />
                      Simpan Penugasan ({batchPicModal.length} Tugas)
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== ASA STOCK DEDUCTION PREVIEW MODAL ===== */}
      {asaDeductionModal && (
        <AsaStockDeductionModal
          open={!!asaDeductionModal}
          onOpenChange={(isOpen) => !isOpen && setAsaDeductionModal(null)}
          title={asaDeductionModal.title}
          taskName={asaDeductionModal.taskName}
          poNumber={asaDeductionModal.poNumber}
          brandName={asaDeductionModal.brandName}
          activityType={asaDeductionModal.activityType}
          previewData={asaDeductionModal.previewData}
          confirmLabel={asaDeductionModal.confirmLabel || "Konfirmasi & Mulai Pengerjaan"}
          onConfirm={asaDeductionModal.onConfirm}
          isLoading={loading !== null}
        />
      )}

      {/* ===== FLOATING BATCH ACTION BAR AT SCREEN BOTTOM ===== */}
      {selectedSubTaskIds.size > 0 && selectedTasksList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900/95 backdrop-blur-md text-white px-4 py-2.5 rounded-2xl shadow-2xl border border-slate-700/80 flex items-center gap-3 animate-in slide-in-from-bottom-5 duration-200 max-w-[95vw] overflow-x-auto">
          <div className="flex items-center gap-2 border-r border-slate-700 pr-3 shrink-0">
            <CheckSquare className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-bold text-slate-100">{selectedSubTaskIds.size} Terpilih</span>
            <button
              type="button"
              onClick={() => setSelectedSubTaskIds(new Set())}
              className="text-[10px] text-slate-400 hover:text-white underline ml-1 cursor-pointer"
            >
              Batal
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {/* MULAI */}
            {selectedTasksList.some(t => t.status === 'assigned') && (
              <Button
                size="sm"
                className="h-8 text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold gap-1.5 rounded-xl shadow-md cursor-pointer"
                disabled={loading === 'batch'}
                onClick={() => handleBatchStartTasks(selectedTasksList.filter(t => t.status === 'assigned'))}
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                Mulai ({selectedTasksList.filter(t => t.status === 'assigned').length})
              </Button>
            )}

            {/* LANJUTKAN */}
            {selectedTasksList.some(t => t.status === 'on_hold') && (
              <Button
                size="sm"
                className="h-8 text-xs bg-amber-600 hover:bg-amber-500 text-white font-bold gap-1.5 rounded-xl shadow-md cursor-pointer"
                disabled={loading === 'batch'}
                onClick={() => handleBatchResumeTasks(selectedTasksList.filter(t => t.status === 'on_hold'))}
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                Lanjutkan ({selectedTasksList.filter(t => t.status === 'on_hold').length})
              </Button>
            )}

            {/* SELESAI */}
            {selectedTasksList.some(t => t.status === 'in_progress') && (
              <Button
                size="sm"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-bold gap-1.5 rounded-xl shadow-md cursor-pointer"
                disabled={loading === 'batch'}
                onClick={() => handleBatchFinishToQC(selectedTasksList.filter(t => t.status === 'in_progress'))}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Selesai ({selectedTasksList.filter(t => t.status === 'in_progress').length})
              </Button>
            )}

            {/* VALIDASI QC */}
            {canValidateQC && selectedTasksList.some(t => t.status === 'waiting_qc' || t.status === 'menunggu_qc') && (
              <Button
                size="sm"
                className="h-8 text-xs bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold gap-1.5 rounded-xl shadow-lg border border-purple-400/40 cursor-pointer transition-all hover:scale-102"
                disabled={loading === 'batch_qc'}
                onClick={() => setBatchQcModal(selectedTasksList.filter(t => t.status === 'waiting_qc' || t.status === 'menunggu_qc'))}
              >
                <ShieldCheck className="w-4 h-4 text-amber-300" />
                Validasi Mutu QC ({selectedTasksList.filter(t => t.status === 'waiting_qc' || t.status === 'menunggu_qc').length})
              </Button>
            )}

            {/* GANTI PIC */}
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 font-bold gap-1.5 rounded-xl cursor-pointer"
              onClick={() => {
                setBatchPicModal(selectedTasksList);
                setBatchTargetPicId(selectedTasksList[0]?.pic_id || "");
              }}
            >
              <UserPlus className="w-3.5 h-3.5 text-blue-400" />
              Ganti PIC ({selectedTasksList.length})
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}